(function () {
  'use strict';
  // MMX Cart Drawer V1.10.25 — button font-size controls + lock removal
  // MMX CART DRAWER V11.3 — smart You May Like quality floor + source attribution

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const routes = {
    cart: `${root}cart.js`,
    add: `${root}cart/add.js`,
    change: `${root}cart/change.js`,
    update: `${root}cart/update.js`
  };

  let config = null;
  let configText = '';
  let cartState = null;
  let cartPromise = null;
  let refreshTimer = null;
  let fastOpenTimer = null;
  let addPendingUntil = 0;
  let syncingGift = false;
  const productDataCache = new Map();
  const shopifyRecommendationCache = new Map();
  const engravingFieldConfigCache = new Map();
  let acceleratedCheckoutRefreshTimer = null;
  let acceleratedCheckoutRefreshToken = 0;
  let acceleratedCheckoutReservationTimer = null;
  let acceleratedCheckoutPaintTimer = null;
  let discountFormState = 'idle';
  let discountFormMessage = '';
  let discountFormRestoreValue = '';
  let discountFormStatusTimer = null;
  let footerUtilityOpenPanel = null;
  let noteSaveCollapseTimer = null;
  let inventorySnapshot = null;
  let inventoryRefreshTimer = null;
  let inventoryRefreshRequest = 0;
  let syncingSafetyTimer = null;
  let externalCartMutationRefreshTimers = [];
  let externalCartMutationPendingUntil = 0;
  let recommendationRenderRequest = 0;
  let preorderAcknowledgedSignature = '';
  let preorderPendingAction = null;
  let preorderReturnFocus = null;
  let mmxMobileDrawerOpenIntent = false;
  let mmxMobileScrollLockActive = false;
  let mmxMobileScrollLockY = 0;
  let mmxMobileScrollLockStyles = null;
  let mmxMobileScrollUnlockTimer = null;
  let mmxRecommendationTouchGesture = null;

  const recentlyViewedStorageKey = 'mmx_recently_viewed_products_v1';

  // Safety guard: prevent huge quantity values from overflowing Shopify/ajax/cart calculations.
  // Keep this intentionally conservative for retail cart UX.
  const MMX_MAX_CART_LINE_QUANTITY = 99;
  const MMX_SYNC_SAFETY_TIMEOUT_MS = 20000;
  const MMX_INTERNAL_CART_REQUEST_HEADER = 'X-MMX-Cart-Request';
  const MMX_EXTERNAL_CART_REFRESH_DELAYS = [40, 220, 650, 1400];

  function sanitizeCartQuantity(value, fallback, options) {
    const opts = options || {};
    const allowZero = opts.allowZero !== false;
    const max = Math.max(1, Number(opts.max || MMX_MAX_CART_LINE_QUANTITY));
    const fallbackValue = Number.isFinite(Number(fallback)) ? Number(fallback) : (allowZero ? 0 : 1);
    const raw = String(value == null ? '' : value).trim();

    if (!raw) return Math.min(max, Math.max(allowZero ? 0 : 1, Math.floor(fallbackValue)));

    // Keep digits only. This prevents decimals, exponents, negative signs, Infinity, NaN, etc.
    const digits = raw.replace(/[^0-9]/g, '');
    if (!digits) return Math.min(max, Math.max(allowZero ? 0 : 1, Math.floor(fallbackValue)));

    const parsed = Number(digits);
    if (!Number.isFinite(parsed)) return max;

    return Math.min(max, Math.max(allowZero ? 0 : 1, Math.floor(parsed)));
  }


  // ----------------------------------------------------------
  // Compatibility bridge for externally managed cart mutations
  // ----------------------------------------------------------
  // Some custom product templates add several variants through their own
  // fetch/XMLHttpRequest sequence instead of submitting a standard Shopify
  // product form. Those requests bypass MMX's normal add-to-cart handler.
  // Observe successful external cart writes and reconcile the drawer from
  // Shopify's authoritative /cart.js state after the batch settles.

  function mmxHeaderValue(headers, name) {
    if (!headers) return '';

    if (typeof headers.get === 'function') {
      return String(headers.get(name) || '');
    }

    const target = String(name || '').toLowerCase();

    if (Array.isArray(headers)) {
      const match = headers.find((entry) => (
        Array.isArray(entry)
        && String(entry[0] || '').toLowerCase() === target
      ));
      return match ? String(match[1] || '') : '';
    }

    if (typeof headers === 'object') {
      const key = Object.keys(headers).find((candidate) => (
        String(candidate || '').toLowerCase() === target
      ));
      return key ? String(headers[key] || '') : '';
    }

    return '';
  }

  function mmxCartMutationRequestMeta(input, init) {
    const options = init || {};
    let url = '';
    let method = String(options.method || '').toUpperCase();
    let headers = options.headers;

    if (typeof input === 'string' || (typeof URL !== 'undefined' && input instanceof URL)) {
      url = String(input);
    } else if (input && typeof input === 'object') {
      url = String(input.url || '');
      if (!method) method = String(input.method || '').toUpperCase();
      if (!headers) headers = input.headers;
    }

    if (!method) method = 'GET';

    let pathname = '';
    try {
      pathname = new URL(url, window.location.href).pathname;
    } catch (error) {
      pathname = String(url || '').split('?')[0].split('#')[0];
    }

    const isMutation = method !== 'GET'
      && method !== 'HEAD'
      && /\/cart\/(?:add|change|update|clear)(?:\.js)?\/?$/i.test(pathname);

    const isInternal = mmxHeaderValue(headers, MMX_INTERNAL_CART_REQUEST_HEADER) === '1';

    return {
      isMutation,
      isInternal,
      method,
      pathname
    };
  }

  function mmxShowExternalCartPendingState() {
    if (!mmxCartDrawerIsOpen()) return;
    if (cartState && Number(cartState.item_count || 0) > 0) return;

    const pendingCart = cartState || {
      items: [],
      item_count: 0,
      total_price: 0,
      original_total_price: 0,
      total_discount: 0,
      cart_level_discount_applications: []
    };

    renderFastCart(pendingCart, {
      loading: true,
      hideFooter: true,
      message: 'Adding items to cart…',
      source: 'external-cart-mutation-pending'
    });
  }

  function mmxScheduleAuthoritativeCartReconciliation(source) {
    externalCartMutationPendingUntil = Date.now() + 3200;
    addPendingUntil = Math.max(addPendingUntil, externalCartMutationPendingUntil);

    // A cart read may already be in flight from page load or drawer opening.
    // Do not let that cached promise block the post-mutation authoritative read.
    cartPromise = null;

    mmxShowExternalCartPendingState();

    externalCartMutationRefreshTimers.forEach((timer) => window.clearTimeout(timer));
    externalCartMutationRefreshTimers = MMX_EXTERNAL_CART_REFRESH_DELAYS.map((delay, index) => (
      window.setTimeout(() => {
        Promise.resolve(refresh(true)).catch((error) => {
          console.warn('[MMX Cart] External cart reconciliation failed.', {
            source: source || 'external',
            attempt: index + 1,
            error
          });
        });
      }, delay)
    ));
  }

  function mmxInstallExternalCartMutationObserver() {
    if (window.__mmxExternalCartMutationObserverInstalled) return;
    window.__mmxExternalCartMutationObserverInstalled = true;

    if (typeof window.fetch === 'function') {
      const originalFetch = window.fetch.bind(window);

      window.fetch = function mmxObservedFetch(input, init) {
        const meta = mmxCartMutationRequestMeta(input, init);
        const request = originalFetch(input, init);

        if (meta.isMutation && !meta.isInternal) {
          Promise.resolve(request)
            .then((response) => {
              if (response && response.ok) {
                mmxScheduleAuthoritativeCartReconciliation('fetch');
              }
            })
            .catch(() => {
              // The originating custom code remains responsible for its own error UI.
            });
        }

        return request;
      };
    }

    if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
      const xhrPrototype = window.XMLHttpRequest.prototype;
      const originalOpen = xhrPrototype.open;
      const originalSend = xhrPrototype.send;
      const originalSetRequestHeader = xhrPrototype.setRequestHeader;

      xhrPrototype.open = function mmxObservedXhrOpen(method, url) {
        this.__mmxCartRequestMeta = mmxCartMutationRequestMeta(url, { method });
        return originalOpen.apply(this, arguments);
      };

      xhrPrototype.setRequestHeader = function mmxObservedXhrHeader(name, value) {
        if (
          this.__mmxCartRequestMeta
          && String(name || '').toLowerCase() === MMX_INTERNAL_CART_REQUEST_HEADER.toLowerCase()
          && String(value || '') === '1'
        ) {
          this.__mmxCartRequestMeta.isInternal = true;
        }
        return originalSetRequestHeader.apply(this, arguments);
      };

      xhrPrototype.send = function mmxObservedXhrSend() {
        const meta = this.__mmxCartRequestMeta;

        if (meta && meta.isMutation && !meta.isInternal) {
          this.addEventListener('loadend', () => {
            if (this.status >= 200 && this.status < 300) {
              mmxScheduleAuthoritativeCartReconciliation('xhr');
            }
          }, { once: true });
        }

        return originalSend.apply(this, arguments);
      };
    }
  }



  // ----------------------------------------------------------
  // Cart drawer vs floating Customer Hub widgets
  // ----------------------------------------------------------
  // Klaviyo's Customer Hub launcher is injected independently of the
  // theme and can use its own very high stacking layer. Rather than
  // permanently disabling it, hide it only while the MMX cart drawer
  // is open, then restore it immediately when the drawer closes.
  const mmxDrawerHiddenWidgetNodes = new Set();
  let mmxDrawerWidgetObserver = null;
  let mmxDrawerWidgetSyncTimer = null;
  let mmxDrawerWidgetFollowupTimers = [];

  function mmxMobileDrawerViewport() {
    return Boolean(
      window.matchMedia
      && window.matchMedia('(max-width: 749px)').matches
    );
  }

  function mmxCaptureInlineStyle(element, properties) {
    const snapshot = {};

    properties.forEach((property) => {
      snapshot[property] = {
        value: element.style.getPropertyValue(property),
        priority: element.style.getPropertyPriority(property)
      };
    });

    return snapshot;
  }

  function mmxRestoreInlineStyle(element, snapshot) {
    if (!element || !snapshot) return;

    Object.keys(snapshot).forEach((property) => {
      const state = snapshot[property];

      if (state && state.value) {
        element.style.setProperty(
          property,
          state.value,
          state.priority || ''
        );
      } else {
        element.style.removeProperty(property);
      }
    });
  }

  function mmxLockMobilePageScroll() {
    window.clearTimeout(mmxMobileScrollUnlockTimer);
    mmxMobileScrollUnlockTimer = null;

    if (
      mmxMobileScrollLockActive
      || !mmxMobileDrawerViewport()
      || !document.body
      || !document.documentElement
    ) {
      return;
    }

    const html = document.documentElement;
    const body = document.body;

    mmxMobileScrollLockY = Math.max(
      0,
      Number(window.scrollY || window.pageYOffset || 0)
    );

    mmxMobileScrollLockStyles = {
      body: mmxCaptureInlineStyle(body, [
        'position',
        'top',
        'left',
        'right',
        'width'
      ])
    };

    html.classList.add('mmx-cart-mobile-scroll-locked');
    body.setAttribute('data-mmx-cart-mobile-scroll-locked', 'true');

    html.style.setProperty('overflow', 'hidden', 'important');
    html.style.setProperty('overscroll-behavior', 'none', 'important');

    body.style.setProperty('position', 'fixed', 'important');
    body.style.setProperty('top', `-${mmxMobileScrollLockY}px`, 'important');
    body.style.setProperty('left', '0', 'important');
    body.style.setProperty('right', '0', 'important');
    body.style.setProperty('width', '100%', 'important');
    body.style.setProperty('overflow', 'hidden', 'important');
    body.style.setProperty('overscroll-behavior', 'none', 'important');

    mmxMobileScrollLockActive = true;
  }

  function mmxUnlockMobilePageScroll(options) {
    const opts = options || {};

    window.clearTimeout(mmxMobileScrollUnlockTimer);
    mmxMobileScrollUnlockTimer = null;

    const html = document.documentElement;
    const body = document.body;
    if (!html || !body) return;

    const hadLock = (
      mmxMobileScrollLockActive
      || html.classList.contains('mmx-cart-mobile-scroll-locked')
      || body.getAttribute('data-mmx-cart-mobile-scroll-locked') === 'true'
      || body.style.getPropertyValue('position') === 'fixed'
    );

    if (!hadLock && !opts.force) return;

    const restoreY = Math.max(0, Number(mmxMobileScrollLockY || 0));

    html.classList.remove('mmx-cart-mobile-scroll-locked');
    body.removeAttribute('data-mmx-cart-mobile-scroll-locked');

    /*
      Always remove the overflow properties written by MMX instead of restoring
      the values captured during opening. Symmetry may already have applied its
      own temporary overflow:hidden before the MMX open event, and restoring
      that captured value would leave the page permanently unscrollable.
    */
    html.style.removeProperty('overflow');
    html.style.removeProperty('overscroll-behavior');
    body.style.removeProperty('overflow');
    body.style.removeProperty('overscroll-behavior');

    if (mmxMobileScrollLockStyles && mmxMobileScrollLockStyles.body) {
      mmxRestoreInlineStyle(body, mmxMobileScrollLockStyles.body);
    } else {
      body.style.removeProperty('position');
      body.style.removeProperty('top');
      body.style.removeProperty('left');
      body.style.removeProperty('right');
      body.style.removeProperty('width');
    }

    mmxMobileScrollLockActive = false;
    mmxMobileScrollLockStyles = null;

    if (opts.restorePosition === false) return;

    // Safari can apply additional layout passes after fixed positioning is
    // removed. Restore the exact original position, then repeat once as a
    // safety pass without re-locking the page.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo(0, restoreY);

        window.setTimeout(() => {
          if (!mmxMobileDrawerOpenIntent) {
            html.classList.remove('mmx-cart-mobile-scroll-locked');
            body.removeAttribute('data-mmx-cart-mobile-scroll-locked');
            html.style.removeProperty('overflow');
            html.style.removeProperty('overscroll-behavior');
            body.style.removeProperty('overflow');
            body.style.removeProperty('overscroll-behavior');

            if (body.style.getPropertyValue('position') === 'fixed') {
              body.style.removeProperty('position');
              body.style.removeProperty('top');
              body.style.removeProperty('left');
              body.style.removeProperty('right');
              body.style.removeProperty('width');
            }

            window.scrollTo(0, restoreY);
          }
        }, 180);
      });
    });
  }

  function mmxScheduleMobilePageScrollUnlock(delay) {
    window.clearTimeout(mmxMobileScrollUnlockTimer);

    const wait = Math.max(0, Number(delay || 0));

    mmxMobileScrollUnlockTimer = window.setTimeout(() => {
      mmxMobileScrollUnlockTimer = null;

      if (!mmxMobileDrawerOpenIntent) {
        mmxUnlockMobilePageScroll({ force: true });

        window.setTimeout(() => {
          if (!mmxMobileDrawerOpenIntent) {
            mmxUnlockMobilePageScroll({
              force: true,
              restorePosition: false
            });
          }
        }, 420);
      }
    }, wait);
  }

  function mmxCartDrawerIsOpen() {
    const popups = document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup');
    return Array.from(popups).some((popup) => {
      if (popup.getAttribute('aria-hidden') === 'false') return true;
      if (popup.classList.contains('is-open') || popup.classList.contains('active') || popup.classList.contains('cc-popup--visible')) return true;

      const style = window.getComputedStyle ? window.getComputedStyle(popup) : null;
      return Boolean(
        popup.getAttribute('aria-hidden') !== 'true'
        && style
        && style.display !== 'none'
        && style.visibility !== 'hidden'
        && Number(style.opacity || 1) > 0
      );
    });
  }

  function mmxElementHaystack(element) {
    if (!(element instanceof Element)) return '';
    const className = typeof element.className === 'string' ? element.className : '';
    return [
      element.tagName || '',
      element.id || '',
      className,
      element.getAttribute('data-testid') || '',
      element.getAttribute('data-klaviyo') || '',
      element.getAttribute('data-klaviyo-customer-hub') || '',
      element.getAttribute('aria-label') || '',
      element.getAttribute('title') || '',
      element.getAttribute('src') || ''
    ].join(' ').toLowerCase();
  }

  function mmxLooksLikeKlaviyoHubElement(element) {
    const haystack = mmxElementHaystack(element);
    if (!haystack) return false;

    return haystack.includes('kl-hub')
      || haystack.includes('customer-hub')
      || haystack.includes('customer hub')
      || (
        haystack.includes('klaviyo')
        && (haystack.includes('widget') || haystack.includes('hub') || haystack.includes('chat'))
      );
  }

  function mmxTopLightDomHost(element) {
    let target = element;
    let rootNode = target && target.getRootNode ? target.getRootNode() : document;

    while (rootNode && rootNode !== document && rootNode.host) {
      target = rootNode.host;
      rootNode = target.getRootNode ? target.getRootNode() : document;
    }

    return target instanceof Element ? target : null;
  }

  function mmxMarkFloatingWidgetHidden(element) {
    const target = mmxTopLightDomHost(element);
    if (!target || target.closest('.mmx-cart-drawer-shell')) return;

    target.setAttribute('data-mmx-hidden-by-cart-drawer', 'true');
    mmxDrawerHiddenWidgetNodes.add(target);
  }

  function mmxScanKlaviyoHubSubtree(rootNode) {
    if (!rootNode) return;

    const inspect = (element) => {
      if (!(element instanceof Element)) return;

      if (mmxLooksLikeKlaviyoHubElement(element)) {
        mmxMarkFloatingWidgetHidden(element);
      }

      // Customer Hub may render inside an open shadow root. If a hub node
      // is found there, hide the top-level host that site CSS can control.
      if (element.shadowRoot) {
        const shadowElements = Array.from(element.shadowRoot.querySelectorAll('*'));
        if (shadowElements.some(mmxLooksLikeKlaviyoHubElement)) {
          mmxMarkFloatingWidgetHidden(element);
        }
        shadowElements.forEach(inspect);
      }
    };

    if (rootNode instanceof Element) inspect(rootNode);
    if (rootNode.querySelectorAll) {
      Array.from(rootNode.querySelectorAll('*')).forEach(inspect);
    }
  }

  function mmxExternalFixedAncestor(element) {
    let current = element;

    while (current && current instanceof Element && current !== document.body) {
      if (current.closest('.mmx-cart-drawer-shell')) return null;
      const style = window.getComputedStyle(current);
      if (style.position === 'fixed') return current;

      if (current.parentElement) {
        current = current.parentElement;
      } else {
        const rootNode = current.getRootNode ? current.getRootNode() : null;
        current = rootNode && rootNode.host ? rootNode.host : null;
      }
    }

    return null;
  }

  function mmxHideOverlappingBottomRightWidget() {
    const drawerPanel = document.querySelector(
      '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup[aria-hidden="false"] .cc-popup-modal, '
      + '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup.is-open .cc-popup-modal, '
      + '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup.cc-popup--visible .cc-popup-modal'
    );
    if (!drawerPanel || !document.elementsFromPoint) return;

    const drawerRect = drawerPanel.getBoundingClientRect();
    if (!drawerRect.width || !drawerRect.height) return;

    const xValues = [drawerRect.right - 10, drawerRect.right - 28, drawerRect.right - 52]
      .map((value) => Math.max(1, Math.min(window.innerWidth - 1, value)));
    const yValues = [12, 32, 56, 84, 116]
      .map((offset) => Math.max(1, Math.min(window.innerHeight - 1, window.innerHeight - offset)));

    xValues.forEach((x) => {
      yValues.forEach((y) => {
        const stack = document.elementsFromPoint(x, y);
        for (const element of stack) {
          if (!(element instanceof Element) || element.closest('.mmx-cart-drawer-shell')) continue;

          const fixed = mmxExternalFixedAncestor(element);
          if (!fixed) continue;

          const rect = fixed.getBoundingClientRect();
          const style = window.getComputedStyle(fixed);
          const zIndex = Number.parseInt(style.zIndex, 10);
          const overlapsDrawer = rect.right > drawerRect.left
            && rect.left < drawerRect.right
            && rect.bottom > drawerRect.top
            && rect.top < drawerRect.bottom;
          const isSmallFloatingControl = rect.width >= 20
            && rect.height >= 20
            && rect.width <= 240
            && rect.height <= 240;
          const isBottomRight = rect.right >= window.innerWidth - 120
            && rect.bottom >= window.innerHeight - 160;
          const isHighLayer = Number.isFinite(zIndex) ? zIndex >= 1000 : false;

          if (overlapsDrawer && isSmallFloatingControl && isBottomRight && isHighLayer) {
            mmxMarkFloatingWidgetHidden(fixed);
            break;
          }
        }
      });
    });
  }

  function mmxRestoreDrawerHiddenWidgets() {
    mmxDrawerHiddenWidgetNodes.forEach((node) => {
      if (node && node.removeAttribute) node.removeAttribute('data-mmx-hidden-by-cart-drawer');
    });
    mmxDrawerHiddenWidgetNodes.clear();
  }

  function mmxSyncDrawerWidgetVisibility() {
    const isOpen = mmxCartDrawerIsOpen();
    document.documentElement.classList.toggle('mmx-cart-drawer-open', isOpen);

    /*
      Widget visibility may use an approximate mounted-drawer detector, but
      page scroll locking must follow only the explicit drawer open/close
      events. Symmetry keeps its off-canvas markup visible in the DOM after
      closing, so the approximate detector can otherwise re-lock the page.
    */

    if (!isOpen) {
      mmxRestoreDrawerHiddenWidgets();
      return;
    }

    mmxScanKlaviyoHubSubtree(document.body);
    mmxHideOverlappingBottomRightWidget();
  }

  function mmxScheduleDrawerWidgetVisibilitySync(delays) {
    window.clearTimeout(mmxDrawerWidgetSyncTimer);
    mmxDrawerWidgetSyncTimer = window.setTimeout(mmxSyncDrawerWidgetVisibility, 0);

    mmxDrawerWidgetFollowupTimers.forEach((timer) => window.clearTimeout(timer));
    mmxDrawerWidgetFollowupTimers = (delays || []).map((delay) => (
      window.setTimeout(mmxSyncDrawerWidgetVisibility, delay)
    ));
  }

  function mmxObserveDrawerAndFloatingWidgets() {
    if (!document.documentElement) return;
    if (mmxDrawerWidgetObserver) mmxDrawerWidgetObserver.disconnect();

    mmxDrawerWidgetObserver = new MutationObserver((mutations) => {
      let drawerStateMayHaveChanged = false;
      const drawerOpen = document.documentElement.classList.contains('mmx-cart-drawer-open') || mmxCartDrawerIsOpen();

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target;
          if (target instanceof Element && target.closest('.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup')) {
            drawerStateMayHaveChanged = true;
          }
          return;
        }

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('.mmx-cart-drawer-shell, .mmx-cart-drawer-shell *') || node.querySelector('.mmx-cart-drawer-shell')) {
            drawerStateMayHaveChanged = true;
          }
          if (drawerOpen) mmxScanKlaviyoHubSubtree(node);
        });
      });

      if (drawerStateMayHaveChanged || drawerOpen) {
        mmxScheduleDrawerWidgetVisibilitySync(drawerOpen ? [60, 220] : []);
      }
    });

    mmxDrawerWidgetObserver.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'class']
    });

    mmxScheduleDrawerWidgetVisibilitySync();
  }


  function startFastCartFeedback(message) {
    const text = message || 'Updating cart…';
    document.documentElement.classList.add('mmx-cart-fast-feedback');
    document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer').forEach((drawer) => {
      drawer.setAttribute('data-mmx-fast-message', text);
    });
    window.clearTimeout(fastOpenTimer);
    fastOpenTimer = window.setTimeout(stopFastCartFeedback, 3200);
  }

  function stopFastCartFeedback() {
    document.documentElement.classList.remove('mmx-cart-fast-feedback');
    document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer[data-mmx-fast-message]').forEach((drawer) => {
      drawer.removeAttribute('data-mmx-fast-message');
    });
    window.clearTimeout(fastOpenTimer);
    fastOpenTimer = null;
  }

  function openDrawerImmediately(message) {
    startFastCartFeedback(message || 'Updating cart…');
    document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', {
      bubbles: true,
      cancelable: false,
      detail: { mmxFastOpen: true }
    }));
  }

  function markCartItemWorking(item, message, options) {
    if (!item) return;

    const opts = options || {};
    item.classList.add('mmx-cart-item-working');
    item.classList.toggle('mmx-cart-item-working--silent', Boolean(opts.silent));
    item.setAttribute('data-mmx-working-message', message || 'Updating…');

    item.querySelectorAll('a, button, input, select').forEach((control) => {
      control.setAttribute('aria-disabled', 'true');
    });
  }

  function clearCartItemWorking() {
    document.querySelectorAll('.mmx-cart-item-working').forEach((item) => {
      item.classList.remove('mmx-cart-item-working', 'mmx-cart-item-working--silent');
      item.removeAttribute('data-mmx-working-message');
      item.querySelectorAll('[aria-disabled="true"]').forEach((control) => {
        control.removeAttribute('aria-disabled');
      });
    });
  }

  const escapeHtml = (value) => String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  function parseConfig() {
    const nodes = document.querySelectorAll('script#MMXSpendTierConfig');
    const node = nodes.length ? nodes[nodes.length - 1] : null;
    if (!node) {
      config = null;
      configText = '';
      return null;
    }

    const nextText = node.textContent.trim();
    if (config && configText === nextText) return config;

    try {
      config = JSON.parse(nextText);
      config.tiers = Array.isArray(config.tiers)
        ? config.tiers
            .filter((tier) => Number(tier.amountCents) > 0 && tier.text)
            .sort((a, b) => Number(a.amountCents) - Number(b.amountCents))
        : [];

      if (!inventorySnapshot && config.inventory) {
        inventorySnapshot = config.inventory;
      }

      configText = nextText;
    } catch (error) {
      console.error('[MMX Cart] Invalid spend tier configuration.', error);
      config = null;
      configText = '';
    }

    return config;
  }

  // ----------------------------------------------------------
  // Pre-order checkout acknowledgement
  // ----------------------------------------------------------

  function preorderConfig() {
    const cfg = parseConfig();
    const settings = cfg && cfg.preorder ? cfg.preorder : {};

    return {
      enabled: Boolean(settings.enabled),
      title: String(settings.title || 'Pre-Order Notice').trim() || 'Pre-Order Notice',
      description1: String(settings.description1 || '').trim(),
      description2: String(settings.description2 || '').trim(),
      agreementLabel: String(
        settings.agreementLabel
        || 'I have acknowledged this and wish to proceed with the order.'
      ).trim(),
      buttonLabel: String(settings.buttonLabel || 'Checkout').trim() || 'Checkout'
    };
  }

  function normalizePreorderPropertyName(value) {
    return String(value || '')
      .replace(/^_+/, '')
      .replace(/[\s_-]+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function cartItemPreorderNote(item) {
    const properties = item && item.properties && typeof item.properties === 'object'
      ? item.properties
      : {};
    const acceptedNames = new Set([
      'preorder note',
      'pre order note',
      'fulfillment month',
      'fulfilment month'
    ]);

    for (const name of Object.keys(properties)) {
      if (!acceptedNames.has(normalizePreorderPropertyName(name))) continue;
      const value = String(properties[name] == null ? '' : properties[name]).trim();
      if (value) return value;
    }

    return '';
  }

  function preorderCartItems(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    return items.reduce((result, item) => {
      const note = cartItemPreorderNote(item);
      if (!note) return result;

      result.push({
        key: String(item.key || ''),
        variantId: Number(item.variant_id || 0),
        title: String(item.product_title || item.title || 'Pre-order item').trim(),
        variantTitle: String(item.variant_title || '').trim(),
        quantity: Math.max(1, Number(item.quantity || 1)),
        note
      });
      return result;
    }, []);
  }

  function preorderCartSignature(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];

    return JSON.stringify(items.map((item) => {
      const properties = item && item.properties && typeof item.properties === 'object'
        ? Object.keys(item.properties)
            .sort()
            .map((name) => [name, String(item.properties[name] == null ? '' : item.properties[name])])
        : [];

      return [
        String(item.key || ''),
        Number(item.variant_id || 0),
        Number(item.quantity || 0),
        Number(item.final_line_price || 0),
        properties
      ];
    }));
  }

  function preorderRequiresAcknowledgement(cart) {
    const settings = preorderConfig();
    if (!settings.enabled) return false;
    if (!preorderCartItems(cart).length) return false;

    return preorderAcknowledgedSignature !== preorderCartSignature(cart);
  }

  function safePreorderRichText(value) {
    const template = document.createElement('template');
    template.innerHTML = String(value || '');
    const allowed = new Set(['B', 'STRONG', 'I', 'EM', 'BR', 'P']);

    const clean = (parent) => {
      Array.from(parent.childNodes).forEach((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (!allowed.has(node.tagName)) {
          const fragment = document.createDocumentFragment();
          while (node.firstChild) fragment.appendChild(node.firstChild);
          node.replaceWith(fragment);
          clean(parent);
          return;
        }

        Array.from(node.attributes || []).forEach((attribute) => {
          node.removeAttribute(attribute.name);
        });
        clean(node);
      });
    };

    clean(template.content);
    return template.innerHTML;
  }

  function preorderModalNode() {
    return document.querySelector('[data-mmx-preorder-modal]');
  }

  function renderPreorderModal(cart) {
    const modal = preorderModalNode();
    if (!modal) return null;

    const settings = preorderConfig();
    const items = preorderCartItems(cart);
    const title = modal.querySelector('[data-mmx-preorder-title]');
    const description1 = modal.querySelector('[data-mmx-preorder-description-1]');
    const description2 = modal.querySelector('[data-mmx-preorder-description-2]');
    const itemList = modal.querySelector('[data-mmx-preorder-items]');
    const agreement = modal.querySelector('[data-mmx-preorder-agreement-text]');
    const submit = modal.querySelector('[data-mmx-preorder-confirm]');

    if (title) title.textContent = settings.title;
    if (description1) {
      description1.textContent = settings.description1;
      description1.hidden = !settings.description1;
    }
    if (description2) {
      description2.innerHTML = safePreorderRichText(settings.description2);
      description2.hidden = !settings.description2;
    }
    if (agreement) agreement.textContent = settings.agreementLabel;
    if (submit) submit.textContent = settings.buttonLabel;

    if (itemList) {
      itemList.innerHTML = items.map((item) => {
        const quantity = item.quantity > 1 ? ` <span class="mmx-preorder-modal__quantity">×${item.quantity}</span>` : '';
        return `<li class="mmx-preorder-modal__item">
          <div class="mmx-preorder-modal__product">${escapeHtml(item.title)}${quantity}</div>
          <div class="mmx-preorder-modal__month">${escapeHtml(item.note)}</div>
        </li>`;
      }).join('');
      itemList.hidden = !items.length;
    }

    return modal;
  }

  function closePreorderModal(options) {
    const opts = options || {};
    const modal = preorderModalNode();

    if (modal) {
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      const checkbox = modal.querySelector('[data-mmx-preorder-checkbox]');
      const submit = modal.querySelector('[data-mmx-preorder-confirm]');
      if (checkbox) checkbox.checked = false;
      if (submit) submit.disabled = true;
    }

    document.documentElement.classList.remove('mmx-preorder-modal-open');

    if (!opts.keepPending) preorderPendingAction = null;

    if (opts.restoreFocus && preorderReturnFocus && preorderReturnFocus.isConnected) {
      preorderReturnFocus.focus({ preventScroll: true });
    }

    preorderReturnFocus = null;
  }

  function openPreorderModal(action) {
    if (!preorderRequiresAcknowledgement(cartState)) return false;

    const modal = renderPreorderModal(cartState);
    if (!modal) return false;

    closeDesktopVariantMenu({ focus: false });
    preorderPendingAction = action || null;
    preorderReturnFocus = action && action.trigger && action.trigger.isConnected
      ? action.trigger
      : document.activeElement;

    const checkbox = modal.querySelector('[data-mmx-preorder-checkbox]');
    const submit = modal.querySelector('[data-mmx-preorder-confirm]');
    if (checkbox) checkbox.checked = false;
    if (submit) submit.disabled = true;

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mmx-preorder-modal-open');

    window.requestAnimationFrame(() => {
      const closeButton = modal.querySelector('[data-mmx-preorder-close]');
      (closeButton || checkbox || modal).focus({ preventScroll: true });
    });

    return true;
  }

  function walletInteractiveControl(rootNode) {
    if (!rootNode) return null;

    const selector = [
      'button:not([disabled]):not([data-mmx-preorder-wallet-gate])',
      '[role="button"]:not([aria-disabled="true"]):not([data-mmx-preorder-wallet-gate])',
      'input[type="submit"]:not([disabled])'
    ].join(',');

    if (rootNode.querySelector) {
      const direct = rootNode.querySelector(selector);
      if (direct) return direct;
    }

    const descendants = rootNode.querySelectorAll
      ? Array.from(rootNode.querySelectorAll('*'))
      : [];

    for (const node of descendants) {
      if (node.shadowRoot) {
        const nested = walletInteractiveControl(node.shadowRoot);
        if (nested) return nested;
      }
    }

    if (rootNode.shadowRoot) return walletInteractiveControl(rootNode.shadowRoot);
    return null;
  }

  function continuePreorderPendingAction(action) {
    if (!action) return;

    if (action.type === 'checkout' && action.href) {
      window.location.assign(action.href);
      return;
    }

    if (action.type === 'wallet') {
      const host = acceleratedCheckoutNode(document);
      const control = walletInteractiveControl(host);

      // This runs synchronously inside the customer's confirmation click, so
      // supported wallet components retain the same user-activation gesture.
      if (control && typeof control.click === 'function') {
        control.click();
        return;
      }

      if (host) {
        host.setAttribute('tabindex', '-1');
        host.focus({ preventScroll: true });
      }
    }
  }

  function confirmPreorderModal() {
    const modal = preorderModalNode();
    if (!modal) return;

    const checkbox = modal.querySelector('[data-mmx-preorder-checkbox]');
    if (!checkbox || !checkbox.checked) return;

    preorderAcknowledgedSignature = preorderCartSignature(cartState);
    const action = preorderPendingAction;
    closePreorderModal({ keepPending: false, restoreFocus: false });
    syncPreorderCheckoutGate(cartState);
    continuePreorderPendingAction(action);
  }

  function syncPreorderCheckoutGate(cart) {
    const settings = preorderConfig();
    const items = preorderCartItems(cart);
    const requiresAcknowledgement = settings.enabled
      && items.length > 0
      && preorderAcknowledgedSignature !== preorderCartSignature(cart);

    if (!settings.enabled || !items.length) {
      preorderAcknowledgedSignature = '';
      if (document.documentElement.classList.contains('mmx-preorder-modal-open')) {
        closePreorderModal({ restoreFocus: false });
      }
    } else if (document.documentElement.classList.contains('mmx-preorder-modal-open')) {
      renderPreorderModal(cart);
      const modal = preorderModalNode();
      const checkbox = modal && modal.querySelector('[data-mmx-preorder-checkbox]');
      const submit = modal && modal.querySelector('[data-mmx-preorder-confirm]');
      if (checkbox) checkbox.checked = false;
      if (submit) submit.disabled = true;
    }

    document.querySelectorAll('[data-mmx-accelerated-checkout]').forEach((host) => {
      let gate = host.querySelector('[data-mmx-preorder-wallet-gate]');

      if (requiresAcknowledgement && host.getAttribute('data-mmx-wallet-state') !== 'empty') {
        host.classList.add('mmx-preorder-wallet-locked');
        if (!gate) {
          gate = document.createElement('button');
          gate.type = 'button';
          gate.className = 'mmx-preorder-wallet-gate';
          gate.setAttribute('data-mmx-preorder-wallet-gate', '');
          gate.setAttribute('aria-label', 'Review pre-order notice before express checkout');
          gate.innerHTML = '<span class="visually-hidden">Review pre-order notice before express checkout</span>';
          host.appendChild(gate);
        }
      } else {
        host.classList.remove('mmx-preorder-wallet-locked');
        if (gate) gate.remove();
      }
    });
  }

  function preorderFocusTrap(event) {
    if (event.key !== 'Tab') return;
    const modal = preorderModalNode();
    if (!modal || modal.hidden) return;

    const focusable = Array.from(modal.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'
    )).filter((node) => node.offsetParent !== null);

    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function formatMoney(cents) {
    const format = (window.theme && (window.theme.money_format_with_cart_code_preference || window.theme.money_format)) || '${{amount}}';
    const value = Number(cents || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return (value / 100).toFixed(2);

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (Number(number || 0) / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[0] + (parts[1] ? decimal + parts[1] : '');
    };

    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = formatWithDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        formatted = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = formatWithDelimiters(value, 2, ',', '.');
    }

    return format.replace(placeholder, formatted);
  }


  function normalizeProductPrice(value) {
    if (value == null || value === '') return 0;
    if (typeof value === 'number') return Math.round(value);
    const raw = String(value).trim();
    if (!raw) return 0;
    if (raw.indexOf('.') >= 0) return Math.round(Number(raw.replace(/[^0-9.]/g, '')) * 100);
    return Math.round(Number(raw.replace(/[^0-9-]/g, '')) || 0);
  }

  function productJsonUrls(handle, productUrl) {
    const urls = [];
    const cleanHandle = String(handle || '').replace(/^\/+|\.js$/g, '').trim();

    const addUrl = (url) => {
      if (!url || urls.includes(url)) return;
      urls.push(url);
    };

    if (productUrl) {
      try {
        const parsed = new URL(String(productUrl), window.location.origin);
        let path = parsed.pathname.replace(/\/$/, '');
        if (path && path.indexOf('/products/') >= 0) {
          if (!path.endsWith('.js')) path = `${path}.js`;
          addUrl(path);
        }
      } catch (error) {
        const path = String(productUrl).split('?')[0].replace(/\/$/, '');
        if (path && path.indexOf('/products/') >= 0) addUrl(path.endsWith('.js') ? path : `${path}.js`);
      }
    }

    if (cleanHandle) {
      addUrl(`${root}products/${encodeURIComponent(cleanHandle)}.js`);
      addUrl(`/products/${encodeURIComponent(cleanHandle)}.js`);
    }

    return urls;
  }

  async function fetchProductData(handle, productUrl) {
    const cleanHandle = String(handle || '').replace(/^\/+|\.js$/g, '').trim();
    const cacheKey = `${cleanHandle}|${String(productUrl || '').split('?')[0]}`;
    if (!cleanHandle && !productUrl) return null;
    if (productDataCache.has(cacheKey)) return productDataCache.get(cacheKey);

    const promise = (async () => {
      const urls = productJsonUrls(cleanHandle, productUrl);
      for (const url of urls) {
        try {
          const response = await fetch(url, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          if (response.ok) return response.json();
        } catch (error) {
          // Try the next candidate URL.
        }
      }

      console.warn('[MMX Cart] Unable to fetch product data for cart variant editor.', { handle: cleanHandle, productUrl });
      return null;
    })();

    productDataCache.set(cacheKey, promise);
    return promise;
  }

  function normaliseTagList(tags) {
    if (Array.isArray(tags)) return tags.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean);
    return String(tags || '').split(',').map((tag) => tag.trim().toLowerCase()).filter(Boolean);
  }

  function hasExcludedTag(productTags, excludedTags) {
    const productTagList = normaliseTagList(productTags);
    const excludedTagList = normaliseTagList(excludedTags);
    if (!productTagList.length || !excludedTagList.length) return false;
    return excludedTagList.some((tag) => productTagList.includes(tag));
  }

  function cartItemHasBnplExclusionProperty(item) {
    const props = item && item.properties ? item.properties : {};
    return String(props._mmx_bnpl_excluded || props._MMX_BNPL_EXCLUDED || '').trim() === '1'
      || String(props._mmx_bnpl_excluded_tag || '').trim() !== '';
  }

  function currentProductIsBnplExcluded(cfg) {
    if (!cfg || !cfg.currentProduct) return false;
    return hasExcludedTag(cfg.currentProduct.tags || [], cfg.bnpl && cfg.bnpl.excludedTags ? cfg.bnpl.excludedTags : 'investmentbar');
  }

  function formVariantId(form) {
    if (!form) return 0;
    const idField = form.querySelector('[name="id"]');
    return idField ? Number(idField.value || 0) : 0;
  }

  function formMatchesCurrentProduct(form, cfg) {
    if (!form || !cfg || !cfg.currentProduct) return false;
    const variants = Array.isArray(cfg.currentProduct.variantIds) ? cfg.currentProduct.variantIds.map(Number) : [];
    const variantId = formVariantId(form);
    if (variantId && variants.includes(variantId)) return true;

    const handle = cfg.currentProduct.handle;
    const action = String(form.getAttribute('action') || '');
    return Boolean(handle && action.indexOf(`/products/${handle}`) >= 0);
  }

  function isDrawerRecommendationForm(form) {
    return Boolean(form && form.closest && form.closest('.mmx-cart-addon-card, .mmx-empty-rec-card'));
  }

  const MMX_RECOMMENDATION_ATTRIBUTION_VERSION = 'v11.7';

  function normaliseRecommendationSource(value) {
    const source = String(value || '').trim().toLowerCase().replace(/-/g, '_');
    if (source === 'you_may_like' || source === 'recently_viewed') return source;
    return '';
  }

  function recommendationSourceForForm(form) {
    if (!form || !form.closest) return '';
    const shell = form.closest('[data-mmx-addons][data-mmx-recommendation-kind]');
    if (!shell) return '';
    return normaliseRecommendationSource(
      shell.getAttribute('data-mmx-recommendation-kind')
    );
  }

  function recommendationSourceFromCustomiseLanding() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('mmx_customise') !== '1') return '';
      return normaliseRecommendationSource(params.get('mmx_rec_source'));
    } catch (error) {
      return '';
    }
  }

  function stampRecommendationAttributionProperties(form, formData) {
    if (!formData) return '';

    const source = recommendationSourceForForm(form)
      || recommendationSourceFromCustomiseLanding();

    if (!source) return '';

    formData.set('properties[_mmx_source]', source);
    formData.set('properties[_mmx_surface]', 'cart_drawer');
    formData.set('properties[_mmx_reco_version]', MMX_RECOMMENDATION_ATTRIBUTION_VERSION);
    return source;
  }

  function shellCurrentProductBnplExcluded() {
    const shell = document.querySelector('.mmx-cart-drawer-shell');
    return shell && shell.getAttribute('data-mmx-current-product-bnpl-excluded') === 'true';
  }

  function stampBnplExcludedProperty(formData, tagValue) {
    if (!formData || formData.has('properties[_mmx_bnpl_excluded]')) return;
    formData.append('properties[_mmx_bnpl_excluded]', '1');
    formData.append('properties[_mmx_bnpl_excluded_tag]', tagValue || 'investmentbar');
  }

  function stampBnplPropertiesForForm(form, formData, cfg) {
    if (!form || !formData || !cfg) return;
    const excludedTags = cfg.bnpl && cfg.bnpl.excludedTags ? cfg.bnpl.excludedTags : 'investmentbar';

    // Manual add-ons can carry the hidden property directly from Liquid.
    if (formData.has('properties[_mmx_bnpl_excluded]')) return;

    // Product page forms do not usually expose product tags in JS, so Liquid passes the current product tags into config.
    // V1.9.22 also reads a direct Liquid-rendered shell attribute, which is more reliable in theme-editor Ajax updates.
    const isRecommendationForm = isDrawerRecommendationForm(form);
    const isCurrentProductForm = !isRecommendationForm && (shellCurrentProductBnplExcluded() || formMatchesCurrentProduct(form, cfg));

    if (
      !isRecommendationForm
      && (
        formDataHasDomBnplExclusion(formData)
        || shellCurrentProductBnplExcluded()
        || ((currentProductIsBnplExcluded(cfg) || currentProductMatchesExcludedProduct(cfg) || currentProductMatchesExcludedHandleOrSku(cfg)) && isCurrentProductForm)
      )
    ) {
      stampBnplExcludedProperty(formData, excludedTags);
    }
  }

  function cartHasBnplExcludedProperty(cart) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    return items.some((item) => !isGiftItem(item) && cartItemHasBnplExclusionProperty(item));
  }

  function normaliseExcludedProducts(cfg) {
    const products = cfg && cfg.bnpl && Array.isArray(cfg.bnpl.excludedProducts) ? cfg.bnpl.excludedProducts : [];
    return products.map((product) => ({
      id: Number(product && product.id || 0),
      handle: String(product && product.handle || '').trim().toLowerCase(),
      variantIds: (Array.isArray(product && product.variantIds) ? product.variantIds : []).map(Number).filter(Boolean)
    })).filter((product) => product.id || product.handle || product.variantIds.length);
  }

  function normaliseExactTokens(value) {
    return String(value || '')
      .split(/[\n,]+/)
      .map((token) => token.trim().toLowerCase())
      .filter(Boolean);
  }

  function bnplDomSource() {
    return document.querySelector('.mmx-cart-drawer-shell') || document.querySelector('[data-mmx-bnpl-block]');
  }

  function markerContains(markers, value) {
    const cleanValue = Number(value || 0);
    if (!cleanValue) return false;
    return String(markers || '').indexOf(`,${cleanValue},`) >= 0;
  }

  function domExcludedProductMarkers() {
    const source = bnplDomSource();
    return source ? String(source.getAttribute('data-mmx-bnpl-excluded-product-markers') || '') : '';
  }

  function domExcludedVariantMarkers() {
    const source = bnplDomSource();
    return source ? String(source.getAttribute('data-mmx-bnpl-excluded-variant-markers') || '') : '';
  }

  function domExcludedHandleTokens() {
    const source = bnplDomSource();
    return normaliseExactTokens(source ? source.getAttribute('data-mmx-bnpl-excluded-handles') || '' : '');
  }

  function cartContainsDomBnplExclusion(cart) {
    const productMarkers = domExcludedProductMarkers();
    const variantMarkers = domExcludedVariantMarkers();
    const handleTokens = domExcludedHandleTokens();
    const handleSet = new Set(handleTokens);
    const items = Array.isArray(cart && cart.items) ? cart.items : [];

    return items.some((item) => {
      if (!item || isGiftItem(item)) return false;
      const productId = Number(item.product_id || 0);
      const variantId = Number(item.variant_id || item.id || 0);
      const handle = String(item.handle || '').trim().toLowerCase();
      const sku = String(item.sku || '').trim().toLowerCase();

      return Boolean(
        markerContains(productMarkers, productId)
        || markerContains(variantMarkers, variantId)
        || (handle && handleSet.has(handle))
        || (sku && handleSet.has(sku))
      );
    });
  }

  function formDataHasDomBnplExclusion(formData) {
    if (!formData) return false;
    const variantId = Number(formData.get('id') || 0);
    if (variantId && markerContains(domExcludedVariantMarkers(), variantId)) return true;

    const productId = Number(formData.get('product-id') || formData.get('product_id') || 0);
    if (productId && markerContains(domExcludedProductMarkers(), productId)) return true;

    return false;
  }

  function cartContainsBnplExcludedHandleOrSku(cart, cfg) {
    const tokens = normaliseExactTokens(cfg && cfg.bnpl ? cfg.bnpl.excludedHandles : '');
    if (!tokens.length) return false;
    const tokenSet = new Set(tokens);
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    return items.some((item) => {
      if (!item || isGiftItem(item)) return false;
      const handle = String(item.handle || '').trim().toLowerCase();
      const sku = String(item.sku || '').trim().toLowerCase();
      return Boolean((handle && tokenSet.has(handle)) || (sku && tokenSet.has(sku)));
    });
  }

  function currentProductMatchesExcludedHandleOrSku(cfg) {
    const tokens = normaliseExactTokens(cfg && cfg.bnpl ? cfg.bnpl.excludedHandles : '');
    if (!tokens.length || !cfg || !cfg.currentProduct) return false;
    const handle = String(cfg.currentProduct.handle || '').trim().toLowerCase();
    return Boolean(handle && tokens.includes(handle));
  }

  function cartItemMatchesExcludedProduct(item, excludedProduct) {
    if (!item || !excludedProduct) return false;
    const productId = Number(item.product_id || 0);
    const variantId = Number(item.variant_id || item.id || 0);
    const handle = String(item.handle || '').trim().toLowerCase();

    return Boolean(
      (excludedProduct.id && productId && excludedProduct.id === productId)
      || (excludedProduct.handle && handle && excludedProduct.handle === handle)
      || (variantId && excludedProduct.variantIds && excludedProduct.variantIds.includes(variantId))
    );
  }

  function cartContainsBnplExcludedProduct(cart, cfg) {
    const excludedProducts = normaliseExcludedProducts(cfg);
    if (!excludedProducts.length) return false;

    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    return items.some((item) => (
      item
      && !isGiftItem(item)
      && excludedProducts.some((excludedProduct) => cartItemMatchesExcludedProduct(item, excludedProduct))
    ));
  }

  function currentProductMatchesExcludedProduct(cfg) {
    const current = cfg && cfg.currentProduct ? cfg.currentProduct : null;
    const excludedProducts = normaliseExcludedProducts(cfg);
    if (!current || !excludedProducts.length) return false;

    const currentItemLike = {
      product_id: Number(current.id || 0),
      handle: current.handle,
      variant_id: 0
    };

    if (excludedProducts.some((excludedProduct) => cartItemMatchesExcludedProduct(currentItemLike, excludedProduct))) return true;

    const currentVariantIds = (Array.isArray(current.variantIds) ? current.variantIds : []).map(Number).filter(Boolean);
    return excludedProducts.some((excludedProduct) => (
      excludedProduct.variantIds || []
    ).some((variantId) => currentVariantIds.includes(variantId)));
  }

  let bnplCheckToken = 0;

  async function cartContainsBnplExcludedTag(cart, cfg) {
    const excludedTags = cfg && cfg.bnpl && cfg.bnpl.excludedTags ? cfg.bnpl.excludedTags : 'investmentbar';
    const excludedTagList = normaliseTagList(excludedTags);
    if (!cart || !excludedTagList.length) return false;

    const items = Array.isArray(cart.items) ? cart.items.filter((item) => item && !isGiftItem(item) && item.handle) : [];
    if (!items.length) return false;

    const results = await Promise.all(items.map(async (item) => {
      const product = await fetchProductData(item.handle);
      return product ? hasExcludedTag(product.tags, excludedTagList) : false;
    }));

    return results.some(Boolean);
  }

  function setBnplVisibility(hidden) {
    const shouldHide = Boolean(hidden);
    document.querySelectorAll('[data-mmx-bnpl-block], .mmx-cart-drawer__bnpl').forEach((node) => {
      node.hidden = shouldHide;
      node.classList.toggle('hidden', shouldHide);
      node.classList.toggle('mmx-bnpl-hidden', shouldHide);

      // Important: the BNPL row is styled as display:flex in our CSS.
      // Some browsers/themes can let that override the native [hidden] display:none.
      // Force inline display none when hidden, and remove it when allowed again.
      if (shouldHide) {
        node.style.setProperty('display', 'none', 'important');
      } else {
        node.style.removeProperty('display');
      }
    });
  }

  function currentCartDrawerSectionId() {
    const sectionNode = document.querySelector('.mmx-cart-drawer-shell[data-section-id], .mmx-cart-drawer-shell [data-section-id], [data-mmx-fast-renderer][data-section-id], cart-form[data-section-id]');
    return sectionNode ? sectionNode.getAttribute('data-section-id') : '';
  }

  function currentSectionRenderUrl(sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  function acceleratedCheckoutNode(scope) {
    return (scope || document).querySelector('[data-mmx-accelerated-checkout]');
  }

  function acceleratedCheckoutHasButtons(node) {
    if (!node) return false;
    return Boolean(node.querySelector(
      'shopify-accelerated-checkout-cart, shopify-accelerated-checkout, [data-shopify-buttoncontainer], .shopify-cleanslate, iframe, button'
    ));
  }

  function acceleratedCheckoutIsPainted(node) {
    if (!node) return false;

    const candidates = Array.from(node.querySelectorAll(
      'shopify-accelerated-checkout-cart, shopify-accelerated-checkout, [data-shopify-buttoncontainer], .shopify-cleanslate, iframe, button'
    ));

    return candidates.some((candidate) => {
      const rect = candidate.getBoundingClientRect();
      const style = window.getComputedStyle(candidate);
      return (
        rect.width >= 120
        && rect.height >= 32
        && style.display !== 'none'
        && style.visibility !== 'hidden'
      );
    });
  }

  function waitForAcceleratedCheckoutPaint(node, timeout) {
    window.clearTimeout(acceleratedCheckoutPaintTimer);

    if (!node) {
      releaseAcceleratedCheckoutSpace(0);
      return;
    }

    const startedAt = Date.now();
    const maxWait = Math.max(1200, Number(timeout || 4500));

    const check = () => {
      const live = acceleratedCheckoutNode(document);
      if (!live || live !== node) return;

      if (acceleratedCheckoutIsPainted(live)) {
        live.setAttribute('data-mmx-wallet-painted', 'true');
        releaseAcceleratedCheckoutSpace(120);
        return;
      }

      if (Date.now() - startedAt >= maxWait) {
        live.removeAttribute('data-mmx-wallet-painted');

        // Shopify returned wallet markup but did not paint an eligible button.
        // Collapse the reserved area instead of leaving a permanent blank space.
        if (!acceleratedCheckoutIsPainted(live)) {
          live.setAttribute('data-mmx-wallet-state', 'empty');
        }

        releaseAcceleratedCheckoutSpace(0);
        return;
      }

      acceleratedCheckoutPaintTimer = window.setTimeout(check, 90);
    };

    check();
  }

  function reserveAcceleratedCheckoutSpace(timeout) {
    const node = acceleratedCheckoutNode(document);
    if (!node) return;

    if (acceleratedCheckoutIsPainted(node)) {
      node.setAttribute('data-mmx-wallet-painted', 'true');
    }

    document.documentElement.classList.add('mmx-wallet-space-reserved');
    node.classList.add('is-reserved');

    window.clearTimeout(acceleratedCheckoutReservationTimer);
    acceleratedCheckoutReservationTimer = window.setTimeout(() => {
      releaseAcceleratedCheckoutSpace(0);
    }, Math.max(2500, Number(timeout || 7000)));
  }

  function releaseAcceleratedCheckoutSpace(delay) {
    window.clearTimeout(acceleratedCheckoutReservationTimer);

    const release = () => {
      window.clearTimeout(acceleratedCheckoutPaintTimer);
      acceleratedCheckoutPaintTimer = null;
      document.documentElement.classList.remove('mmx-wallet-space-reserved');
      document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-accelerated-checkout].is-reserved').forEach((node) => {
        node.classList.remove('is-reserved');
      });
    };

    if (Number(delay || 0) > 0) {
      acceleratedCheckoutReservationTimer = window.setTimeout(release, Number(delay));
    } else {
      release();
    }
  }

  function initialiseAcceleratedCheckout(node) {
    if (!node) return;

    try {
      if (window.customElements && typeof window.customElements.upgrade === 'function') {
        window.customElements.upgrade(node);
      }
    } catch (error) {
      console.warn('[MMX Cart] Unable to upgrade accelerated checkout web component.', error);
    }

    const initialise = () => {
      try {
        if (
          window.Shopify
          && window.Shopify.PaymentButton
          && typeof window.Shopify.PaymentButton.init === 'function'
        ) {
          window.Shopify.PaymentButton.init();
        }
      } catch (error) {
        console.warn('[MMX Cart] Unable to initialise Shopify accelerated checkout.', error);
      }
    };

    initialise();
    window.requestAnimationFrame(() => {
      initialise();
      window.setTimeout(initialise, 120);
    });
  }

  async function refreshAcceleratedCheckout() {
    const current = acceleratedCheckoutNode(document);
    if (!current) {
      releaseAcceleratedCheckoutSpace(0);
      return null;
    }

    reserveAcceleratedCheckoutSpace(7000);

    const sectionId = currentCartDrawerSectionId();
    if (!sectionId) {
      releaseAcceleratedCheckoutSpace(0);
      return null;
    }

    const token = ++acceleratedCheckoutRefreshToken;
    current.classList.add('is-refreshing');
    current.setAttribute('aria-busy', 'true');

    try {
      const url = new URL(currentSectionRenderUrl(sectionId));
      url.searchParams.set('mmx_wallet_ts', String(Date.now()));

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'text/html',
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`Accelerated checkout section request failed: ${response.status}`);
      }

      const html = await response.text();
      if (token !== acceleratedCheckoutRefreshToken) return null;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshSource = acceleratedCheckoutNode(doc);
      if (!freshSource) {
        throw new Error('Fresh cart drawer section did not contain an accelerated checkout anchor.');
      }

      const fresh = document.importNode(freshSource, true);
      fresh.classList.remove('is-refreshing');
      fresh.removeAttribute('aria-busy');
      fresh.setAttribute(
        'data-mmx-wallet-state',
        acceleratedCheckoutHasButtons(fresh) ? 'ready' : 'empty'
      );

      const liveCurrent = acceleratedCheckoutNode(document);
      if (!liveCurrent || token !== acceleratedCheckoutRefreshToken) return null;

      liveCurrent.replaceWith(fresh);
      initialiseAcceleratedCheckout(fresh);
      syncPreorderCheckoutGate(cartState);

      // Shopify's custom element may take longer than a fixed delay to paint.
      // Keep the reserved height until a real wallet button has measurable dimensions.
      waitForAcceleratedCheckoutPaint(fresh, 4500);
      return fresh;
    } catch (error) {
      console.warn('[MMX Cart] Accelerated checkout refresh failed.', error);
      const live = acceleratedCheckoutNode(document);
      if (live && token === acceleratedCheckoutRefreshToken) {
        live.classList.remove('is-refreshing');
        live.removeAttribute('aria-busy');
        live.setAttribute(
          'data-mmx-wallet-state',
          acceleratedCheckoutHasButtons(live) ? 'ready' : 'empty'
        );
      }
      syncPreorderCheckoutGate(cartState);
      releaseAcceleratedCheckoutSpace(240);
      return null;
    }
  }

  function scheduleAcceleratedCheckoutRefresh(delay) {
    reserveAcceleratedCheckoutSpace(7000);
    window.clearTimeout(acceleratedCheckoutRefreshTimer);
    acceleratedCheckoutRefreshTimer = window.setTimeout(() => {
      refreshAcceleratedCheckout();
    }, Math.max(0, Number(delay == null ? 220 : delay)));
  }

  async function renderedSectionSaysBnplHidden() {
    const sectionId = currentCartDrawerSectionId();
    if (!sectionId) return null;

    const response = await fetch(currentSectionRenderUrl(sectionId), {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    });

    if (!response.ok) throw new Error(`Cart drawer section render failed: ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const bnplBlock = doc.querySelector('[data-mmx-bnpl-block]');

    if (!bnplBlock) return null;

    return bnplBlock.hasAttribute('hidden') || bnplBlock.classList.contains('hidden') || bnplBlock.getAttribute('data-mmx-bnpl-hidden-by-tag') === 'true';
  }

  function itemBaseUnitPrice(item) {
    if (!item) return 0;

    // Shopify keeps the merchandise price in `price` / `original_price`,
    // while automatic line-item discounts such as PWP are reflected in
    // `final_price` / `discounted_price`. Always show what the customer is
    // actually paying for one unit.
    return Math.max(
      0,
      Number(
        item.final_price != null
          ? item.final_price
          : (
              item.discounted_price != null
                ? item.discounted_price
                : (
                    item.price != null
                      ? item.price
                      : item.original_price || 0
                  )
            )
      )
    );
  }

  function itemCompareAtFallback(item) {
    if (!item) return 0;

    const current = itemBaseUnitPrice(item);
    const merchandisePrice = Math.max(
      0,
      Number(item.original_price || 0),
      Number(item.price || 0)
    );

    // Until product JSON supplies the true compare-at price, use the
    // pre-discount merchandise price as a safe fallback.
    return merchandisePrice > current ? merchandisePrice : 0;
  }

  function priceHtmlWithSavings(currentCents, compareAtCents) {
    const current = Math.max(0, Number(currentCents || 0));
    const compareAt = Math.max(0, Number(compareAtCents || 0));
    const save = Math.max(0, compareAt - current);
    return `<div class="mmx-cart-price-display">
      <div class="mmx-cart-price-display__row">
        <span class="mmx-cart-price-display__current">${formatMoney(current)}</span>
        ${compareAt > current ? `<span class="mmx-cart-price-display__compare">${formatMoney(compareAt)}</span>` : ''}
      </div>
      ${save > 0 ? `<div class="mmx-cart-price-display__save">Save ${formatMoney(save)}</div>` : ''}
    </div>`;
  }

  function addonPriceHtml(currentCents, compareAtCents) {
    const current = Math.max(0, Number(currentCents || 0));
    const compareAt = Math.max(0, Number(compareAtCents || 0));
    return `<div class="mmx-cart-addon-price-display">
      <span class="mmx-cart-addon-price-display__current">${formatMoney(current)}</span>
      ${compareAt > current ? `<span class="mmx-cart-addon-price-display__compare">${formatMoney(compareAt)}</span>` : ''}
    </div>`;
  }

  async function enhanceCompareAtPrices(cart) {
    const nodes = Array.from(
      document.querySelectorAll(
        '.mmx-cart-drawer-shell [data-mmx-cart-price]'
      )
    );
    if (!nodes.length) return;

    await Promise.all(nodes.map(async (node) => {
      const handle = node.getAttribute('data-product-handle');
      const productUrl = node.getAttribute('data-product-url');
      const variantId = Number(
        node.getAttribute('data-variant-id') || 0
      );
      const currentPrice = Number(
        node.getAttribute('data-current-price') || 0
      );
      let compareAt = Number(
        node.getAttribute('data-compare-at-price') || 0
      );

      /*
        Shopify's cart JSON only exposes the pre-discount selling price for
        automatic PWP discounts. For this item that is $139, while the actual
        storefront compare-at price is $179. Always enrich the line from the
        product JSON instead of fetching only when the fallback is empty.
      */
      if ((handle || productUrl) && variantId) {
        const product = await fetchProductData(handle, productUrl);
        const variants = product && Array.isArray(product.variants)
          ? product.variants
          : [];
        const variant = variants.find((candidate) => (
          Number(candidate && candidate.id || 0) === variantId
        ));

        if (variant && variant.compare_at_price != null) {
          compareAt = Math.max(
            compareAt,
            normalizeProductPrice(variant.compare_at_price)
          );
        }
      }

      node.setAttribute(
        'data-compare-at-price',
        String(compareAt)
      );

      node.innerHTML = priceHtmlWithSavings(
        currentPrice,
        compareAt
      );
    }));
  }

  async function enhanceGiftWorthPrices() {
    const nodes = Array.from(
      document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-gift-price]')
    );
    if (!nodes.length) return;

    await Promise.all(nodes.map(async (node) => {
      let worth = Number(node.getAttribute('data-gift-worth') || 0);
      const handle = node.getAttribute('data-product-handle');
      const productUrl = node.getAttribute('data-product-url');
      const variantId = Number(node.getAttribute('data-variant-id') || 0);

      if (!worth && (handle || productUrl) && variantId) {
        const product = await fetchProductData(handle, productUrl);
        const variants = product && Array.isArray(product.variants)
          ? product.variants
          : [];
        const variant = variants.find((candidate) => (
          Number(candidate && candidate.id || 0) === variantId
        ));

        if (variant) {
          worth = Math.max(
            normalizeProductPrice(variant.price),
            normalizeProductPrice(variant.compare_at_price)
          );
        }
      }

      const worthNode = node.querySelector('[data-mmx-gift-worth-text]');
      if (!worthNode || worth <= 0) return;

      node.setAttribute('data-gift-worth', String(worth));
      worthNode.textContent = `Worth ${formatMoney(worth)}`;
      worthNode.hidden = false;
    }));
  }


  function isGiftItem(item) {
    return Boolean(item && item.properties && Object.prototype.hasOwnProperty.call(item.properties, '_GWP'));
  }

  function isFullyDiscountedGiftItem(item) {
    if (!item || isGiftItem(item)) return false;

    const quantity = Math.max(1, Number(item.quantity || 1));
    const originalLinePrice = Math.max(
      0,
      Number(item.original_line_price || 0),
      Number(item.original_price || item.price || 0) * quantity
    );
    const finalLinePrice = Math.max(
      0,
      Number(
        item.final_line_price != null
          ? item.final_line_price
          : Number(item.final_price || 0) * quantity
      )
    );
    const lineDiscount = Math.max(
      0,
      Number(item.line_level_total_discount || 0)
    );
    const allocatedDiscount = (
      Array.isArray(item.line_level_discount_allocations)
        ? item.line_level_discount_allocations
        : []
    ).reduce((total, allocation) => (
      total + Math.max(0, Number(allocation && allocation.amount || 0))
    ), 0);

    return originalLinePrice > 0 && (
      finalLinePrice === 0
      || lineDiscount >= originalLinePrice
      || allocatedDiscount >= originalLinePrice
    );
  }

  function isExcluded(item, cfg) {
    const excluded = Array.isArray(cfg.excludedProductIds) ? cfg.excludedProductIds.map(String) : [];
    return excluded.includes(String(item.product_id));
  }

  function eligibleSpend(cart, cfg) {
    if (!cart || !cfg) return 0;
    if (cfg.tierBasis === 'final_total') return Number(cart.total_price || 0);

    return (cart.items || []).reduce((total, item) => {
      if (isGiftItem(item) || isExcluded(item, cfg)) return total;
      if (cfg.excludeFreeItems && Number(item.final_line_price) === 0 && Number(item.original_line_price) > 0) return total;
      return total + Number(cfg.tierBasis === 'original_subtotal' ? item.original_line_price : item.final_line_price || 0);
    }, 0);
  }

  function checkoutDisplayTotal(cart) {
    if (!cart) return 0;
    return Math.max(0, Number(cart.total_price || 0));
  }

  function tierPosition(index, count) {
    if (!count) return 0;
    return ((index + 1) / count) * 100;
  }

  function progressPercent(spend, tiers, spacing) {
    if (!tiers.length) return 0;
    if (spacing === 'proportional') {
      const finalAmount = Number(tiers[tiers.length - 1].amountCents || 1);
      return Math.max(0, Math.min(100, (spend / finalAmount) * 100));
    }

    let previousAmount = 0;
    let previousPosition = 0;
    for (let index = 0; index < tiers.length; index += 1) {
      const tier = tiers[index];
      const amount = Number(tier.amountCents);
      const position = tierPosition(index, tiers.length);
      if (spend < amount) {
        const range = Math.max(1, amount - previousAmount);
        const segment = Math.max(0, spend - previousAmount) / range;
        return Math.max(0, Math.min(100, previousPosition + ((position - previousPosition) * segment)));
      }
      previousAmount = amount;
      previousPosition = position;
    }
    return 100;
  }

  function replaceMessage(template, values) {
    let output = String(template || '');
    Object.keys(values).forEach((key) => {
      output = output.split(`[${key}]`).join(values[key]);
    });
    return output;
  }

  // V11.8 — cart drawer reward labels use a consistent two-line layout.
  // Theme Editor reward text remains plain text; no <br> is required.
  function tierRewardLabelHtml(value) {
    const text = String(value || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!text) return '';

    const words = text.split(' ');
    if (words.length < 2) return escapeHtml(text);

    let splitIndex = 1;
    let bestDifference = Number.POSITIVE_INFINITY;

    for (let index = 1; index < words.length; index += 1) {
      const firstLine = words.slice(0, index).join(' ');
      const secondLine = words.slice(index).join(' ');
      const difference = Math.abs(firstLine.length - secondLine.length);

      if (difference < bestDifference) {
        bestDifference = difference;
        splitIndex = index;
      }
    }

    const firstLine = words.slice(0, splitIndex).join(' ');
    const secondLine = words.slice(splitIndex).join(' ');

    return `${escapeHtml(firstLine)}<br>${escapeHtml(secondLine)}`;
  }
  function tierIcon(icon, unlocked, customIconUrl) {
    if (customIconUrl) {
      return `<img class="mmx-spend-tiers__custom-icon" src="${escapeHtml(customIconUrl)}" alt="" loading="lazy">`;
    }
    if (unlocked) {
      return '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3.2 8.1 6.5 11.3 12.9 4.9" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }

    const icons = {
      shipping: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M2.5 5.5h9v8h-9zM11.5 8h3l3 3v2.5h-6zM5.5 16a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM14.5 16a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/></svg>',
      discount: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m3 10 7-7h5l2 2v5l-7 7-7-7Z" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/><circle cx="13" cy="7" r="1" fill="currentColor"/></svg>',
      gift: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 8h14v9H3zM2 5h16v3H2zM10 5v12" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/><path d="M10 5C8 5 5 4.8 5 2.9 5 1.4 7.7 1.5 10 5Zm0 0c2 0 5-.2 5-2.1 0-1.5-2.7-1.4-5 2.1Z" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/></svg>',
      star: '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m10 2.2 2.3 4.7 5.2.8-3.8 3.7.9 5.2-4.6-2.4-4.6 2.4.9-5.2-3.8-3.7 5.2-.8L10 2.2Z" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linejoin="round"/></svg>',
      none: '<span class="mmx-spend-tiers__dot" aria-hidden="true"></span>'
    };
    return icons[icon] || icons.star;
  }

  class MMXSpendTiers extends HTMLElement {
    connectedCallback() {
      this.setAttribute('data-mmx-ready', '');
      scheduleRefresh(false, 0);
    }

    disconnectedCallback() {
      window.clearTimeout(this._mmxUnlockFlashTimer);
      this._mmxUnlockFlashTimer = null;
      window.clearTimeout(this._mmxCelebrationTimer);
      this._mmxCelebrationTimer = null;
      (this._mmxGiftPopTimers || []).forEach((timer) => window.clearTimeout(timer));
      this._mmxGiftPopTimers = [];
      this.removeTierConfetti();
    }

    unlockedMessage(tier, cfg) {
      const reward = String(
        (tier && (tier.successText || tier.text))
        || 'Reward'
      ).trim();

      let message;

      // Some tier success labels already contain "unlocked". Avoid output such
      // as "$8 Off unlocked unlocked".
      if (/\bunlocked[.!?]?$/i.test(reward)) {
        message = reward;
      } else {
        message = replaceMessage(
          cfg.unlockedMessage || '[reward] unlocked',
          { reward }
        );
      }

      message = String(message || '').trim();
      if (message && !/[.!?]$/.test(message)) message += '!';

      return message;
    }

    clearUnlockFlash() {
      window.clearTimeout(this._mmxUnlockFlashTimer);
      this._mmxUnlockFlashTimer = null;
      this._mmxUnlockFlashUntil = 0;
      this._mmxUnlockFlashMessage = '';
    }

    startUnlockFlash(tier, cfg) {
      this.clearUnlockFlash();

      this._mmxUnlockFlashMessage = this.unlockedMessage(tier, cfg);
      this._mmxUnlockFlashUntil = Date.now() + 3000;

      this._mmxUnlockFlashTimer = window.setTimeout(() => {
        this._mmxUnlockFlashTimer = null;
        this._mmxUnlockFlashUntil = 0;
        this._mmxUnlockFlashMessage = '';

        if (this.isConnected && this._mmxLastCart && this._mmxLastConfig) {
          this.render(this._mmxLastCart, this._mmxLastConfig);
        }
      }, 3000);
    }

    prefersReducedMotion() {
      return Boolean(
        window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    }

    removeTierCelebration() {
      const tierWrap = this.closest('.mmx-cart-drawer__tiers') || this.parentElement;
      if (tierWrap) {
        tierWrap.querySelectorAll('[data-mmx-tier-celebration]').forEach((node) => node.remove());
      }

      this.classList.remove('is-tier-shine-sweeping', 'is-tier-final-shimmering', 'is-tier-final-celebrating');
      this.querySelectorAll('.mmx-spend-tiers__marker').forEach((marker) => {
        marker.classList.remove('is-newly-unlocked', 'is-tier-glow-ripple', 'is-tier-final-glow');
      });
    }

    celebrationSettings(cfg) {
      const raw = cfg && cfg.celebration ? cfg.celebration : {};
      const allowedStyles = new Set(['none', 'confetti', 'gold_sparkles', 'glow_ripple', 'shine_sweep']);
      const allowedTriggers = new Set(['every_tier', 'gift_and_final', 'final_only']);
      const allowedIntensity = new Set(['subtle', 'normal', 'strong']);
      const allowedDirection = new Set(['right', 'left', 'both']);
      const allowedFinalModes = new Set(['same', 'stronger']);
      const allowedColorStyles = new Set(['brand', 'gold', 'custom']);

      const durationRaw = Number(raw.durationMs || 900);

      return {
        style: allowedStyles.has(raw.style) ? raw.style : 'confetti',
        trigger: allowedTriggers.has(raw.trigger) ? raw.trigger : 'every_tier',
        intensity: allowedIntensity.has(raw.intensity) ? raw.intensity : 'normal',
        durationMs: Math.min(1500, Math.max(500, Number.isFinite(durationRaw) ? durationRaw : 900)),
        direction: allowedDirection.has(raw.direction) ? raw.direction : 'right',
        milestonePulse: raw.milestonePulse !== false,
        goldDustTrail: raw.goldDustTrail === true,
        finalBothSides: raw.finalBothSides !== false,
        finalShimmer: raw.finalShimmer !== false,
        finalMode: allowedFinalModes.has(raw.finalMode) ? raw.finalMode : 'same',
        colorStyle: allowedColorStyles.has(raw.colorStyle) ? raw.colorStyle : 'brand',
        customColors: Array.isArray(raw.customColors)
          ? raw.customColors.map((color) => String(color || '').trim()).filter(Boolean).slice(0, 3)
          : []
      };
    }

    shouldCelebrateTier(tierIndex, cfg, settings) {
      const tiers = cfg && Array.isArray(cfg.tiers) ? cfg.tiers : [];
      const tier = tiers[tierIndex];
      const isFinal = tierIndex === tiers.length - 1;
      if (!tier || !settings) return false;

      if (settings.trigger === 'final_only') return isFinal;
      if (settings.trigger === 'gift_and_final') return Boolean(tier.autoAddGift) || isFinal;
      return true;
    }

    celebrationPalette(settings) {
      if (settings.colorStyle === 'gold') {
        return ['#d4af37', '#f2b134', '#ffd66b', '#b8860b'];
      }

      if (settings.colorStyle === 'custom' && settings.customColors.length) {
        return settings.customColors;
      }

      return ['#0082ca', '#f2b134', '#d71920', '#4db5e8'];
    }

    particleOrigins(direction, count) {
      if (direction === 'left') return Array(count).fill('left');
      if (direction === 'both') {
        return Array.from({ length: count }, (_, index) => (index % 2 === 0 ? 'right' : 'left'));
      }
      return Array(count).fill('right');
    }

    createParticleCelebration(style, settings, isFinal, strongerFinal) {
      const tierWrap = this.closest('.mmx-cart-drawer__tiers') || this.parentElement;
      if (!tierWrap) return;

      const intensityCounts = { subtle: 8, normal: 14, strong: 22 };
      let count = intensityCounts[settings.intensity] || 14;
      if (strongerFinal) count = Math.round(count * 1.45);

      const palette = this.celebrationPalette(settings);
      const origins = this.particleOrigins(settings.direction, count);
      const burst = document.createElement('div');
      burst.className = `mmx-tier-celebration mmx-tier-celebration--${style}${isFinal ? ' is-final' : ''}`;
      burst.setAttribute('data-mmx-tier-celebration', '');
      burst.setAttribute('aria-hidden', 'true');
      burst.style.setProperty('--mmx-tier-celebration-duration', `${settings.durationMs}ms`);

      const travel = settings.intensity === 'subtle' ? 145 : settings.intensity === 'strong' ? 285 : 220;

      for (let index = 0; index < count; index += 1) {
        const origin = origins[index];
        const sign = origin === 'left' ? 1 : -1;
        const spread = 70 + ((index * 37) % travel);
        const vertical = -95 + ((index * 47) % 190);
        const rotate = 140 + ((index * 83) % 420);
        const delay = Math.min(260, index * (settings.intensity === 'strong' ? 11 : 17));
        const originY = 38 + ((index * 13) % 28);

        const node = document.createElement('span');
        node.className = style === 'gold_sparkles'
          ? 'mmx-tier-celebration__sparkle'
          : `mmx-tier-celebration__piece${index % 3 === 0 ? ' mmx-tier-celebration__piece--round' : ''}`;
        node.classList.add(`mmx-tier-celebration__particle--${origin}`);
        node.style.setProperty('--mmx-celebration-color', palette[index % palette.length]);
        node.style.setProperty('--mmx-celebration-x', `${sign * spread}px`);
        node.style.setProperty('--mmx-celebration-y', `${vertical}px`);
        node.style.setProperty('--mmx-celebration-rotate', `${sign * rotate}deg`);
        node.style.setProperty('--mmx-celebration-x-mid', `${sign * Math.round(spread * 0.58)}px`);
        node.style.setProperty('--mmx-celebration-y-mid', `${Math.round(vertical * 0.58)}px`);
        node.style.setProperty('--mmx-celebration-rotate-mid', `${sign * Math.round(rotate * 0.55)}deg`);
        node.style.setProperty('--mmx-celebration-delay', `${delay}ms`);
        node.style.setProperty('--mmx-celebration-origin-y', `${originY}%`);
        burst.appendChild(node);
      }

      tierWrap.appendChild(burst);
    }

    createGoldDustTrail(marker, settings, strongerFinal) {
      if (!marker) return;
      const progressWrap = this.querySelector('.mmx-spend-tiers__progress-wrap');
      if (!progressWrap) return;

      const wrapRect = progressWrap.getBoundingClientRect();
      const markerRect = marker.getBoundingClientRect();
      const targetX = Math.max(0, Math.min(wrapRect.width, (markerRect.left + markerRect.width / 2) - wrapRect.left));
      const count = strongerFinal ? 13 : settings.intensity === 'strong' ? 11 : settings.intensity === 'subtle' ? 6 : 8;
      const trail = document.createElement('span');
      trail.className = 'mmx-tier-gold-dust';
      trail.setAttribute('data-mmx-tier-celebration', '');
      trail.setAttribute('aria-hidden', 'true');
      trail.style.setProperty('--mmx-tier-celebration-duration', `${settings.durationMs}ms`);

      for (let index = 0; index < count; index += 1) {
        const dust = document.createElement('i');
        const jitter = -6 + ((index * 7) % 13);
        const delay = index * 34;
        const scale = 0.6 + ((index % 4) * 0.12);
        dust.style.setProperty('--mmx-dust-x', `${Math.max(14, targetX - (index % 3) * 4)}px`);
        dust.style.setProperty('--mmx-dust-y', `${jitter}px`);
        dust.style.setProperty('--mmx-dust-delay', `${delay}ms`);
        dust.style.setProperty('--mmx-dust-scale', String(scale));
        trail.appendChild(dust);
      }

      progressWrap.appendChild(trail);
    }


    celebrateUnlockedTier(tierIndex, cfg) {
      window.clearTimeout(this._mmxCelebrationTimer);
      this._mmxCelebrationTimer = null;
      this.removeTierCelebration();

      const settings = this.celebrationSettings(cfg);
      const tiers = cfg && Array.isArray(cfg.tiers) ? cfg.tiers : [];
      const tier = tiers[tierIndex];
      const isFinal = Boolean(tier && tierIndex === tiers.length - 1);
      const strongerFinal = isFinal && settings.finalMode === 'stronger';
      const marker = this.querySelectorAll('.mmx-spend-tiers__marker')[tierIndex];

      if (!this.shouldCelebrateTier(tierIndex, cfg, settings)) return;

      this.style.setProperty('--mmx-tier-celebration-duration', `${settings.durationMs}ms`);

      if (settings.milestonePulse && marker) {
        marker.classList.add('is-newly-unlocked');
      }

      if (strongerFinal && settings.style !== 'none') {
        this.classList.add('is-tier-final-celebrating');
        this.querySelectorAll('.mmx-spend-tiers__marker.is-unlocked').forEach((node) => {
          node.classList.add('is-tier-final-glow');
        });
      }

      if (!this.prefersReducedMotion() && settings.style !== 'none') {
        if (settings.style === 'confetti' || settings.style === 'gold_sparkles') {
          const particleSettings = isFinal && settings.finalBothSides
            ? Object.assign({}, settings, { direction: 'both' })
            : settings;
          this.createParticleCelebration(settings.style, particleSettings, isFinal, strongerFinal);
        } else if (settings.style === 'glow_ripple' && marker) {
          marker.classList.add('is-tier-glow-ripple');
        } else if (settings.style === 'shine_sweep') {
          this.classList.add('is-tier-shine-sweeping');
        }

        if (settings.goldDustTrail && marker) {
          this.createGoldDustTrail(marker, settings, strongerFinal);
        }

        if (isFinal && settings.finalShimmer) {
          this.classList.add('is-tier-final-shimmering');
        }

      }

      const cleanupDelay = Math.max(1200, settings.durationMs + 420);
      this._mmxCelebrationTimer = window.setTimeout(() => {
        this._mmxCelebrationTimer = null;
        this.removeTierCelebration();
      }, cleanupDelay);
    }

    render(cart, cfg) {
      if (!cart || !cfg || !cfg.tiers || !cfg.tiers.length) {
        this.innerHTML = '';
        this.hidden = true;
        this.clearUnlockFlash();
        return;
      }

      this._mmxLastCart = cart;
      this._mmxLastConfig = cfg;
      this.hidden = false;

      const tierStyle = cfg.tierStyle || {};
      if (tierStyle.circleSize) this.style.setProperty('--mmx-tier-circle-size', `${Number(tierStyle.circleSize)}px`);
      if (tierStyle.iconSize) this.style.setProperty('--mmx-tier-icon-size', `${Number(tierStyle.iconSize)}px`);
      if (tierStyle.messageFontSize) this.style.setProperty('--mmx-tier-message-font-size', `${Number(tierStyle.messageFontSize)}px`);
      if (tierStyle.unlockedFontSize) this.style.setProperty('--mmx-tier-unlocked-font-size', `${Number(tierStyle.unlockedFontSize)}px`);
      if (tierStyle.rewardFontSize) this.style.setProperty('--mmx-tier-reward-font-size', `${Number(tierStyle.rewardFontSize)}px`);
      if (tierStyle.amountFontSize) this.style.setProperty('--mmx-tier-amount-font-size', `${Number(tierStyle.amountFontSize)}px`);

      const celebration = this.celebrationSettings(cfg);
      this.style.setProperty('--mmx-tier-celebration-duration', `${celebration.durationMs}ms`);

      const tiers = cfg.tiers;
      const spend = eligibleSpend(cart, cfg);
      const nextTier = tiers.find((tier) => spend < Number(tier.amountCents));
      const unlocked = tiers.filter((tier) => spend >= Number(tier.amountCents));
      const unlockedCount = unlocked.length;
      const progress = progressPercent(spend, tiers, cfg.tierSpacing);
      const mode = this.dataset.mode || 'compact';
      const isDrawerTier = Boolean(this.closest('.mmx-cart-drawer-shell'));
      const defaultHeading = this.dataset.heading || '';

      const previousUnlockedCount = Number.isInteger(this._mmxPreviousUnlockedCount)
        ? this._mmxPreviousUnlockedCount
        : null;

      let celebrationTierIndex = -1;

      if (previousUnlockedCount !== null) {
        if (unlockedCount > previousUnlockedCount) {
          // When one action crosses several thresholds, celebrate the highest
          // newly reached reward once. Intermediate tiers keep the short
          // unlock message; the final tier goes straight to the completed state.
          celebrationTierIndex = unlockedCount - 1;
          if (unlockedCount >= tiers.length) {
            this.clearUnlockFlash();
          } else {
            this.startUnlockFlash(unlocked[unlockedCount - 1], cfg);
          }
        } else if (unlockedCount < previousUnlockedCount) {
          // Removing products should immediately return to the next-target
          // message rather than continuing an outdated success message.
          this.clearUnlockFlash();
        }
      }

      this._mmxPreviousUnlockedCount = unlockedCount;

      const flashActive = Boolean(
        this._mmxUnlockFlashMessage
        && Number(this._mmxUnlockFlashUntil || 0) > Date.now()
      );

      const isComplete = !nextTier;
      let mainMessage = '';
      let messageClass = '';

      if (isComplete && isDrawerTier) {
        mainMessage = cfg.completedMessage || 'Congrats! All Rewards Unlocked!';
        messageClass = 'mmx-spend-tiers__message--complete';
      } else if (!isComplete && flashActive) {
        mainMessage = this._mmxUnlockFlashMessage;
        messageClass = 'mmx-spend-tiers__message--unlocked';
      } else if (!isComplete) {
        mainMessage = replaceMessage(
          cfg.nextMessage || 'Spend [amount] more to unlock [reward]',
          {
            amount: formatMoney(Number(nextTier.amountCents) - spend),
            reward: nextTier.text
          }
        );
        messageClass = 'mmx-spend-tiers__message--next';
      }

      const heading = isDrawerTier
        ? ''
        : (isComplete
          ? (cfg.completedMessage || 'Congrats! All Rewards Unlocked!')
          : defaultHeading);
      const headingClass = isComplete
        ? 'mmx-spend-tiers__heading mmx-spend-tiers__heading--complete'
        : 'mmx-spend-tiers__heading';
      const headingHtml = heading
        ? `<div class="${headingClass}" aria-live="${isComplete ? 'polite' : 'off'}">${escapeHtml(heading)}</div>`
        : '';
      const messageHtml = mainMessage
        ? `<div class="mmx-spend-tiers__message ${messageClass}" aria-live="polite">${escapeHtml(mainMessage)}</div>`
        : '';

      if (mode === 'list') {
        const rows = tiers.map((tier) => {
          const isUnlocked = spend >= Number(tier.amountCents);
          return `<div class="mmx-spend-tiers__list-row${isUnlocked ? ' is-unlocked' : ''}">
            <span class="mmx-spend-tiers__list-icon">${tierIcon(tier.icon, isUnlocked, tier.customIconUrl)}</span>
            <span class="mmx-spend-tiers__list-reward">${escapeHtml(tier.text)}</span>
            <span class="mmx-spend-tiers__list-amount">${escapeHtml(formatMoney(tier.amountCents))}</span>
          </div>`;
        }).join('');

        this.innerHTML = `<div class="mmx-spend-tiers__inner">
          ${headingHtml}
          ${messageHtml}
          <div class="mmx-spend-tiers__list">${rows}</div>
        </div>`;
        if (celebrationTierIndex >= 0) {
          window.requestAnimationFrame(() => this.celebrateUnlockedTier(celebrationTierIndex, cfg));
        }
        return;
      }

      const markers = mode === 'full'
        ? `<div class="mmx-spend-tiers__markers">${tiers.map((tier, index) => {
            const isUnlocked = spend >= Number(tier.amountCents);
            const position = cfg.tierSpacing === 'proportional'
              ? (Number(tier.amountCents) / Number(tiers[tiers.length - 1].amountCents)) * 100
              : tierPosition(index, tiers.length);
            return `<div class="mmx-spend-tiers__marker${isUnlocked ? ' is-unlocked' : ''}" style="--mmx-tier-position:${position}%">
              <span class="mmx-spend-tiers__marker-icon">${tierIcon(tier.icon, isUnlocked, tier.customIconUrl)}</span>
              <span class="mmx-spend-tiers__marker-copy">
                <span class="mmx-spend-tiers__marker-reward">${isDrawerTier
                  ? tierRewardLabelHtml(tier.text)
                  : (this.closest('.mmx-product-spend-tiers')
                    ? `<span class="mmx-product-tier-reward--desktop">${escapeHtml(tier.text)}</span><span class="mmx-product-tier-reward--mobile">${tierRewardLabelHtml(tier.text)}</span>`
                    : escapeHtml(tier.text))}</span>
                <span class="mmx-spend-tiers__marker-amount">${escapeHtml(formatMoney(tier.amountCents))}</span>
              </span>
            </div>`;
          }).join('')}</div>`
        : '';

      this.innerHTML = `<div class="mmx-spend-tiers__inner">
        ${headingHtml}
        ${messageHtml}
        <div class="mmx-spend-tiers__progress-wrap">
          <div class="mmx-spend-tiers__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(progress)}">
            <span class="mmx-spend-tiers__fill" style="width:${progress}%"></span>
          </div>
          ${markers}
        </div>
      </div>`;

      if (celebrationTierIndex >= 0) {
        window.requestAnimationFrame(() => this.celebrateUnlockedTier(celebrationTierIndex, cfg));
      }
    }
  }

  if (!window.customElements.get('mmx-spend-tiers')) {
    window.customElements.define('mmx-spend-tiers', MMXSpendTiers);
  }

  let mmxPdpSpendTierPreviewTimer = null;

  function mmxClearPdpSpendTierPreview(element) {
    if (!element || !element.querySelector) return;

    const wrapper = element.closest('.mmx-product-spend-tiers');
    const previewFill = element.querySelector('[data-mmx-pdp-preview-fill]');

    if (previewFill) previewFill.remove();

    element.querySelectorAll('.mmx-spend-tiers__marker.is-mmx-preview-unlocked').forEach((marker) => {
      marker.classList.remove('is-mmx-preview-unlocked');
    });

    const message = element.querySelector('.mmx-spend-tiers__message');
    if (message) {
      if (message.hasAttribute('data-mmx-actual-message')) {
        message.textContent = message.getAttribute('data-mmx-actual-message') || '';
        message.removeAttribute('data-mmx-actual-message');
      }
      message.classList.remove('is-mmx-product-preview');
    }

    if (wrapper) wrapper.classList.remove('is-mmx-product-preview-active');
  }

  function mmxPdpSpendTierPreviewSelection(element) {
    const wrapper = element && element.closest
      ? element.closest('.mmx-product-spend-tiers')
      : null;

    if (!wrapper) return null;
    if (wrapper.getAttribute('data-mmx-preview-enabled') !== 'true') return null;

    const form = wrapper.closest('.product-form');
    if (!form) return null;

    const dataNode = wrapper.querySelector('[data-mmx-pdp-tier-variants]');
    if (!dataNode) return null;

    let productData;
    try {
      productData = JSON.parse(dataNode.textContent || '{}');
    } catch (error) {
      console.warn('[MMX Cart] Unable to read PDP spend-tier product data.', error);
      return null;
    }

    const selector = form.querySelector('select.original-selector[name="id"]');
    const variantId = Number(selector && selector.value || 0);

    if (!variantId) return null;

    const variants = productData && productData.variants
      ? productData.variants
      : {};

    const variant = variants[String(variantId)];
    if (!variant || variant.available !== true) return null;

    const priceCents = Math.max(0, Number(variant.priceCents || 0));
    if (!priceCents) return null;

    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantity = Math.max(
      1,
      Math.floor(Number(quantityInput && quantityInput.value || 1))
    );

    return {
      wrapper,
      form,
      productData,
      variantId,
      variantTitle: selector && selector.selectedOptions && selector.selectedOptions[0]
        ? String(selector.selectedOptions[0].textContent || '').trim()
        : '',
      priceCents,
      quantity
    };
  }

  function mmxProjectedPdpCart(cart, selection) {
    if (!cart || !selection) return cart;

    const productData = selection.productData || {};
    const linePrice = selection.priceCents * selection.quantity;

    const previewItem = {
      id: selection.variantId,
      key: `mmx-pdp-preview-${selection.variantId}`,
      variant_id: selection.variantId,
      product_id: Number(productData.productId || 0),
      title: String(productData.productTitle || ''),
      product_title: String(productData.productTitle || ''),
      variant_title: String(selection.variantTitle || ''),
      vendor: String(productData.vendor || ''),
      product_type: String(productData.productType || ''),
      handle: String(productData.productHandle || ''),
      url: String(productData.productUrl || ''),
      quantity: selection.quantity,
      price: selection.priceCents,
      original_price: selection.priceCents,
      final_price: selection.priceCents,
      discounted_price: selection.priceCents,
      line_price: linePrice,
      original_line_price: linePrice,
      final_line_price: linePrice,
      properties: {},
      discounts: [],
      gift_card: false
    };

    const items = Array.isArray(cart.items)
      ? cart.items.slice()
      : [];

    items.push(previewItem);

    return Object.assign({}, cart, {
      items,
      item_count: Number(cart.item_count || 0) + selection.quantity,
      total_price: Number(cart.total_price || 0) + linePrice,
      original_total_price: Number(cart.original_total_price || cart.total_price || 0) + linePrice
    });
  }

  function mmxPdpTierRewardLabel(tier) {
    return String(tier && tier.text || 'reward')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function mmxPdpTierIsDiscount(tier) {
    const icon = String(tier && tier.icon || '')
      .trim()
      .toLowerCase();

    if (icon === 'discount') return true;

    // Fallback for legacy/misconfigured tiers where the visible
    // reward is clearly a dollar-off discount.
    return /^\$\s*\d+(?:\.\d+)?\s+off\b/i.test(
      mmxPdpTierRewardLabel(tier)
    );
  }

  function mmxPdpProjectedUnlockRewards(
    tiers,
    currentSpend,
    projectedSpend
  ) {
    const newlyReached = tiers.filter((tier) => {
      const amount = Number(tier && tier.amountCents || 0);

      return (
        amount > 0 &&
        currentSpend < amount &&
        projectedSpend >= amount
      );
    });

    if (!newlyReached.length) return [];

    const discountTiers =
      newlyReached.filter(mmxPdpTierIsDiscount);

    const highestDiscount =
      discountTiers.reduce((highest, tier) => {
        if (!highest) return tier;

        return (
          Number(tier.amountCents || 0) >
          Number(highest.amountCents || 0)
        )
          ? tier
          : highest;
      }, null);

    const nonDiscountRewards =
      newlyReached
        .filter((tier) => !mmxPdpTierIsDiscount(tier))
        .map(mmxPdpTierRewardLabel)
        .filter(Boolean);

    const rewards = [];

    // Discount tiers are non-stacking:
    // show only the highest newly reached discount.
    if (highestDiscount) {
      rewards.push(
        mmxPdpTierRewardLabel(highestDiscount)
      );
    }

    // Shipping / gifts / other non-discount rewards can
    // still be communicated alongside the highest discount.
    rewards.push(...nonDiscountRewards);

    return rewards;
  }

  function mmxUpdatePdpSpendTierPreview(element, cart, cfg) {
    if (!element || !cart || !cfg || !Array.isArray(cfg.tiers) || !cfg.tiers.length) {
      mmxClearPdpSpendTierPreview(element);
      return;
    }

    const selection = mmxPdpSpendTierPreviewSelection(element);

    if (!selection) {
      mmxClearPdpSpendTierPreview(element);
      return;
    }

    const track = element.querySelector('.mmx-spend-tiers__track');

    if (!track) {
      mmxClearPdpSpendTierPreview(element);
      return;
    }

    const tiers = cfg.tiers;
    const currentSpend = eligibleSpend(cart, cfg);
    const projectedCart = mmxProjectedPdpCart(cart, selection);
    const projectedSpend = eligibleSpend(projectedCart, cfg);

    if (!(projectedSpend > currentSpend)) {
      mmxClearPdpSpendTierPreview(element);
      return;
    }

    const currentProgress = progressPercent(
      currentSpend,
      tiers,
      cfg.tierSpacing
    );

    const projectedProgress = progressPercent(
      projectedSpend,
      tiers,
      cfg.tierSpacing
    );

    if (!(projectedProgress > currentProgress)) {
      mmxClearPdpSpendTierPreview(element);
      return;
    }

    let previewFill = track.querySelector('[data-mmx-pdp-preview-fill]');

    if (!previewFill) {
      previewFill = document.createElement('span');
      previewFill.className = 'mmx-product-spend-tiers__preview-fill';
      previewFill.setAttribute('data-mmx-pdp-preview-fill', '');
      previewFill.setAttribute('aria-hidden', 'true');
      track.appendChild(previewFill);
    }

    previewFill.style.left = `${currentProgress}%`;
    previewFill.style.width = `${Math.max(0, projectedProgress - currentProgress)}%`;

    selection.wrapper.classList.add('is-mmx-product-preview-active');

    element.querySelectorAll('.mmx-spend-tiers__marker').forEach((marker, index) => {
      const tier = tiers[index];
      const amount = Number(tier && tier.amountCents || 0);

      marker.classList.toggle(
        'is-mmx-preview-unlocked',
        projectedSpend >= amount && currentSpend < amount
      );
    });

    const nextTier = tiers.find(
      (tier) => currentSpend < Number(tier.amountCents || 0)
    );

    const message = element.querySelector('.mmx-spend-tiers__message');

    if (!message) return;

    if (!message.hasAttribute('data-mmx-actual-message')) {
      message.setAttribute(
        'data-mmx-actual-message',
        String(message.textContent || '')
      );
    }

    const lead = selection.quantity > 1
      ? 'Add these items'
      : 'Add this item';

    const projectedUnlockRewards =
      mmxPdpProjectedUnlockRewards(
        tiers,
        currentSpend,
        projectedSpend
      );

    if (projectedUnlockRewards.length) {
      message.textContent =
        `${lead} → Unlock ${projectedUnlockRewards.join(' + ')}`;
    } else if (nextTier) {
      const nextAmount =
        Number(nextTier.amountCents || 0);

      const reward =
        mmxPdpTierRewardLabel(nextTier);

      const remaining =
        Math.max(0, nextAmount - projectedSpend);

      message.textContent =
        `${lead} → only ${formatMoney(remaining)} more to unlock ${reward}`;
    } else {
      message.textContent =
        message.getAttribute('data-mmx-actual-message') || '';
    }

    message.classList.add('is-mmx-product-preview');
  }

  function mmxRefreshPdpSpendTierPreviews() {
    if (!cartState) return;

    const cfg = parseConfig();
    if (!cfg) return;

    document.querySelectorAll(
      '.mmx-product-spend-tiers mmx-spend-tiers'
    ).forEach((element) => {
      mmxUpdatePdpSpendTierPreview(element, cartState, cfg);
    });
  }

  function mmxSchedulePdpSpendTierPreview(delay) {
    window.clearTimeout(mmxPdpSpendTierPreviewTimer);

    mmxPdpSpendTierPreviewTimer = window.setTimeout(() => {
      mmxPdpSpendTierPreviewTimer = null;
      mmxRefreshPdpSpendTierPreviews();
    }, Math.max(0, Number(delay || 0)));
  }

  document.addEventListener('change', (event) => {
    const target = event.target;
    if (!target || !target.closest) return;
    if (!target.closest('.product-form')) return;

    mmxSchedulePdpSpendTierPreview(60);
  }, true);

  document.addEventListener('input', (event) => {
    const target = event.target;
    if (!target || !target.matches) return;

    if (
      target.matches('.product-form input[name="quantity"]')
    ) {
      mmxSchedulePdpSpendTierPreview(30);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const control = event.target && event.target.closest
      ? event.target.closest(
          '.product-form [data-quantity="up"], .product-form [data-quantity="down"], .product-form .qty-btn'
        )
      : null;

    if (!control) return;

    mmxSchedulePdpSpendTierPreview(80);
  }, true);

  async function fetchCart(force) {
    if (!force && cartState) return cartState;
    if (cartPromise) return cartPromise;

    const cartUrl = `${routes.cart}${routes.cart.includes('?') ? '&' : '?'}mmx_ts=${Date.now()}`;
    cartPromise = fetch(cartUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Cache-Control': 'no-cache'
      },
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
        return response.json();
      })
      .then((cart) => {
        cartState = cart;
        return cart;
      })
      .catch((error) => {
        console.error('[MMX Cart] Unable to refresh cart.', error);
        return cartState;
      })
      .finally(() => {
        cartPromise = null;
      });

    return cartPromise;
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        [MMX_INTERNAL_CART_REQUEST_HEADER]: '1'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      let message = `Cart update failed with status ${response.status}`;
      try {
        const error = await response.json();
        message = error.description || error.message || message;
      } catch (parseError) {
        // Keep the HTTP status message.
      }
      throw new Error(message);
    }

    return response.json();
  }

  function setSyncingState(active, options) {
    const opts = options || {};
    const silent = Boolean(active && opts.silent);

    document.documentElement.classList.toggle('mmx-cart-syncing', Boolean(active && !silent));
    document.documentElement.classList.toggle('mmx-cart-syncing-silent', silent);

    window.clearTimeout(syncingSafetyTimer);
    syncingSafetyTimer = null;

    if (active) {
      reserveAcceleratedCheckoutSpace(7000);

      // Final safety net: an unexpected exception or stalled third-party callback
      // must never leave checkout disabled indefinitely. Reconcile with Shopify's
      // authoritative cart before unlocking the drawer.
      syncingSafetyTimer = window.setTimeout(() => {
        syncingSafetyTimer = null;
        console.warn('[MMX Cart] Recovering a cart update that exceeded the safety timeout.');

        Promise.resolve(fetchCart(true))
          .then((cart) => {
            if (cart) renderFastCart(cart, { source: 'sync-safety-recovery' });
          })
          .finally(() => {
            setSyncingState(false);
            clearCartItemWorking();
            stopFastCartFeedback();
          });
      }, MMX_SYNC_SAFETY_TIMEOUT_MS);
    } else if (!cartState || Number(cartState.item_count || 0) <= 0) {
      releaseAcceleratedCheckoutSpace(0);
    }

    document.querySelectorAll('.mmx-cart-drawer-shell .checkout-buttons a, .mmx-cart-drawer-shell .checkout-buttons button').forEach((button) => {
      if (active) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    });
  }

  function isAddonSubmitter(submitter) {
    return Boolean(submitter && submitter.classList && submitter.classList.contains('mmx-cart-addon__button'));
  }

  function returnDrawerToRewardsAfterRecommendationAdd() {
    const prefersReducedMotion = Boolean(
      window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    const drawerContent = () => document.querySelector(
      '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup[aria-hidden="false"] .cart-drawer__content, '
      + '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup.is-open .cart-drawer__content, '
      + '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup.cc-popup--visible .cart-drawer__content, '
      + '.mmx-cart-drawer-shell .cart-drawer__content'
    );

    const forceTop = (behavior) => {
      const content = drawerContent();
      if (!content) return;

      // iOS Safari can interrupt a smooth programmatic scroll when the cart
      // rerenders immediately after an in-drawer add. Set scrollTop as the
      // authoritative position and also call scrollTo for browsers that keep
      // momentum scrolling state internally.
      content.scrollTop = 0;

      try {
        content.scrollTo({
          top: 0,
          left: 0,
          behavior: behavior || 'auto'
        });
      } catch (error) {
        content.scrollTop = 0;
      }
    };

    // Start with a smooth return where supported, then make two short
    // corrective passes after the cart/recommendation DOM has settled. This is
    // intentionally limited to recommendation adds while the drawer is open.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        forceTop(prefersReducedMotion ? 'auto' : 'smooth');

        window.setTimeout(() => forceTop('auto'), 180);
        window.setTimeout(() => forceTop('auto'), 420);
      });
    });
  }

  function setAddonButtonState(button, state) {
    if (!isAddonSubmitter(button)) return;
    if (!button.dataset.mmxOriginalText) button.dataset.mmxOriginalText = button.textContent.trim() || 'Add';

    button.classList.toggle('is-adding', state === 'adding');
    button.classList.toggle('is-added', state === 'added');

    if (state === 'adding') {
      button.innerHTML = '<span class="mmx-cart-addon__button-spinner" aria-hidden="true"></span><span class="visually-hidden">Adding</span>';
      button.setAttribute('aria-label', 'Adding');
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    if (state === 'added') {
      button.textContent = 'Added';
      button.setAttribute('aria-label', 'Added');
      button.removeAttribute('aria-disabled');
      return;
    }

    button.textContent = button.dataset.mmxOriginalText || 'Add';
    button.setAttribute('aria-label', button.dataset.mmxOriginalText || 'Add');
    button.classList.remove('is-adding', 'is-added');
    button.removeAttribute('aria-disabled');
  }

  function giftTierId(item) {
    return item && item.properties && item.properties._MMX_GIFT_TIER != null
      ? String(item.properties._MMX_GIFT_TIER)
      : '';
  }

  function managedGiftProperties(tier) {
    return {
      _GWP: '1',
      _MMX_GIFT_TIER: String(tier.id),
      _MMX_GIFT_BADGE: String(tier.giftBadgeText || 'FREE GIFT'),
      _MMX_GIFT_LOCKED: tier.lockGiftQuantity === false ? '0' : '1',
      _MMX_GIFT_QTY: String(Math.max(1, Number(tier.giftQuantity || 1)))
    };
  }

  function giftPropertiesMatch(item, expected) {
    if (!item || !item.properties) return false;
    return Object.keys(expected).every((key) => String(item.properties[key] == null ? '' : item.properties[key]) === String(expected[key]));
  }

  function hasGiftConfiguration(cfg) {
    return Boolean(
      cfg
      && Array.isArray(cfg.tiers)
      && cfg.tiers.some((tier) => tier && tier.autoAddGift && Number(tier.giftVariantId) > 0)
    );
  }

  function giftDisplayName(tier) {
    return String((tier && (tier.giftTitle || tier.successText || tier.text)) || 'Free gift').replace(/\s+unlocked$/i, '');
  }

  function rewardRowSelector(tierId) {
    const id = String(tierId || 'gift');
    if (window.CSS && typeof window.CSS.escape === 'function') return `[data-mmx-reward-sync-tier="${window.CSS.escape(id)}"]`;
    return `[data-mmx-reward-sync-tier="${id.replace(/"/g, '\\"')}"]`;
  }

  function clearRewardStatusRows() {
    document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-reward-status-row], .mmx-cart-drawer-shell [data-mmx-reward-sync-tier]').forEach((node) => {
      node.remove();
    });
  }

  function rewardStatusRowHtml(options) {
    const opts = options || {};
    const title = opts.title || 'Checking rewards';
    const message = opts.message || 'Checking rewards…';
    const tierId = opts.tierId || 'status';
    const icon = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 8h-2.2A3.2 3.2 0 0 0 12 5.9 3.2 3.2 0 0 0 6.2 8H4v13h16V8ZM8.7 4.8c1 0 1.8.8 2.3 2.2H8.9c-.9 0-1.6-.5-1.6-1.1 0-.6.6-1.1 1.4-1.1Zm6.6 0c.8 0 1.4.5 1.4 1.1 0 .6-.7 1.1-1.6 1.1H13c.5-1.4 1.3-2.2 2.3-2.2ZM6 10h5v9H6v-9Zm7 9v-9h5v9h-5Z" fill="currentColor"/></svg>';
    const statusAttr = opts.generic === false ? '' : ' data-mmx-reward-status-row';
    const specificAttr = opts.generic === false ? ' data-mmx-reward-specific-row' : '';
    return `<div data-mmx-cart-line-wrapper${statusAttr}${specificAttr} data-mmx-reward-sync-tier="${escapeHtml(tierId)}" class="mmx-cart-line-reward-sync">
      <div class="cart-item mmx-cart-item-reward-sync" data-mmx-cart-item>
        <div class="cart-item__column cart-item__image">
          <div class="mmx-cart-reward-sync__image" aria-hidden="true">${icon}</div>
        </div>
        <div class="cart-item__not-image">
          <div class="cart-item__column cart-item__description">
            <div class="lightly-spaced-row">
              <div class="cart-item__title"><span class="inh-col">${escapeHtml(title)}</span></div>
              <div class="small-text" data-mmx-reward-sync-message>${escapeHtml(message)}</div>
            </div>
          </div>
          <div class="cart-item__column cart-item__price"><span class="mmx-cart-item__pending">Checking…</span></div>
          <div class="cart-item__column cart-item__quantity"><span class="mmx-cart-pending-pill"><span class="mmx-spinner" aria-hidden="true"></span>Checking rewards…</span></div>
        </div>
      </div>
    </div>`;
  }

  function showRewardStatusRow(message) {
    const cfg = parseConfig();
    if (!hasGiftConfiguration(cfg)) return;
    const list = document.querySelector('.mmx-cart-drawer-shell .cart-item-list__body');
    if (!list) return;
    const existing = list.querySelector('[data-mmx-reward-status-row]');
    if (existing) {
      const messageNode = existing.querySelector('[data-mmx-reward-sync-message]');
      if (messageNode) messageNode.textContent = message || 'Checking rewards…';
      return;
    }
    list.insertAdjacentHTML('beforeend', rewardStatusRowHtml({
      tierId: 'reward-status',
      title: 'Checking rewards',
      message: message || 'Checking rewards…'
    }));
  }

  function showRewardSyncRow(tier, message) {
    const list = document.querySelector('.mmx-cart-drawer-shell .cart-item-list__body');
    if (!list || !tier) return;

    const tierId = String(tier.id || tier.giftVariantId || 'gift');

    // V1.7.8: once we know which reward is being added, remove the generic
    // "Checking rewards" row so the drawer only shows one reward status.
    list.querySelectorAll('[data-mmx-reward-status-row]:not([data-mmx-reward-specific-row])').forEach((node) => {
      node.remove();
    });

    const existing = list.querySelector(rewardRowSelector(tierId));
    if (existing) {
      const messageNode = existing.querySelector('[data-mmx-reward-sync-message]');
      if (messageNode) messageNode.textContent = message || 'Adding your free gift…';
      return;
    }

    list.insertAdjacentHTML('beforeend', rewardStatusRowHtml({
      tierId,
      title: giftDisplayName(tier),
      message: message || 'Adding your free gift…',
      badge: tier.giftBadgeText || 'FREE GIFT',
      generic: false
    }));
  }

  function markGiftLinesWorking(lines, message) {
    (lines || []).forEach((line) => {
      const wrapper = Array.from(document.querySelectorAll('[data-mmx-cart-line-wrapper]')).find((node) => node.getAttribute('data-mmx-line-key') === String(line.key));
      const item = wrapper && wrapper.querySelector('.cart-item');
      if (item) markCartItemWorking(item, message || 'Updating rewards…');
    });
  }


  function pendingGiftTiers(cart, cfg) {
    if (!cart || !hasGiftConfiguration(cfg)) return [];

    const configuredGiftTiers = cfg.tiers.filter((tier) => (
      tier.autoAddGift
      && Number(tier.giftVariantId) > 0
    ));

    if (!configuredGiftTiers.length) return [];

    const currentSpend = eligibleSpend(cart, cfg);
    const qualifiedTiers = configuredGiftTiers.filter((tier) => currentSpend >= Number(tier.amountCents));
    if (!qualifiedTiers.length) return [];

    let desiredTiers = qualifiedTiers;
    if (cfg.giftRewardBehavior === 'highest') {
      const highestTier = qualifiedTiers.reduce((highest, tier) => (
        !highest || Number(tier.amountCents) > Number(highest.amountCents) ? tier : highest
      ), null);
      desiredTiers = highestTier ? [highestTier] : [];
    }

    return desiredTiers.filter((tier) => {
      const tierId = String(tier.id);
      const desiredVariantId = Number(tier.giftVariantId);
      return !(cart.items || []).some((item) => (
        isGiftItem(item)
        && Number(item.variant_id) === desiredVariantId
        && (giftTierId(item) === tierId || !giftTierId(item))
      ));
    });
  }

  function showPendingRewardRows(cart, cfg, message) {
    const tiers = pendingGiftTiers(cart, cfg);
    if (!tiers.length) {
      // No pending reward to add. Avoid leaving the generic status row stuck.
      document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-reward-status-row]:not([data-mmx-reward-specific-row])').forEach((node) => {
        node.remove();
      });
      return false;
    }

    // MoneyMax currently has one gift tier; if more are added later, showing
    // all pending gift rows is still clearer than a duplicate generic row.
    tiers.forEach((tier) => showRewardSyncRow(tier, message || 'Adding your free gift…'));
    return true;
  }



  
async function syncGifts(cart, cfg, options) {
    if (!cart || !cfg || syncingGift) return cart;

    const configuredGiftTiers = cfg.tiers.filter((tier) => (
      tier.autoAddGift
      && Number(tier.giftVariantId) > 0
    ));
    if (!configuredGiftTiers.length) {
      clearRewardStatusRows();
      return cart;
    }

    const opts = options || {};
    const configuredTierIds = new Set(configuredGiftTiers.map((tier) => String(tier.id)));
    syncingGift = true;
    setSyncingState(true);

    let workingCart = cart;
    let changed = false;

    const queueChange = async (body) => {
      const updated = await postJson(routes.change, body);
      workingCart = updated && updated.items ? updated : await fetchCart(true);
      cartState = workingCart;
      changed = true;
      if (opts.renderDuring !== false) renderFastCart(workingCart, { source: 'gift-sync' });
      return workingCart;
    };

    const queueAdd = async (body) => {
      await postJson(routes.add, body);
      workingCart = await fetchCart(true);
      cartState = workingCart;
      changed = true;
      if (opts.renderDuring !== false) renderFastCart(workingCart, { source: 'gift-sync' });
      return workingCart;
    };

    try {
      const staleManagedLines = (workingCart.items || []).filter((item) => (
        isGiftItem(item)
        && giftTierId(item)
        && !configuredTierIds.has(giftTierId(item))
      ));

      for (const staleLine of staleManagedLines) {
        await queueChange({ id: staleLine.key, quantity: 0 });
      }

      const currentSpend = eligibleSpend(workingCart, cfg);
      const qualifiedTiers = configuredGiftTiers.filter((tier) => currentSpend >= Number(tier.amountCents));
      const desiredTierIds = new Set();

      if (cfg.giftRewardBehavior === 'highest') {
        const highestTier = qualifiedTiers.reduce((highest, tier) => (
          !highest || Number(tier.amountCents) > Number(highest.amountCents) ? tier : highest
        ), null);
        if (highestTier) desiredTierIds.add(String(highestTier.id));
      } else {
        qualifiedTiers.forEach((tier) => desiredTierIds.add(String(tier.id)));
      }

      for (const tier of configuredGiftTiers) {
        const tierId = String(tier.id);
        const desiredVariantId = Number(tier.giftVariantId);
        const desiredQuantity = Math.max(1, Number(tier.giftQuantity || 1));
        const shouldExist = desiredTierIds.has(tierId);
        const qualifies = currentSpend >= Number(tier.amountCents);
        const removeForHighestMode = cfg.giftRewardBehavior === 'highest' && qualifies && !shouldExist;

        const ownedLines = (workingCart.items || []).filter((item) => (
          isGiftItem(item) && giftTierId(item) === tierId
        ));
        const legacyLines = (workingCart.items || []).filter((item) => (
          isGiftItem(item)
          && !giftTierId(item)
          && Number(item.variant_id) === desiredVariantId
        ));
        const relevantLines = [...ownedLines, ...legacyLines];

        if (shouldExist) {
          let primaryGift = relevantLines.find((item) => Number(item.variant_id) === desiredVariantId);

          if (!primaryGift && ownedLines.length) {
            ownedLines.forEach((line) => optimisticRemoveLine(line.key));
            markGiftLinesWorking(ownedLines, 'Updating rewards…');
            for (const oldGift of ownedLines) {
              await queueChange({ id: oldGift.key, quantity: 0 });
            }
          }

          if (!primaryGift) {
            showRewardSyncRow(tier, 'Adding your free gift…');
            await queueAdd({
              items: [{
                id: desiredVariantId,
                quantity: desiredQuantity,
                properties: managedGiftProperties(tier)
              }]
            });
            continue;
          }

          const expectedProperties = managedGiftProperties(tier);
          const targetQuantity = tier.lockGiftQuantity === false
            ? Math.max(1, Number(primaryGift.quantity || 1))
            : desiredQuantity;

          if (Number(primaryGift.quantity) !== targetQuantity || !giftPropertiesMatch(primaryGift, expectedProperties)) {
            await queueChange({
              id: primaryGift.key,
              quantity: targetQuantity,
              properties: expectedProperties
            });
            primaryGift = (workingCart.items || []).find((item) => (
              isGiftItem(item)
              && giftTierId(item) === tierId
              && Number(item.variant_id) === desiredVariantId
            )) || primaryGift;
          }

          const currentPrimaryKey = primaryGift && primaryGift.key;
          const duplicateLines = (workingCart.items || []).filter((item) => (
            item.key !== currentPrimaryKey
            && isGiftItem(item)
            && (
              giftTierId(item) === tierId
              || (!giftTierId(item) && Number(item.variant_id) === desiredVariantId)
            )
          ));

          if (duplicateLines.length) {
            duplicateLines.forEach((line) => optimisticRemoveLine(line.key));
            markGiftLinesWorking(duplicateLines, 'Updating rewards…');
          }
          for (const duplicateGift of duplicateLines) {
            await queueChange({ id: duplicateGift.key, quantity: 0 });
          }
          continue;
        }

        const shouldRemove = removeForHighestMode || (!qualifies && tier.removeBelowThreshold !== false);
        if (shouldRemove && relevantLines.length) {
          relevantLines.forEach((line) => optimisticRemoveLine(line.key));
          markGiftLinesWorking(relevantLines, 'Removing free gift…');
          for (const gift of relevantLines) {
            await queueChange({ id: gift.key, quantity: 0 });
          }
        }
      }
    } catch (error) {
      console.error('[MMX Cart] Free gift sync failed.', error);
      clearRewardStatusRows();
    } finally {
      syncingGift = false;
      setSyncingState(false);
    }

    if (changed) {
      cartState = workingCart;
      renderFastCart(workingCart, { source: 'gift-sync-final' });
    } else {
      clearRewardStatusRows();
    }

    return workingCart;
  }

  function nonPrivateProperties(item) {
    if (!item || !item.properties) return [];
    return Object.keys(item.properties).filter((key) => {
      const value = item.properties[key];
      return key && key.charAt(0) !== '_' && value != null && String(value) !== '';
    }).map((key) => ({ name: key, value: item.properties[key] }));
  }

  function itemImageHtml(item) {
    const src = item && (item.image || (item.featured_image && item.featured_image.url));
    const title = item && (item.product_title || item.title || 'Cart item');
    if (!src) return '<div class="mmx-cart-item__image-placeholder" aria-hidden="true"></div>';
    const sized = String(src).replace(/(\.jpg|\.jpeg|\.png|\.webp)(\?v=|\?|$)/i, '_160x$1$2');
    return `<img src="${escapeHtml(sized)}" alt="${escapeHtml(title)}" loading="eager" width="88" height="88">`;
  }

  function itemVariantHtml(item) {
    if (!item) return '';
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    if (options.length) {
      return options
        .filter((option) => option && option.value && String(option.value) !== 'Default Title')
        .map((option) => `<div class="cart-item__variant"><span class="cart-item__variant-label">${escapeHtml(option.name)}:</span> <span class="cart-item__variant-value">${escapeHtml(option.value)}</span></div>`)
        .join('');
    }
    if (item.variant_title && item.variant_title !== 'Default Title') {
      return `<div class="cart-item__variant"><span class="cart-item__variant-value">${escapeHtml(item.variant_title)}</span></div>`;
    }
    return '';
  }

  function itemPropertiesHtml(item) {
    return nonPrivateProperties(item).map((property) => {
      const value = String(property.value || '');
      const label = property.name === 'Preorder note' ? '' : `${escapeHtml(property.name)}:`;
      const shownValue = value.indexOf('/uploads/') >= 0
        ? `<a href="${escapeHtml(value)}">${escapeHtml(value.split('/').pop())}</a>`
        : escapeHtml(value);
      return `<div class="line-item-property"><span class="line-item-property__label">${label}</span> <span class="line-item-property__value">${shownValue}</span></div>`;
    }).join('');
  }

  function cartLineVariantEditorHtml(item, disabled, lineIndex) {
    const fallback = itemVariantHtml(item);

    // A blank fallback means Shopify's single "Default Title" variant.
    // Do not show a misleading loading message for products with no choices.
    if (!fallback) return '';
    if (disabled || !item || !item.handle || !item.variant_id) return fallback;

    return `<div
      class="mmx-cart-line-variant"
      data-mmx-cart-variant-editor
      data-line-key="${escapeHtml(item.key || '')}"
      data-product-handle="${escapeHtml(item.handle || '')}"
      data-product-url="${escapeHtml(item.url || '')}"
      data-line-index="${Number(lineIndex || 1)}"
      data-current-variant-id="${Number(item.variant_id || 0)}">
      <div class="mmx-cart-line-variant__fallback">${fallback}</div>
    </div>`;
  }

  function activeInventorySnapshot() {
    const cfg = parseConfig();
    return inventorySnapshot || (cfg && cfg.inventory) || null;
  }

  function inventoryLineForItem(item) {
    const snapshot = activeInventorySnapshot();
    const lines = snapshot && Array.isArray(snapshot.lines)
      ? snapshot.lines
      : [];

    const byKey = lines.find((line) => (
      String(line && line.key || '') === String(item && item.key || '')
    ));

    if (byKey) return byKey;

    return lines.find((line) => (
      Number(line && line.variantId || 0) === Number(item && item.variant_id || 0)
    )) || null;
  }


  function inventoryLimitForItem(item) {
    if (!item || isGiftItem(item)) {
      return { enforced: false, max: MMX_MAX_CART_LINE_QUANTITY, available: null };
    }

    const line = inventoryLineForItem(item);
    const tracked = Boolean(line && line.tracked);
    const policy = String(line && line.policy || 'deny').toLowerCase();

    // Shopify merchants can intentionally allow overselling with the
    // "continue selling" policy. Only enforce a hard limit for tracked
    // inventory whose policy is deny.
    if (!tracked || policy !== 'deny') {
      return { enforced: false, max: MMX_MAX_CART_LINE_QUANTITY, available: null };
    }

    const available = Math.max(0, Math.floor(Number(line.quantity || 0)));
    const currentQuantity = Math.max(0, Math.floor(Number(item.quantity || 0)));
    const cartItems = Array.isArray(cartState && cartState.items) ? cartState.items : [];
    const otherVariantQuantity = cartItems.reduce((total, cartItem) => {
      if (!cartItem || isGiftItem(cartItem)) return total;
      if (String(cartItem.key || '') === String(item.key || '')) return total;
      if (Number(cartItem.variant_id || 0) !== Number(item.variant_id || 0)) return total;
      return total + Math.max(0, Math.floor(Number(cartItem.quantity || 0)));
    }, 0);

    // Inventory is shared by every cart line using the same variant, including
    // separate lines created by engraving or other line-item properties.
    const remainingForThisLine = Math.max(0, available - otherVariantQuantity);

    // Never force an already accepted cart line below its current quantity if
    // inventory changes between requests. The customer can still decrease or
    // remove it, but cannot increase it further.
    const max = Math.min(
      MMX_MAX_CART_LINE_QUANTITY,
      Math.max(currentQuantity, remainingForThisLine)
    );

    return { enforced: true, max, available, otherVariantQuantity };
  }

  function inventoryLimitMessage(maxQuantity, changedToMaximum) {
    const max = Math.max(0, Math.floor(Number(maxQuantity || 0)));
    const noun = max === 1 ? 'item is' : 'items are';
    return changedToMaximum
      ? `Only ${max} ${noun} available. Quantity has been set to ${max}.`
      : `Only ${max} ${noun} available. Your quantity was not changed.`;
  }

  function syncQuantityControlState(wrapper, item) {
    if (!wrapper || !item || isGiftItem(item)) return;

    const input = wrapper.querySelector('.cart-item__quantity-input:not(.mmx-gift-quantity-input)');
    const increase = wrapper.querySelector('[data-mmx-cart-action="increase"], .quantity-up');
    if (!input || !increase) return;

    const displayedQuantity = sanitizeCartQuantity(input.value, item.quantity, { allowZero: true });
    const limit = inventoryLimitForItem(item);
    const maxQuantity = limit.enforced ? limit.max : MMX_MAX_CART_LINE_QUANTITY;
    const canIncrease = displayedQuantity < maxQuantity;

    input.setAttribute('max', String(maxQuantity));
    input.setAttribute('data-mmx-inventory-max', String(maxQuantity));

    increase.classList.toggle('unusable', !canIncrease);
    if (canIncrease) {
      increase.removeAttribute('aria-disabled');
      increase.removeAttribute('tabindex');
    } else {
      increase.setAttribute('aria-disabled', 'true');
      increase.setAttribute('tabindex', '-1');
    }

    if (increase.tagName === 'A') {
      const nextQuantity = canIncrease ? displayedQuantity + 1 : displayedQuantity;
      increase.href = `${root}cart/change?id=${encodeURIComponent(item.key)}&quantity=${nextQuantity}`;
    }
  }

  function inventoryNoticeData(item) {
    if (!item || isGiftItem(item)) return null;

    const snapshot = activeInventorySnapshot();
    if (!snapshot) return null;

    const showNotice = String(snapshot.showNotice || 'low').toLowerCase();
    const showCount = String(snapshot.showCount || 'low').toLowerCase();
    const threshold = Math.max(1, Number(snapshot.threshold || 10));
    const line = inventoryLineForItem(item);

    if (showNotice === 'never' || !line || !line.tracked || line.available === false) {
      return null;
    }

    const quantity = Number(line.quantity || 0);
    const policy = String(line.policy || 'deny').toLowerCase();
    const low = quantity > 0 && quantity <= threshold;

    if (showNotice === 'low' && !low) return null;

    // A zero or negative quantity with continue-selling enabled belongs to the
    // theme's backorder messaging rather than the low-stock notice.
    if (quantity <= 0 && policy === 'continue') return null;
    if (quantity <= 0) return null;

    const includeCount = showCount === 'always' || (showCount === 'low' && low);
    let text;

    if (includeCount) {
      text = low
        ? `Only ${quantity} left in stock`
        : `${quantity} in stock`;
    } else {
      text = low ? 'Low stock' : 'In stock';
    }

    return {
      text,
      low,
      quantity
    };
  }

  function inventoryNoticeInnerHtml(item) {
    const notice = inventoryNoticeData(item);
    if (!notice) return '';

    return `<span class="mmx-cart-inventory-notice__dot" aria-hidden="true"></span><span>${escapeHtml(notice.text)}</span>`;
  }

  function renderInventoryNotices(cart) {
    const currentCart = cart || cartState;
    if (!currentCart || !Array.isArray(currentCart.items)) return;

    document.querySelectorAll(
      '.mmx-cart-drawer-shell [data-mmx-cart-line-wrapper]'
    ).forEach((wrapper) => {
      const lineKey = wrapper.getAttribute('data-mmx-line-key');
      const item = cartItemByKey(lineKey, currentCart);
      if (!item) return;

      // Quantity limits remain active even when the merchant has hidden the
      // low-stock notice in theme settings.
      syncQuantityControlState(wrapper, item);

      const noticeNode = wrapper.querySelector('[data-mmx-inventory-notice]');
      const variantRow = wrapper.querySelector('[data-mmx-cart-variant-row]');
      if (!noticeNode || !variantRow) return;

      const notice = inventoryNoticeData(item);
      const hasBaseContent = variantRow.getAttribute('data-mmx-has-base-content') === 'true';

      if (notice) {
        noticeNode.innerHTML = `<span class="mmx-cart-inventory-notice__dot" aria-hidden="true"></span><span>${escapeHtml(notice.text)}</span>`;
        noticeNode.classList.toggle('is-low', notice.low);
        noticeNode.hidden = false;
        variantRow.hidden = false;
      } else {
        noticeNode.textContent = '';
        noticeNode.classList.remove('is-low');
        noticeNode.hidden = true;
        variantRow.hidden = !hasBaseContent;
      }
    });
  }

  async function refreshInventorySnapshot(cart) {
    const cfg = parseConfig();
    const inventory = cfg && cfg.inventory;
    const sectionId = inventory && inventory.sectionId;

    if (!sectionId) return activeInventorySnapshot();

    const requestId = ++inventoryRefreshRequest;

    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html'
        },
        cache: 'no-store'
      });

      if (!response.ok) return activeInventorySnapshot();

      const html = await response.text();
      const documentNode = new DOMParser().parseFromString(html, 'text/html');
      const configNode = documentNode.querySelector('script#MMXSpendTierConfig');
      if (!configNode) return activeInventorySnapshot();

      const freshConfig = JSON.parse(configNode.textContent.trim());
      if (
        requestId !== inventoryRefreshRequest
        || !freshConfig.inventory
      ) {
        return activeInventorySnapshot();
      }

      inventorySnapshot = freshConfig.inventory;
      renderInventoryNotices(cartState || cart);
      return inventorySnapshot;
    } catch (error) {
      console.warn('[MMX Cart] Unable to refresh inventory notice data.', error);
      return activeInventorySnapshot();
    }
  }

  function scheduleInventoryRefresh(cart, delay) {
    const cfg = parseConfig();
    const inventory = cfg && cfg.inventory;

    if (!inventory) return;

    window.clearTimeout(inventoryRefreshTimer);
    inventoryRefreshTimer = window.setTimeout(() => {
      inventoryRefreshTimer = null;
      refreshInventorySnapshot(cart || cartState);
    }, Math.max(80, Number(delay || 180)));
  }


  function cartItemByKey(lineKey, cart) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    return items.find((item) => String(item.key || '') === String(lineKey || '')) || null;
  }

  function sellingPlanIdForItem(item) {
    const allocation = item && item.selling_plan_allocation ? item.selling_plan_allocation : null;
    const id = allocation && allocation.selling_plan ? allocation.selling_plan.id : item && item.selling_plan;
    return Number(id || 0);
  }

  function variantOptionValues(variant, fallbackTitle) {
    if (variant && Array.isArray(variant.options)) {
      return variant.options
        .map((value) => String(value || '').trim())
        .filter(Boolean);
    }

    return String(fallbackTitle || (variant && variant.title) || '')
      .split('/')
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function targetVariantIsNoEngraving(variant, fallbackTitle) {
    const cfg = parseConfig();
    const configuredValue = String(
      cfg
      && cfg.recommendationCustomisation
      && cfg.recommendationCustomisation.variantValue
      || 'Engraving'
    ).trim();

    const noEngravingValue = `No ${configuredValue}`.toLowerCase();

    return variantOptionValues(variant, fallbackTitle).some((value) => {
      const normalised = String(value || '').trim().toLowerCase();
      return normalised === noEngravingValue
        || normalised === 'no engraving'
        || normalised === 'without engraving';
    });
  }

  function isEngravingPropertyName(name) {
    return String(name || '').toLowerCase().includes('engrav');
  }

  function targetVariantRequiresEngraving(variant, fallbackTitle) {
    const cfg = parseConfig();
    const configuredValue = String(
      cfg
      && cfg.recommendationCustomisation
      && cfg.recommendationCustomisation.variantValue
      || 'Engraving'
    ).trim().toLowerCase();

    return variantOptionValues(variant, fallbackTitle).some((value) => (
      String(value || '').trim().toLowerCase() === configuredValue
    ));
  }

  function cartItemEngravingProperty(item) {
    const properties = item && item.properties && typeof item.properties === 'object'
      ? item.properties
      : {};

    const key = Object.keys(properties).find((name) => (
      isEngravingPropertyName(name)
      && String(properties[name] == null ? '' : properties[name]).trim()
    ));

    return key
      ? {
          name: key,
          value: String(properties[key] == null ? '' : properties[key]).trim()
        }
      : null;
  }

  function engravingFieldConfigFallback(item) {
    const existing = cartItemEngravingProperty(item);

    return {
      propertyName: existing && existing.name ? existing.name : 'Engraving Text',
      englishLimit: 10,
      chineseLimit: 8
    };
  }

  function positiveEngravingLimit(value) {
    const limit = Number(value || 0);
    return Number.isFinite(limit) && limit > 0 && limit <= 200
      ? Math.floor(limit)
      : 0;
  }

  function firstEngravingLimit(source, names) {
    if (!source) return 0;

    for (const name of names) {
      const attributeValue = source.getAttribute && source.getAttribute(name);
      const datasetName = String(name || '')
        .replace(/^data-/, '')
        .replace(/-([a-z])/g, (match, letter) => letter.toUpperCase());
      const datasetValue = source.dataset && source.dataset[datasetName];
      const limit = positiveEngravingLimit(attributeValue || datasetValue);

      if (limit) return limit;
    }

    return 0;
  }

  function engravingLimitsFromText(text) {
    const copy = String(text || '').replace(/\s+/g, ' ').trim();
    if (!copy || !/engrav|english|chinese/i.test(copy)) {
      return { englishLimit: 0, chineseLimit: 0 };
    }

    const englishPatterns = [
      /(?:max(?:imum)?\s*)?(\d{1,3})\s*(?:english|latin|roman)(?:\s*(?:letters?|characters?))?/i,
      /(?:english|latin|roman)(?:\s*(?:letters?|characters?))?\s*[:=-]?\s*(\d{1,3})/i
    ];
    const chinesePatterns = [
      /(?:max(?:imum)?\s*)?(\d{1,3})\s*(?:chinese|中文|华文)(?:\s*(?:letters?|characters?|字))?/i,
      /(?:chinese|中文|华文)(?:\s*(?:letters?|characters?|字))?\s*[:=-]?\s*(\d{1,3})/i
    ];

    const read = (patterns) => {
      for (const pattern of patterns) {
        const match = copy.match(pattern);
        const limit = positiveEngravingLimit(match && match[1]);
        if (limit) return limit;
      }
      return 0;
    };

    return {
      englishLimit: read(englishPatterns),
      chineseLimit: read(chinesePatterns)
    };
  }

  function isChineseEngravingCharacter(character) {
    return /[\u3400-\u4DBF\u4E00-\u9FFF\uF900-\uFAFF]/u.test(
      String(character || '')
    );
  }

  function engravingTextWithinLimits(value, englishLimit, chineseLimit) {
    const englishMax = positiveEngravingLimit(englishLimit) || 10;
    const chineseMax = positiveEngravingLimit(chineseLimit) || 8;
    const chineseWeight = englishMax / chineseMax;
    let used = 0;
    let output = '';

    Array.from(String(value || '')).some((character) => {
      const nextWeight = isChineseEngravingCharacter(character)
        ? chineseWeight
        : 1;

      if (used + nextWeight > englishMax + 0.0001) {
        return true;
      }

      used += nextWeight;
      output += character;
      return false;
    });

    return output;
  }

  function applyEngravingInputLimits(input, config) {
    if (!input) return '';

    const englishLimit = positiveEngravingLimit(
      config && config.englishLimit || input.dataset.englishLimit
    ) || 10;
    const chineseLimit = positiveEngravingLimit(
      config && config.chineseLimit || input.dataset.chineseLimit
    ) || 8;

    input.dataset.englishLimit = String(englishLimit);
    input.dataset.chineseLimit = String(chineseLimit);
    input.maxLength = englishLimit;
    input.title = `Maximum ${englishLimit} English characters or ${chineseLimit} Chinese characters`;

    const limitedValue = engravingTextWithinLimits(
      input.value,
      englishLimit,
      chineseLimit
    );

    if (limitedValue !== input.value) {
      input.value = limitedValue;

      const editor = input.closest('[data-mmx-cart-line-engraving]');
      const status = editor && editor.querySelector(
        '[data-mmx-cart-line-engraving-status]'
      );

      if (status) {
        status.textContent = `Maximum ${englishLimit} English characters or ${chineseLimit} Chinese characters.`;
      }
    }

    return limitedValue;
  }

  async function resolveEngravingFieldConfig(item) {
    const fallback = engravingFieldConfigFallback(item);
    const productUrl = String(item && item.url || '');
    const handle = String(item && item.handle || '');
    const cacheKey = handle || productUrl;

    if (!cacheKey || !productUrl) return fallback;
    if (engravingFieldConfigCache.has(cacheKey)) {
      return engravingFieldConfigCache.get(cacheKey);
    }

    const promise = (async () => {
      try {
        const url = new URL(productUrl, window.location.origin);
        url.search = '';
        url.hash = '';

        const response = await fetch(url.href, {
          credentials: 'same-origin',
          headers: {
            Accept: 'text/html'
          }
        });

        if (!response.ok) return fallback;

        const html = await response.text();
        const documentNode = new DOMParser().parseFromString(html, 'text/html');
        const candidates = Array.from(documentNode.querySelectorAll(
          'input[name^="properties["], textarea[name^="properties["]'
        ));

        const field = candidates.find((candidate) => {
          const name = String(candidate.getAttribute('name') || '');
          const type = String(candidate.getAttribute('type') || 'text').toLowerCase();

          return /engrav/i.test(name)
            && type !== 'hidden'
            && type !== 'checkbox'
            && type !== 'radio';
        });

        if (!field) return fallback;

        const rawName = String(field.getAttribute('name') || '');
        const propertyMatch = rawName.match(/^properties\[(.+)\]$/i);

        const explicitEnglishLimit = firstEngravingLimit(field, [
          'data-english-limit',
          'data-max-english',
          'data-english-max',
          'data-max-english-characters'
        ]);
        const explicitChineseLimit = firstEngravingLimit(field, [
          'data-chinese-limit',
          'data-max-chinese',
          'data-chinese-max',
          'data-max-chinese-characters'
        ]);

        const nearbyContainer = field.closest(
          '[data-engraving], .engraving, .engraving-field, .product-form__input, fieldset'
        ) || field.parentElement;
        const nearbyLimits = engravingLimitsFromText(
          nearbyContainer && nearbyContainer.textContent
        );

        const engravingScriptText = Array.from(documentNode.scripts)
          .map((script) => String(script.textContent || ''))
          .filter((scriptText) => /engrav/i.test(scriptText))
          .join(' ');
        const scriptLimits = engravingLimitsFromText(engravingScriptText);

        const genericMaxLength = positiveEngravingLimit(
          field.getAttribute('maxlength')
        );
        const usableGenericMaxLength = genericMaxLength > 0 && genericMaxLength <= 14
          ? genericMaxLength
          : 0;

        return {
          propertyName: propertyMatch && propertyMatch[1]
            ? propertyMatch[1]
            : fallback.propertyName,
          englishLimit: explicitEnglishLimit
            || nearbyLimits.englishLimit
            || scriptLimits.englishLimit
            || usableGenericMaxLength
            || fallback.englishLimit,
          chineseLimit: explicitChineseLimit
            || nearbyLimits.chineseLimit
            || scriptLimits.chineseLimit
            || fallback.chineseLimit
        };
      } catch (error) {
        console.warn('[MMX Cart] Unable to read product engraving field settings.', error);
        return fallback;
      }
    })();

    engravingFieldConfigCache.set(cacheKey, promise);
    return promise;
  }

  function removePendingCartLineEngravingEditor(itemElement) {
    if (!itemElement) return;

    itemElement.querySelectorAll('[data-mmx-cart-line-engraving]').forEach((editor) => {
      editor.remove();
    });
  }

  function updatePendingEngravingButton(editor) {
    if (!editor) return;

    const input = editor.querySelector('[data-mmx-cart-line-engraving-input]');
    const button = editor.querySelector('[data-mmx-cart-line-engraving-update]');
    if (!input || !button) return;

    const valid = Boolean(String(input.value || '').trim());
    button.disabled = !valid;
    button.setAttribute('aria-disabled', valid ? 'false' : 'true');
  }

  function showPendingCartLineEngravingEditor(options) {
    const opts = options || {};
    const itemElement = opts.itemElement;
    const variantEditor = opts.variantEditor;
    const cartItem = opts.cartItem;
    if (!itemElement || !variantEditor || !cartItem) return;

    const row = variantEditor.closest('.small-text')
      || variantEditor.parentElement
      || itemElement;

    let pendingEditor = itemElement.querySelector('[data-mmx-cart-line-engraving]');

    if (!pendingEditor) {
      pendingEditor = document.createElement('div');
      pendingEditor.className = 'mmx-cart-line-engraving';
      pendingEditor.setAttribute('data-mmx-cart-line-engraving', '');
      pendingEditor.innerHTML = `
        <label class="visually-hidden">Engraving text</label>
        <input
          type="text"
          class="mmx-cart-line-engraving__input"
          data-mmx-cart-line-engraving-input
          placeholder="Engraving text"
          autocomplete="off"
          maxlength="10"
        >
        <button
          type="button"
          class="mmx-cart-line-engraving__button"
          data-mmx-cart-line-engraving-update
          disabled
          aria-disabled="true"
        >UPDATE</button>
        <span
          class="visually-hidden"
          data-mmx-cart-line-engraving-status
          aria-live="polite"
          aria-atomic="true"
        ></span>
      `;
      row.appendChild(pendingEditor);
    }

    pendingEditor.setAttribute('data-line-key', String(opts.lineKey || ''));
    pendingEditor.setAttribute('data-line-index', String(Number(opts.lineIndex || 0)));
    pendingEditor.setAttribute('data-variant-id', String(Number(opts.variantId || 0)));
    pendingEditor.setAttribute('data-variant-title', String(opts.variantTitle || ''));
    pendingEditor.setAttribute('data-variant-price-cents', String(Number(opts.priceCents || 0)));
    pendingEditor.setAttribute('data-variant-compare-at-cents', String(Number(opts.compareAtCents || 0)));

    const input = pendingEditor.querySelector('[data-mmx-cart-line-engraving-input]');
    if (input && !input.dataset.propertyName) {
      const fallback = engravingFieldConfigFallback(cartItem);
      input.dataset.propertyName = fallback.propertyName;
      applyEngravingInputLimits(input, fallback);
    }

    updatePendingEngravingButton(pendingEditor);

    if (input) {
      window.requestAnimationFrame(() => {
        input.focus({ preventScroll: true });
      });
    }

    resolveEngravingFieldConfig(cartItem).then((fieldConfig) => {
      if (!pendingEditor.isConnected || !input) return;

      input.dataset.propertyName = fieldConfig.propertyName;
      applyEngravingInputLimits(input, fieldConfig);
      updatePendingEngravingButton(pendingEditor);
    });
  }

  function cartLinePropertiesForAdd(item, options) {
    const opts = options || {};
    const props = item && item.properties && typeof item.properties === 'object'
      ? Object.assign({}, item.properties)
      : {};

    Object.keys(props).forEach((key) => {
      if (props[key] == null || props[key] === '') {
        delete props[key];
        return;
      }

      if (opts.removeEngravingProperties && isEngravingPropertyName(key)) {
        delete props[key];
      }
    });

    return props;
  }

  function linePropertiesSignature(item) {
    const props = item && item.properties && typeof item.properties === 'object' ? item.properties : {};
    return Object.keys(props)
      .filter((key) => props[key] != null && props[key] !== '')
      .sort()
      .map((key) => `${key}:${String(props[key])}`)
      .join('|');
  }

  function findCartLineIndex(cart, matcher, preferredIndex) {
    const items = Array.isArray(cart && cart.items) ? cart.items : [];
    const matches = [];
    items.forEach((item, index) => {
      if (matcher(item, index)) matches.push(index);
    });

    if (!matches.length) return -1;
    if (Number.isFinite(preferredIndex) && matches.includes(preferredIndex)) return preferredIndex;

    return matches[0];
  }

  async function removeOldVariantLineAfterAdd(originalItem, originalLineIndex, afterAddCart) {
    const oldKey = String(originalItem && originalItem.key || '');
    const oldVariantId = Number(originalItem && originalItem.variant_id || 0);
    const oldProductId = Number(originalItem && originalItem.product_id || 0);
    const oldSignature = linePropertiesSignature(originalItem);
    const preferredIndex = Math.max(0, Number(originalLineIndex || 1) - 1);

    let cart = afterAddCart && Array.isArray(afterAddCart.items) ? afterAddCart : await fetchCart(true);

    let foundIndex = findCartLineIndex(cart, (item) => String(item.key || '') === oldKey, preferredIndex);

    if (foundIndex < 0) {
      foundIndex = findCartLineIndex(cart, (item) => (
        Number(item.variant_id || 0) === oldVariantId
        && (!oldProductId || Number(item.product_id || 0) === oldProductId)
        && linePropertiesSignature(item) === oldSignature
      ), preferredIndex);
    }

    if (foundIndex >= 0) {
      return postJson(routes.change, { line: foundIndex + 1, quantity: 0 });
    }

    // Last fallback: try the original key directly. This should rarely be needed.
    if (oldKey) return postJson(routes.change, { id: oldKey, quantity: 0 });

    throw new Error('Unable to find the original cart line to replace.');
  }

  function availableProductVariants(product) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    return variants.filter((variant) => variant && variant.available !== false);
  }

  function normaliseVariantDisplayCasing(value) {
    return String(value == null ? '' : value)
      // Shopify/product feeds sometimes return measurement units in full caps
      // even when the storefront variant is written as 16cm / 4.2g.
      .replace(/(\d(?:[\d.,]*))\s*(CM|MM|KG|G)\b/g, (match, amount, unit) => (
        `${amount}${String(unit).toLowerCase()}`
      ))
      .trim();
  }

  function meaningfulVariantTitle(variant) {
    if (!variant) return '';

    const title = normaliseVariantDisplayCasing(
      variant.title || ''
    );

    return title && title !== 'Default Title' && title !== 'Default'
      ? title
      : '';
  }

  // ----------------------------------------------------------
  // Desktop-only in-drawer variant dropdown
  // ----------------------------------------------------------
  // Desktop Chrome can promote a long native <select> into a browser-owned
  // modal with an opaque blue backdrop. That backdrop cannot be styled
  // reliably from theme code. On desktop only, replace the visible control
  // with an anchored MMX dropdown while retaining the original <select> as
  // the authoritative form value. Mobile/touch devices keep their native
  // selector behaviour.

  const desktopVariantMedia = window.matchMedia(
    '(min-width: 750px) and (hover: hover) and (pointer: fine)'
  );

  let activeDesktopVariantSelect = null;
  let activeDesktopVariantTrigger = null;
  let desktopVariantMenu = null;
  let mobileVariantBackdrop = null;
  let desktopVariantTriggerSequence = 0;

  function desktopVariantModeEnabled() {
    // Use the controlled MMX selector on every device. The presentation is an
    // anchored dropdown on desktop and a bottom sheet on mobile.
    return true;
  }

  function desktopVariantViewportEnabled() {
    return Boolean(desktopVariantMedia && desktopVariantMedia.matches);
  }

  function mobileVariantSheetEnabled() {
    return !desktopVariantViewportEnabled();
  }

  function setVariantDrawerInteractionState(isInteractive) {
    const enabled = Boolean(isInteractive);

    document.documentElement.classList.toggle(
      'mmx-variant-drawer-interactive',
      enabled
    );

    if (!enabled) {
      closeDesktopVariantMenu({ focus: false });
    }
  }

  function variantDrawerPopupFor(element) {
    return element
      && element.closest
      && element.closest(
        '.mmx-cart-drawer-shell .cart-drawer-modal.cc-popup'
      );
  }

  function variantDrawerIsActuallyInteractive(element) {
    if (!element || !element.isConnected) return false;

    const popup = variantDrawerPopupFor(element);
    if (!popup) return false;

    // The explicit root state is the source of truth. It is enabled by the
    // drawer-open event and continuously reconciled by the existing drawer
    // visibility observer. Avoid rejecting valid clicks based on transient
    // animation geometry or stale theme ARIA attributes.
    return document.documentElement.classList.contains(
      'mmx-variant-drawer-interactive'
    );
  }

  function selectedVariantTitle(select) {
    if (!select || !select.options || select.selectedIndex < 0) return '';
    const option = select.options[select.selectedIndex];

    return normaliseVariantDisplayCasing(
      option.getAttribute('data-variant-title')
      || option.textContent
      || ''
    );
  }

  function ensureDesktopVariantMenu() {
    let menu = document.querySelector('[data-mmx-desktop-variant-menu]');
    let backdrop = document.querySelector('[data-mmx-mobile-variant-backdrop]');

    if (!backdrop) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.className = 'mmx-mobile-variant-backdrop';
      backdrop.setAttribute('data-mmx-mobile-variant-backdrop', '');
      backdrop.setAttribute('aria-label', 'Close variant selector');
      backdrop.hidden = true;
      document.body.appendChild(backdrop);
    }

    if (!menu) {
      menu = document.createElement('div');
      menu.id = 'MMXDesktopVariantMenu';
      menu.className = 'mmx-desktop-variant-menu';
      menu.setAttribute('data-mmx-desktop-variant-menu', '');
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-label', 'Choose variant');
      menu.hidden = true;
      document.body.appendChild(menu);
    }

    desktopVariantMenu = menu;
    mobileVariantBackdrop = backdrop;
    return menu;
  }

  function closeDesktopVariantMenu(options) {
    const opts = options || {};
    const trigger = activeDesktopVariantTrigger;
    const select = activeDesktopVariantSelect;
    const shell = trigger && trigger.closest('.mmx-variant-select-shell');

    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (shell) shell.classList.remove('is-open');

    if (desktopVariantMenu) {
      desktopVariantMenu.hidden = true;
      desktopVariantMenu.innerHTML = '';
      desktopVariantMenu.classList.remove('is-mobile-sheet');
      desktopVariantMenu.style.removeProperty('left');
      desktopVariantMenu.style.removeProperty('right');
      desktopVariantMenu.style.removeProperty('top');
      desktopVariantMenu.style.removeProperty('bottom');
      desktopVariantMenu.style.removeProperty('width');
      desktopVariantMenu.style.removeProperty('max-height');
      desktopVariantMenu.style.removeProperty('visibility');
      desktopVariantMenu.removeAttribute('data-open-direction');
    }

    if (mobileVariantBackdrop) {
      mobileVariantBackdrop.hidden = true;
    }

    document.documentElement.classList.remove('mmx-mobile-variant-open');
    activeDesktopVariantSelect = null;
    activeDesktopVariantTrigger = null;

    if (opts.focus && trigger && trigger.isConnected && !trigger.disabled) {
      trigger.focus({ preventScroll: true });
    }

    if (select && select.isConnected) syncVariantSelectDisplay(select);
  }

  function desktopVariantBoundary(trigger) {
    const drawerModal = trigger
      && trigger.closest('.mmx-cart-drawer-shell')
      && trigger.closest('.mmx-cart-drawer-shell').querySelector('.cc-popup-modal');

    if (drawerModal) return drawerModal.getBoundingClientRect();

    return {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight
    };
  }

  function positionDesktopVariantMenu() {
    const menu = desktopVariantMenu;
    const trigger = activeDesktopVariantTrigger;

    if (!menu || !trigger || menu.hidden || !trigger.isConnected) return;

    if (mobileVariantSheetEnabled()) {
      menu.classList.add('is-mobile-sheet');
      menu.style.visibility = 'visible';
      menu.style.left = '0px';
      menu.style.right = '0px';
      menu.style.top = 'auto';
      menu.style.bottom = '0px';
      menu.style.width = '100%';
      menu.style.maxHeight = (
        window.CSS
        && typeof window.CSS.supports === 'function'
        && window.CSS.supports('height', '55dvh')
      ) ? '55dvh' : '55vh';
      menu.setAttribute('data-open-direction', 'sheet');
      return;
    }

    menu.classList.remove('is-mobile-sheet');
    const triggerRect = trigger.getBoundingClientRect();
    const boundary = desktopVariantBoundary(trigger);
    const edge = 8;
    const gap = 4;
    const minimumHeight = 90;
    const maximumHeight = 310;

    const width = Math.max(
      180,
      Math.min(
        triggerRect.width,
        Math.max(180, boundary.width - (edge * 2))
      )
    );

    const left = Math.min(
      Math.max(triggerRect.left, boundary.left + edge),
      boundary.right - edge - width
    );

    const spaceBelow = Math.max(
      0,
      boundary.bottom - triggerRect.bottom - edge - gap
    );
    const spaceAbove = Math.max(
      0,
      triggerRect.top - boundary.top - edge - gap
    );

    const wantedHeight = Math.min(
      maximumHeight,
      Math.max(minimumHeight, menu.scrollHeight)
    );

    const openUpward = spaceBelow < Math.min(wantedHeight, 170)
      && spaceAbove > spaceBelow;

    const availableHeight = Math.max(
      minimumHeight,
      openUpward ? spaceAbove : spaceBelow
    );

    const maxHeight = Math.min(maximumHeight, availableHeight);

    menu.style.visibility = 'hidden';
    menu.style.width = `${Math.round(width)}px`;
    menu.style.left = `${Math.round(left)}px`;
    menu.style.maxHeight = `${Math.round(maxHeight)}px`;
    menu.setAttribute('data-open-direction', openUpward ? 'up' : 'down');

    const renderedHeight = Math.min(menu.scrollHeight, maxHeight);
    const top = openUpward
      ? Math.max(boundary.top + edge, triggerRect.top - gap - renderedHeight)
      : Math.min(
          boundary.bottom - edge - renderedHeight,
          triggerRect.bottom + gap
        );

    menu.style.top = `${Math.round(top)}px`;
    menu.style.visibility = 'visible';
  }

  function desktopVariantMenuHtml(select) {
    const optionsHtml = Array.from(select.options || []).map((option, index) => {
      if (!option || option.disabled || !String(option.value || '').trim()) {
        return '';
      }

      const selected = index === select.selectedIndex;
      const fullLabel = normaliseVariantDisplayCasing(
        String(option.textContent || '').trim()
      );

      return `<button
        type="button"
        class="mmx-desktop-variant-menu__option${selected ? ' is-selected' : ''}"
        role="option"
        tabindex="-1"
        aria-selected="${selected ? 'true' : 'false'}"
        data-mmx-desktop-variant-option
        data-option-index="${index}">
        <span class="mmx-desktop-variant-menu__label">${escapeHtml(fullLabel)}</span>
        <span class="mmx-desktop-variant-menu__check" aria-hidden="true">✓</span>
      </button>`;
    }).join('');

    if (!mobileVariantSheetEnabled()) return optionsHtml;

    return `<div class="mmx-mobile-variant-sheet__header">
      <strong class="mmx-mobile-variant-sheet__title">Change variant</strong>
      <button
        type="button"
        class="mmx-mobile-variant-sheet__close"
        data-mmx-mobile-variant-close
        aria-label="Close variant selector">×</button>
    </div>
    <div class="mmx-mobile-variant-sheet__options">${optionsHtml}</div>`;
  }

  function openDesktopVariantMenu(select, focusPreference) {
    if (!desktopVariantModeEnabled() || !select || select.disabled) return;

    enhanceDesktopVariantSelect(select);

    const shell = select.closest('.mmx-variant-select-shell');
    const trigger = shell
      && shell.querySelector('[data-mmx-desktop-variant-trigger]');

    if (!shell || !trigger) return;

    if (!variantDrawerIsActuallyInteractive(trigger)) {
      return;
    }

    if (activeDesktopVariantSelect === select) {
      closeDesktopVariantMenu({ focus: false });
      return;
    }

    closeDesktopVariantMenu({ focus: false });

    const menu = ensureDesktopVariantMenu();
    const drawerShell = trigger.closest('.mmx-cart-drawer-shell');
    const drawerStyles = drawerShell
      ? window.getComputedStyle(drawerShell)
      : null;

    if (drawerStyles) {
      menu.style.setProperty(
        '--mmx-variant-bg',
        drawerStyles.getPropertyValue('--mmx-drawer-bg').trim() || '#ffffff'
      );
      menu.style.setProperty(
        '--mmx-variant-text',
        drawerStyles.getPropertyValue('--mmx-drawer-text').trim() || '#171717'
      );
      menu.style.setProperty(
        '--mmx-variant-border',
        drawerStyles.getPropertyValue('--mmx-drawer-border').trim() || '#e7e7e7'
      );
      menu.style.setProperty(
        '--mmx-variant-accent',
        drawerStyles.getPropertyValue('--mmx-drawer-accent').trim() || '#0082ca'
      );
    }

    menu.innerHTML = desktopVariantMenuHtml(select);
    menu.hidden = false;

    if (mobileVariantSheetEnabled()) {
      document.documentElement.classList.add('mmx-mobile-variant-open');
      if (mobileVariantBackdrop) mobileVariantBackdrop.hidden = false;
    }

    activeDesktopVariantSelect = select;
    activeDesktopVariantTrigger = trigger;

    shell.classList.add('is-open');
    trigger.setAttribute('aria-expanded', 'true');

    positionDesktopVariantMenu();

    window.requestAnimationFrame(() => {
      if (!desktopVariantMenu || desktopVariantMenu.hidden) return;

      const options = Array.from(
        desktopVariantMenu.querySelectorAll(
          '[data-mmx-desktop-variant-option]'
        )
      );

      if (!options.length) return;

      let target = desktopVariantMenu.querySelector(
        '[data-mmx-desktop-variant-option][aria-selected="true"]'
      );

      if (focusPreference === 'first') target = options[0];
      if (focusPreference === 'last') target = options[options.length - 1];

      (target || options[0]).focus({ preventScroll: true });
    });
  }

  function setDesktopVariantLegacyVisibility(select, hidden) {
    if (!select) return;

    const shell = select.closest('.mmx-variant-select-shell');
    if (!shell) return;

    const trigger = shell.querySelector(
      '[data-mmx-desktop-variant-trigger]'
    );

    const legacyChildren = Array.from(shell.children).filter((child) => (
      child !== trigger
    ));

    if (hidden) {
      if (select.getAttribute('data-mmx-desktop-native-hidden') !== 'true') {
        select.setAttribute('data-mmx-desktop-native-hidden', 'true');
      }
      if (select.getAttribute('tabindex') !== '-1') {
        select.setAttribute('tabindex', '-1');
      }
      if (select.getAttribute('aria-hidden') !== 'true') {
        select.setAttribute('aria-hidden', 'true');
      }

      // Hide every pre-existing child in the selector shell, not only the
      // native <select>. Symmetry can generate its own replacement button
      // beside the select after page load; that was the second visible field.
      legacyChildren.forEach((child) => {
        if (child.getAttribute('data-mmx-desktop-legacy-hidden') !== 'true') {
          child.setAttribute('data-mmx-desktop-legacy-hidden', 'true');
        }
        if (!child.hidden) child.hidden = true;
        if (child.style.getPropertyValue('display') !== 'none') {
          child.style.setProperty('display', 'none', 'important');
        }
        if (child.style.getPropertyValue('visibility') !== 'hidden') {
          child.style.setProperty('visibility', 'hidden', 'important');
        }
        if (child.style.getPropertyValue('pointer-events') !== 'none') {
          child.style.setProperty('pointer-events', 'none', 'important');
        }
      });

      return;
    }

    select.removeAttribute('data-mmx-desktop-native-hidden');
    select.removeAttribute('tabindex');
    select.removeAttribute('aria-hidden');

    shell.querySelectorAll(
      '[data-mmx-desktop-legacy-hidden="true"]'
    ).forEach((child) => {
      child.removeAttribute('data-mmx-desktop-legacy-hidden');
      child.hidden = false;
      child.style.removeProperty('display');
      child.style.removeProperty('visibility');
      child.style.removeProperty('pointer-events');
    });
  }

  function enhanceDesktopVariantSelect(select) {
    if (!select) return null;

    const shell = select.closest('.mmx-variant-select-shell');
    if (!shell) return null;

    if (!desktopVariantModeEnabled()) {
      teardownDesktopVariantSelect(select);
      return null;
    }

    shell.classList.add('mmx-desktop-variant-enhanced');
    setDesktopVariantLegacyVisibility(select, true);

    let trigger = shell.querySelector(
      '[data-mmx-desktop-variant-trigger]'
    );

    if (!trigger) {
      trigger = document.createElement('button');
      trigger.type = 'button';
      trigger.id = `MMXDesktopVariantTrigger-${++desktopVariantTriggerSequence}`;
      trigger.className = 'mmx-desktop-variant-trigger';
      trigger.setAttribute('data-mmx-desktop-variant-trigger', '');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', 'MMXDesktopVariantMenu');
      trigger.innerHTML = `
        <span
          class="mmx-desktop-variant-trigger__label"
          data-mmx-desktop-variant-label
        ></span>
        <span
          class="mmx-desktop-variant-trigger__chevron"
          aria-hidden="true"
        ></span>
      `;

      trigger.addEventListener('pointerdown', (event) => {
        if (!variantDrawerIsActuallyInteractive(trigger)) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }

        event.stopPropagation();
      });

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (!variantDrawerIsActuallyInteractive(trigger)) {
          return;
        }

        openDesktopVariantMenu(select, 'selected');
      });

      select.insertAdjacentElement('afterend', trigger);
    }

    // The first visibility pass happens before the trigger exists. Run it
    // again now so the new MMX trigger is preserved and every legacy/theme
    // control in the same shell is suppressed.
    setDesktopVariantLegacyVisibility(select, true);
    syncVariantSelectDisplay(select);
    return trigger;
  }

  function teardownDesktopVariantSelect(select) {
    if (!select) return;

    const shell = select.closest('.mmx-variant-select-shell');
    if (!shell) return;

    if (activeDesktopVariantSelect === select) {
      closeDesktopVariantMenu({ focus: false });
    }

    shell.classList.remove(
      'mmx-desktop-variant-enhanced',
      'is-open'
    );

    const trigger = shell.querySelector(
      '[data-mmx-desktop-variant-trigger]'
    );

    if (trigger) trigger.remove();

    setDesktopVariantLegacyVisibility(select, false);
  }

  function syncDesktopVariantSelects(scope) {
    const rootNode = scope || document;

    rootNode.querySelectorAll(
      '.mmx-cart-drawer-shell .mmx-variant-select-shell > select'
    ).forEach((select) => {
      if (desktopVariantModeEnabled()) {
        enhanceDesktopVariantSelect(select);
      } else {
        teardownDesktopVariantSelect(select);
      }
    });
  }

  // Native selects use the option text for both the collapsed value and the
  // expanded menu on mobile. Keep prices in the option text for the expanded
  // picker, then overlay a clean title-only value while the selector is closed.
  function syncVariantSelectDisplay(select) {
    if (!select) return;

    const shell = select.closest('.mmx-variant-select-shell');
    const display = shell
      && shell.querySelector('[data-mmx-variant-select-display]');
    const triggerLabel = shell
      && shell.querySelector('[data-mmx-desktop-variant-label]');
    const option = select.options && select.selectedIndex >= 0
      ? select.options[select.selectedIndex]
      : null;

    if (!shell || !option) return;

    if (
      desktopVariantModeEnabled()
      && shell.classList.contains('mmx-desktop-variant-enhanced')
    ) {
      setDesktopVariantLegacyVisibility(select, true);
    }

    const title = normaliseVariantDisplayCasing(
      option.getAttribute('data-variant-title')
      || option.textContent
      || ''
    );

    if (display) display.textContent = title;
    if (triggerLabel) triggerLabel.textContent = title;

    const trigger = shell.querySelector(
      '[data-mmx-desktop-variant-trigger]'
    );

    if (trigger) {
      trigger.disabled = Boolean(select.disabled);
      trigger.setAttribute(
        'aria-disabled',
        select.disabled ? 'true' : 'false'
      );
      trigger.setAttribute(
        'aria-label',
        title ? `Choose variant. Current selection: ${title}` : 'Choose variant'
      );
    }
  }

  function cartLineVariantOptionsHtml(product, currentVariantId) {
    const variants = availableProductVariants(product);
    const currentAvailable = variants.some((variant) => (
      Number(variant.id || 0) === Number(currentVariantId || 0)
    ));

    const placeholder = currentAvailable
      ? ''
      : '<option value="" selected disabled>Choose an available option</option>';

    return placeholder + variants.map((variant) => {
      const id = Number(variant.id || 0);
      const current = id === Number(currentVariantId || 0);
      const selected = current ? ' selected' : '';
      const title = normaliseVariantDisplayCasing(
        variant.title && variant.title !== 'Default Title'
          ? variant.title
          : 'Default'
      );
      const priceCents = Number(variant.price || 0);
      const compareCents = Number(variant.compare_at_price || 0);

      const optionLabel = priceCents > 0
        ? `${title} — ${formatMoney(priceCents)}`
        : title;

      return `<option value="${id}" data-variant-title="${escapeHtml(title)}" data-variant-price-cents="${priceCents}" data-variant-compare-at-cents="${compareCents}"${selected}>${escapeHtml(optionLabel)}</option>`;
    }).join('');
  }

  async function hydrateCartVariantEditors(cart) {
    const editors = Array.from(document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-cart-variant-editor]'));
    if (!editors.length) return;

    await Promise.all(editors.map(async (editor) => {
      if (editor.getAttribute('data-mmx-loaded') === 'true') return;

      const lineKey = editor.getAttribute('data-line-key');
      const item = cartItemByKey(lineKey, cartState || cart);
      if (!item || isGiftItem(item)) return;

      const handle = editor.getAttribute('data-product-handle');
      const productUrl = editor.getAttribute('data-product-url');
      const currentVariantId = Number(editor.getAttribute('data-current-variant-id') || item.variant_id || 0);
      const product = await fetchProductData(handle, productUrl);
      const availableVariants = availableProductVariants(product);
      const hasEngravingVariant = availableVariants.some((variant) => (
        targetVariantRequiresEngraving(variant)
      ));
      const hasNoEngravingVariant = availableVariants.some((variant) => (
        targetVariantIsNoEngraving(variant)
      ));
      const customisationProduct = productRequiresRecommendationCustomisation(product)
        || (hasEngravingVariant && hasNoEngravingVariant);

      editor.setAttribute(
        'data-mmx-customisation-product',
        customisationProduct ? 'true' : 'false'
      );

      const currentAvailable = availableVariants.some((variant) => (
        Number(variant.id || 0) === currentVariantId
      ));

      if (!availableVariants.length) {
        editor.innerHTML = '<div class="mmx-cart-line-variant__fallback">No available options</div>';
        editor.setAttribute('data-mmx-loaded', 'unavailable');
        return;
      }

      // When only one purchasable variant remains, keep its details visible
      // as a read-only field. This is important for products whose option
      // combination carries meaningful specifications even though there is
      // currently nothing else available to switch to.
      if (availableVariants.length === 1 && currentAvailable) {
        const onlyTitle = meaningfulVariantTitle(availableVariants[0]);

        if (onlyTitle) {
          editor.innerHTML = `<div
            class="mmx-cart-line-variant__static"
            aria-label="Selected variant"><span
              class="mmx-single-variant__value">${escapeHtml(onlyTitle)}</span></div>`;
          editor.setAttribute('data-mmx-loaded', 'static');
        } else {
          editor.remove();
        }

        return;
      }

      editor.innerHTML = `<label class="visually-hidden">Choose variant</label><span class="mmx-variant-select-shell mmx-cart-line-variant__shell"><select class="mmx-cart-line-variant__select" data-mmx-cart-line-variant-select aria-label="Choose variant">${cartLineVariantOptionsHtml(product, currentVariantId)}</select><span class="mmx-variant-select-display" data-mmx-variant-select-display aria-hidden="true"></span></span>`;
      const variantSelect = editor.querySelector('.mmx-cart-line-variant__select');
      syncVariantSelectDisplay(variantSelect);
      enhanceDesktopVariantSelect(variantSelect);
      editor.setAttribute('data-mmx-loaded', 'true');
    }));
  }

  async function applyCartVariantChange(body, options) {
    const opts = options || {};
    const cfg = parseConfig();
    const lineKey = body && body.lineKey;
    const lineIndex = Number(body && body.lineIndex || 0);
    const item = cartItemByKey(lineKey, cartState);

    if (!item) {
      throw new Error('This cart item changed before it could be updated. Please try again.');
    }

    const nextVariantId = Number(body.variantId || 0);
    if (!nextVariantId || nextVariantId === Number(item.variant_id || 0)) return cartState;

    const originalItem = Object.assign({}, item, {
      properties: item.properties && typeof item.properties === 'object' ? Object.assign({}, item.properties) : {}
    });

    const addBody = {
      id: nextVariantId,
      quantity: sanitizeCartQuantity(item.quantity, 1, { allowZero: false })
    };

    const selectedVariantTitle = String(body.variantTitle || '');

    // The selected option title was generated from the product JSON when the
    // selector was hydrated, so there is no need to wait for another product
    // request before deciding whether engraving properties should be removed.
    const removeEngravingProperties = targetVariantIsNoEngraving(
      null,
      selectedVariantTitle
    );

    const props = cartLinePropertiesForAdd(item, {
      removeEngravingProperties
    });

    const engravingText = String(body.engravingText || '').trim();
    if (engravingText) {
      Object.keys(props).forEach((key) => {
        if (isEngravingPropertyName(key)) delete props[key];
      });

      const engravingPropertyName = String(
        body.engravingPropertyName || 'Engraving Text'
      ).trim() || 'Engraving Text';

      props[engravingPropertyName] = engravingText;
    }

    if (Object.keys(props).length) addBody.properties = props;

    const sellingPlanId = sellingPlanIdForItem(item);
    if (sellingPlanId) addBody.selling_plan = sellingPlanId;

    const canUseSingleRequest = Boolean(
      removeEngravingProperties
      && !sellingPlanId
      && Object.keys(props).length === 0
    );

    setSyncingState(true, { silent: canUseSingleRequest });

    if (canUseSingleRequest) {
      const quantity = sanitizeCartQuantity(item.quantity, 1, { allowZero: false });
      const existingTargetLine = (cartState && Array.isArray(cartState.items)
        ? cartState.items
        : []
      ).find((cartItem) => (
        String(cartItem.key || '') !== String(originalItem.key || '')
        && Number(cartItem.variant_id || 0) === nextVariantId
        && !sellingPlanIdForItem(cartItem)
        && Object.keys(cartLinePropertiesForAdd(cartItem)).length === 0
      ));

      const updates = {};
      updates[String(originalItem.key || '')] = 0;

      if (existingTargetLine) {
        updates[String(existingTargetLine.key)] = (
          sanitizeCartQuantity(existingTargetLine.quantity, 1, { allowZero: false })
          + quantity
        );
      } else {
        updates[String(nextVariantId)] = quantity;
      }

      const updatedCart = await postJson(routes.update, { updates });
      cartState = updatedCart;

      renderFastCart(updatedCart, { source: 'variant-change-single-request' });
      setSyncingState(false);

      let finalCart = updatedCart;

      if (cfg) {
        finalCart = await syncGifts(updatedCart, cfg, { renderDuring: false }) || updatedCart;

        if (finalCart !== updatedCart) {
          renderFastCart(finalCart, { source: 'variant-change-single-request-gifts' });
        }
      }

      return finalCart;
    }

    // General variant replacement fallback for variants that need properties
    // or a selling plan:
    // 1. Add the selected new variant.
    // 2. Remove the original line directly by its existing key.
    await postJson(routes.add, addBody);

    let cartAfterRemove;

    try {
      cartAfterRemove = await postJson(routes.change, {
        id: String(originalItem.key || ''),
        quantity: 0
      });
    } catch (directRemovalError) {
      console.warn(
        '[MMX Cart] Direct variant replacement fallback used.',
        directRemovalError
      );

      const afterAddCart = await fetchCart(true);
      cartAfterRemove = await removeOldVariantLineAfterAdd(
        originalItem,
        lineIndex,
        afterAddCart
      );
    }

    cartState = cartAfterRemove && cartAfterRemove.items
      ? cartAfterRemove
      : await fetchCart(true);

    renderFastCart(cartState, { source: 'variant-change-main' });

    let finalCart = cartState;
    if (cfg) {
      showPendingRewardRows(finalCart, cfg, 'Updating rewards…');
      finalCart = await syncGifts(finalCart, cfg, { renderDuring: false }) || finalCart;

      if (finalCart !== cartState) {
        renderFastCart(finalCart, { source: 'variant-change-gifts' });
      }
    }

    setSyncingState(false);
    return finalCart;
  }

  function itemDiscountsHtml(item) {
    const discounts = Array.isArray(item.discounts) ? item.discounts : [];
    if (!discounts.length) return '';
    return `<div class="cart-item__item-discounts"><ul class="cart-discount-list">${discounts.map((discount) => `<li class="cart-discount cart-discount--inline"><span class="cart-discount__label"><span class="cart-discount__title">${escapeHtml(discount.title || 'Discount')}</span></span><span class="cart-discount__amount theme-money">-${formatMoney(discount.amount || 0)}</span></li>`).join('')}</ul></div>`;
  }

  function renderCartItem(item, lineIndex) {
    const isGift = isGiftItem(item);
    const isDiscountGift = isFullyDiscountedGiftItem(item);
    const displayAsGift = isGift || isDiscountGift;
    const hasLineDiscount = !displayAsGift && (
      Number(item.final_price || 0) < Math.max(
        Number(item.original_price || 0),
        Number(item.price || 0)
      )
    );
    const lockedGift = isGift && item.properties && item.properties._MMX_GIFT_LOCKED !== '0';
    const title = item.product_title || item.title || 'Cart item';
    const url = item.url || '#';
    const finalPrice = Number(item.final_price != null ? item.final_price : item.price || 0);
    const basePrice = displayAsGift ? finalPrice : itemBaseUnitPrice(item);
    const compareAtFallback = displayAsGift ? 0 : itemCompareAtFallback(item);
    const displayedQty = sanitizeCartQuantity(item.quantity, 1, { allowZero: false });
    const quantityLimit = inventoryLimitForItem(item);
    const maxQuantity = quantityLimit.enforced ? quantityLimit.max : MMX_MAX_CART_LINE_QUANTITY;
    const canIncrease = displayedQty < maxQuantity;
    const giftWorth = Math.max(
      0,
      Number(item.original_price || 0),
      Number(item.price || 0),
      displayedQty > 0
        ? Math.round(Number(item.original_line_price || 0) / displayedQty)
        : 0,
      Number(itemCompareAtFallback(item) || 0)
    );
    const priceHtml = displayAsGift
      ? `<div
          class="mmx-cart-price mmx-cart-price--gift"
          data-mmx-gift-price
          data-product-handle="${escapeHtml(item.handle || '')}"
          data-product-url="${escapeHtml(item.url || '')}"
          data-variant-id="${Number(item.variant_id || 0)}"
          data-gift-worth="${giftWorth}"
        >
          <span class="mmx-gift-price-free">FREE</span>
          <span class="mmx-gift-price-worth" data-mmx-gift-worth-text ${giftWorth > 0 ? '' : 'hidden'}>${giftWorth > 0 ? `Worth ${escapeHtml(formatMoney(giftWorth))}` : ''}</span>
        </div>`
      : `<div class="mmx-cart-price" data-mmx-cart-price data-product-handle="${escapeHtml(item.handle || '')}" data-product-url="${escapeHtml(item.url || '')}" data-variant-id="${Number(item.variant_id || 0)}" data-current-price="${basePrice}" data-compare-at-price="${compareAtFallback}">${priceHtmlWithSavings(basePrice, compareAtFallback)}</div>`;

    const minusIcon = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    const plusIcon = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3v10M3 8h10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    const controlsHtml = lockedGift
      ? `<input class="cart-item__quantity-input mmx-gift-quantity-input" type="hidden" name="updates[]" data-line="${lineIndex}" value="${displayedQty}">`
      : `<div class="quantity buttoned-input" data-mmx-line-key="${escapeHtml(item.key)}">
          <a href="${root}cart/change?id=${encodeURIComponent(item.key)}&quantity=${Math.max(0, displayedQty - 1)}" class="notabutton quantity-down" data-mmx-cart-action="decrease" data-mmx-line-key="${escapeHtml(item.key)}" aria-label="Decrease quantity">${minusIcon}</a>
          <input class="cart-item__quantity-input" type="text" inputmode="numeric" pattern="[0-9]*" maxlength="${String(Math.max(1, maxQuantity)).length}" size="2" name="updates[]" data-line="${lineIndex}" data-mmx-line-key="${escapeHtml(item.key)}" data-initial-value="${displayedQty}" data-mmx-inventory-max="${maxQuantity}" value="${displayedQty}" min="0" max="${maxQuantity}" aria-label="Quantity">
          <a href="${root}cart/change?id=${encodeURIComponent(item.key)}&quantity=${canIncrease ? displayedQty + 1 : displayedQty}" class="notabutton quantity-up${canIncrease ? '' : ' unusable'}" data-mmx-cart-action="increase" data-mmx-line-key="${escapeHtml(item.key)}" aria-label="Increase quantity"${canIncrease ? '' : ' aria-disabled="true" tabindex="-1"'}>${plusIcon}</a>
        </div>
        <a class="cart-item__remove small-text underline" href="${root}cart/change?id=${encodeURIComponent(item.key)}&quantity=0" data-mmx-cart-action="remove" data-mmx-line-key="${escapeHtml(item.key)}">Remove</a>`;

    const variantEditorHtml = cartLineVariantEditorHtml(item, isGift, lineIndex);
    const propertiesHtml = itemPropertiesHtml(item);
    const inventoryHtml = inventoryNoticeInnerHtml(item);
    const hasVariantBaseContent = Boolean(variantEditorHtml || propertiesHtml);
    const shouldRenderVariantRow = !isGift;

    const variantRowHtml = shouldRenderVariantRow
      ? `<div
          class="cart-item__column cart-item__variant-row"
          data-mmx-cart-variant-row
          data-mmx-has-base-content="${hasVariantBaseContent ? 'true' : 'false'}"
          ${!hasVariantBaseContent && !inventoryHtml ? 'hidden' : ''}
        ><div class="small-text">${variantEditorHtml}${propertiesHtml}<div class="mmx-cart-inventory-notice${inventoryHtml ? ' is-low' : ''}" data-mmx-inventory-notice ${inventoryHtml ? '' : 'hidden'}>${inventoryHtml}</div></div></div>`
      : '';

    const managedGiftTierId = isGift ? giftTierId(item) : '';
    const giftTrackingAttrs = isGift
      ? ` data-mmx-gift-tier-id="${escapeHtml(managedGiftTierId)}" data-mmx-gift-variant-id="${Number(item.variant_id || 0)}"`
      : '';

    return `<div data-mmx-cart-line-wrapper data-mmx-line-key="${escapeHtml(item.key)}"${giftTrackingAttrs}>
      <div class="cart-item product-${escapeHtml(item.handle || '')}${isGift ? ' cart-item--gift' : ''}${isDiscountGift ? ' cart-item--discount-gift' : ''}${hasLineDiscount ? ' cart-item--line-discounted' : ''}" data-mmx-cart-item data-mmx-line-key="${escapeHtml(item.key)}">
        <div class="cart-item__column cart-item__image"><a href="${escapeHtml(url)}">${itemImageHtml(item)}</a></div>
        <div class="cart-item__not-image">
          <div class="cart-item__column cart-item__description">
            <div class="lightly-spaced-row">
              <div class="cart-item__title"><a class="inh-col" href="${escapeHtml(url)}">${escapeHtml(title)}</a></div>
            </div>
          </div>
          <div class="cart-item__column cart-item__price">${priceHtml}</div>
          ${variantRowHtml}
          <div class="cart-item__column cart-item__quantity">${controlsHtml}</div>
        </div>
      </div>
    </div>`;
  }


  function displayCartEntries(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    return items.map((item, index) => ({
      item,
      lineIndex: index + 1,
      originalIndex: index,
      displayGroup: isGiftItem(item) ? 1 : 0
    })).sort((a, b) => {
      if (a.displayGroup !== b.displayGroup) return a.displayGroup - b.displayGroup;
      return a.originalIndex - b.originalIndex;
    });
  }


  function firstUsefulText(selectors, scope) {
    const rootNode = scope || document;
    for (const selector of selectors) {
      const node = rootNode.querySelector && rootNode.querySelector(selector);
      if (!node) continue;
      const text = (node.getAttribute('data-product-title') || node.getAttribute('aria-label') || node.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
    }
    return '';
  }

  function findOptimisticTitle(form) {
    if (form && form.dataset && form.dataset.productTitle) return form.dataset.productTitle;
    const recCard = form && form.closest ? form.closest('.mmx-empty-rec-card, .mmx-cart-addon-card') : null;
    if (recCard && recCard.dataset && recCard.dataset.productTitle) return recCard.dataset.productTitle;

    const local = firstUsefulText([
      '[data-product-title]',
      '.mmx-empty-rec__title',
      '.mmx-cart-addon__title',
      '.mmx-cart-addon__title',
      '.mmx-cart-addon__title',
      '.product-title',
      '.product-detail__title',
      '.product__title',
      'h1'
    ], form && form.closest ? (form.closest('.mmx-empty-rec-card, .mmx-cart-addon-card, [data-section-type], .product-detail, .product, main') || document) : document);
    return local || firstUsefulText(['h1', '.product-title', '.product-detail__title'], document) || 'Adding item';
  }

  function findOptimisticVariantText(form) {
    const pieces = [];
    if (form) {
      const directVariant = form.querySelector('select[name="id"] option:checked');
      const directVariantText = directVariant && (directVariant.getAttribute('data-variant-title') || directVariant.textContent || '').replace(/\s+/g, ' ').trim();
      if (directVariantText && directVariantText !== 'Default Title') return directVariantText;
    }

    const container = form && form.closest ? (form.closest('.mmx-empty-rec-card, .mmx-cart-addon-card, .product-detail, .product, [data-section-type]') || form) : form;
    if (!container) return '';

    container.querySelectorAll('select[name^="options"], select.single-option-selector').forEach((select) => {
      const label = select.closest('.option-selector, .selector-wrapper, .variant-picker')
        ? firstUsefulText(['label', '.label', '.option-selector__label'], select.closest('.option-selector, .selector-wrapper, .variant-picker'))
        : '';
      const option = select.options && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
      const value = option ? option.textContent.replace(/\s+/g, ' ').trim() : '';
      if (value && value !== 'Default Title') pieces.push(label ? `${label}: ${value}` : value);
    });

    container.querySelectorAll('input[type="radio"][name^="options"]:checked').forEach((input) => {
      const label = input.closest('label') || (window.CSS && CSS.escape && input.id ? container.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);
      const value = (label ? label.textContent : input.value || '').replace(/\s+/g, ' ').trim();
      if (value && value !== 'Default Title') pieces.push(value);
    });

    const activeButton = container.querySelector('.option-selector .is-active, .option-selector [aria-pressed="true"], .variant-picker .is-active, .product-form__input input:checked + label');
    if (!pieces.length && activeButton) {
      const value = activeButton.textContent.replace(/\s+/g, ' ').trim();
      if (value && value !== 'Default Title') pieces.push(value);
    }

    return Array.from(new Set(pieces)).join(' / ');
  }

  function findOptimisticImage(form) {
    if (form && form.dataset && form.dataset.productImage) return form.dataset.productImage;
    const hiddenImage = form && form.querySelector && form.querySelector('[data-product-image]');
    if (hiddenImage && hiddenImage.value) return hiddenImage.value;
    const container = form && form.closest ? (form.closest('.mmx-empty-rec-card, .mmx-cart-addon-card, .product-detail, .product, [data-section-type]') || document) : document;
    const img = container.querySelector('.mmx-empty-rec__image img, .mmx-cart-addon__image img, .product-media img, .product-detail__image img, .rimage-wrapper img, .product__media img, img');
    if (!img) return '';
    return img.currentSrc || img.src || img.getAttribute('data-src') || '';
  }

  function optimisticAddRowHtml(data) {
    const image = data.image
      ? `<img src="${escapeHtml(data.image)}" alt="${escapeHtml(data.title)}" loading="eager" width="88" height="88">`
      : '<div class="mmx-cart-item__image-placeholder" aria-hidden="true"></div>';
    const variant = data.variantText
      ? `<div class="cart-item__variant"><span class="cart-item__variant-value">${escapeHtml(data.variantText)}</span></div>`
      : '';
    const qty = Number(data.quantity || 1) > 1 ? `<div class="cart-item__variant">Qty: ${Number(data.quantity || 1)}</div>` : '';
    return `<div data-mmx-cart-line-wrapper class="mmx-cart-line-optimistic">
      <div class="cart-item mmx-cart-item-optimistic" data-mmx-cart-item>
        <div class="cart-item__column cart-item__image">${image}</div>
        <div class="cart-item__not-image">
          <div class="cart-item__column cart-item__description">
            <div class="lightly-spaced-row">
              <div class="cart-item__title"><span class="inh-col">${escapeHtml(data.title)}</span></div>
              <div class="small-text">${variant}${qty}</div>
            </div>
          </div>
          <div class="cart-item__column cart-item__price" aria-hidden="true"></div>
          <div class="cart-item__column cart-item__quantity"><span class="mmx-cart-pending-pill"><span class="mmx-spinner" aria-hidden="true"></span>Adding to cart…</span></div>
        </div>
      </div>
    </div>`;
  }

  function renderOptimisticAdd(form, formData) {
    const existing = cartState && Array.isArray(cartState.items)
      ? cartState
      : { item_count: 0, items: [], total_price: 0 };
    const quantity = sanitizeCartQuantity((formData && formData.get('quantity')) || (form && form.querySelector('[name="quantity"]') && form.querySelector('[name="quantity"]').value) || 1, 1, { allowZero: false });
    if (formData && formData.set) formData.set('quantity', String(quantity));
    const optimisticData = {
      title: findOptimisticTitle(form),
      variantText: findOptimisticVariantText(form),
      image: findOptimisticImage(form),
      quantity
    };

    const list = document.querySelector('.mmx-cart-drawer-shell .cart-item-list__body');
    if (list) {
      const currentRows = displayCartEntries(existing).map((entry) => renderCartItem(entry.item, entry.lineIndex)).join('');
      list.innerHTML = optimisticAddRowHtml(optimisticData) + currentRows;
    }

    const pseudoCart = Object.assign({}, existing, {
      item_count: Number(existing.item_count || 0) + quantity,
      items: [{}].concat(existing.items || [])
    });
    setDrawerState(pseudoCart, {
      loading: true,
      optimistic: true,
      hideFooter: false
    });
    updateCartCount(pseudoCart);
    renderAll(existing);
    document.documentElement.classList.add('mmx-cart-optimistic-open');
    window.clearTimeout(window.__mmxCartOptimisticTimer);
    window.__mmxCartOptimisticTimer = window.setTimeout(() => {
      document.documentElement.classList.remove('mmx-cart-optimistic-open');
    }, 2200);
  }

  function cartDrawerCountLabel(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    const cartQuantity = Math.max(0, Number(cart && cart.item_count || 0));
    let paidQuantity = 0;
    let giftQuantity = 0;

    items.forEach((item) => {
      if (!item) return;
      const quantity = Math.max(0, Math.floor(Number(item.quantity || 0)));
      if (isGiftItem(item) || isFullyDiscountedGiftItem(item)) giftQuantity += quantity;
      else paidQuantity += quantity;
    });

    // Optimistic Add to Cart can temporarily include a placeholder with no
    // quantity. Keep its added units visible as paid items until Shopify responds.
    paidQuantity += Math.max(0, cartQuantity - paidQuantity - giftQuantity);
    const itemLabel = `${paidQuantity} ${paidQuantity === 1 ? 'item' : 'items'}`;
    const giftLabel = giftQuantity > 0
      ? ` + ${giftQuantity} free ${giftQuantity === 1 ? 'gift' : 'gifts'}`
      : '';
    return ` • ${itemLabel}${giftLabel}`;
  }

  function updateCartCount(cart) {
    const count = Math.max(0, Number(cart && cart.item_count || 0));

    document.querySelectorAll('.cart-drawer__title-count').forEach((node) => {
      node.textContent = cartDrawerCountLabel(cart);
    });

    // Symmetry does not render .cart-link__count at all when the page first
    // loads with an empty cart. Updating only existing nodes therefore leaves
    // the header looking empty until a full page refresh. Create the missing
    // badge for every desktop/mobile/sticky cart icon as soon as the count
    // becomes positive.
    document.querySelectorAll('.cart-link .cart-link__icon').forEach((icon) => {
      let badge = icon.querySelector('.cart-link__count');

      if (count > 0 && !badge) {
        badge = document.createElement('span');
        badge.className = 'cart-link__count';
        badge.setAttribute('data-mmx-created-cart-count', 'true');
        icon.appendChild(badge);
      }

      if (badge) {
        badge.textContent = String(count);
        badge.hidden = count <= 0;
        badge.setAttribute('aria-hidden', count > 0 ? 'false' : 'true');
      }
    });

    document.querySelectorAll('.cart-link').forEach((link) => {
      const hasItems = count > 0;

      link.classList.toggle('cart-link--has-items', hasItems);
      link.classList.toggle('has-items', hasItems);
      link.classList.toggle('is-empty', !hasItems);
      link.setAttribute('data-cart-count', String(count));
      link.setAttribute('data-cart-empty', hasItems ? 'false' : 'true');

      const existingLabel = String(link.getAttribute('aria-label') || '').trim();
      if (!existingLabel || /^cart(?:\s*\(|$)/i.test(existingLabel)) {
        link.setAttribute(
          'aria-label',
          hasItems
            ? `Cart (${count} ${count === 1 ? 'item' : 'items'})`
            : 'Cart (empty)'
        );
      }
    });

    document.querySelectorAll('.cart-link__count').forEach((node) => {
      node.textContent = String(count);
      node.hidden = count <= 0;
      node.setAttribute('aria-hidden', count > 0 ? 'false' : 'true');
    });

    document.querySelectorAll(
      '[data-cart-count]:not(.cart-link), .cart-count, .js-cart-count'
    ).forEach((node) => {
      node.textContent = String(count);
      node.hidden = count <= 0;
      node.setAttribute('aria-hidden', count > 0 ? 'false' : 'true');
    });
  }


  function collectRemovableDiscountCodes(cart) {
    const codes = new Map();
    const addCode = (discount) => {
      if (!discount || String(discount.type || '').toLowerCase() !== 'discount_code') return;
      const cleanCode = String(discount.code || discount.title || '').trim();
      if (!cleanCode) return;
      codes.set(cleanCode.toLowerCase(), cleanCode);
    };

    (Array.isArray(cart && cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []).forEach(addCode);
    (Array.isArray(cart && cart.discount_applications) ? cart.discount_applications : []).forEach(addCode);

    return Array.from(codes.values());
  }

  function collectCartDiscounts(cart) {
    const totalDiscount = Math.max(0, Number(cart && cart.total_discount || 0));
    if (!totalDiscount) return { labels: [], total: 0, removableCodes: [] };

    const labels = new Map();
    const addLabel = (title) => {
      const cleanTitle = String(title || '').trim();
      if (!cleanTitle) return;
      labels.set(cleanTitle.toLowerCase(), cleanTitle);
    };

    (Array.isArray(cart && cart.cart_level_discount_applications) ? cart.cart_level_discount_applications : []).forEach((discount) => {
      addLabel(discount.title || discount.code);
    });

    (Array.isArray(cart && cart.discount_applications) ? cart.discount_applications : []).forEach((discount) => {
      addLabel(discount.title || discount.code);
    });

    (Array.isArray(cart && cart.items) ? cart.items : []).forEach((item) => {
      (Array.isArray(item.discounts) ? item.discounts : []).forEach((discount) => {
        addLabel(discount.title);
      });

      (Array.isArray(item.line_level_discount_allocations) ? item.line_level_discount_allocations : []).forEach((allocation) => {
        const application = allocation.discount_application || {};
        addLabel(application.title || application.code);
      });
    });

    const labelList = Array.from(labels.values());
    return {
      labels: labelList.length ? labelList : ['Discount'],
      total: totalDiscount,
      removableCodes: collectRemovableDiscountCodes(cart)
    };
  }

  function setNoteSaveState(container, state) {
    if (!container) return;

    const button = container.querySelector('[data-mmx-note-save]');
    const status = container.querySelector('[data-mmx-note-status]');
    if (!button) return;

    const nextState = state || 'idle';
    const isSaving = nextState === 'saving';
    const isSaved = nextState === 'saved';

    container.setAttribute('data-mmx-note-state', nextState);
    button.classList.toggle('is-saving', isSaving);
    button.classList.toggle('is-saved', isSaved);
    button.disabled = isSaving || isSaved;
    button.setAttribute(
      'aria-label',
      isSaving ? 'Saving order note' : (isSaved ? 'Order note saved' : 'Save order note')
    );

    if (isSaving) {
      button.innerHTML = '<span class="mmx-spinner" aria-hidden="true"></span>';
    } else {
      button.textContent = isSaved ? 'SAVED' : 'SAVE';
    }

    if (status) {
      status.textContent = isSaving
        ? 'Saving order note.'
        : (isSaved ? 'Order note saved.' : '');
    }
  }

  function syncNoteSaveState(container) {
    if (!container) return;

    const input = container.querySelector('[data-mmx-note-input]');
    if (!input) return;

    const savedNote = String(input.dataset.savedNote || '');
    const hasSavedState = input.dataset.hasSavedState === 'true';
    const isUnchanged = String(input.value || '') === savedNote;

    setNoteSaveState(container, hasSavedState && isUnchanged ? 'saved' : 'idle');
  }

  function hydrateNoteSaveControls(scope) {
    const rootScope = scope || document;

    rootScope
      .querySelectorAll('.mmx-cart-drawer-shell [data-mmx-footer-panel="note"]')
      .forEach((panel) => {
        const input = panel.querySelector('textarea[name="mmx_order_note"]');
        if (!input) return;

        const container = input.closest('.toggle-target-container') || input.parentElement;
        if (!container) return;

        container.classList.add('mmx-cart-note-form');
        input.setAttribute('data-mmx-note-input', '');
        input.setAttribute('placeholder', 'Order note');

        if (input.dataset.mmxNoteHydrated !== 'true') {
          const currentValue = String(input.value || '');
          input.dataset.savedNote = currentValue;
          input.dataset.hasSavedState = currentValue ? 'true' : 'false';
          input.dataset.mmxNoteHydrated = 'true';
        }

        let button = container.querySelector('[data-mmx-note-save]');
        if (!button) {
          button = document.createElement('button');
          button.type = 'button';
          button.className = 'mmx-discount-form__button mmx-cart-note-form__button';
          button.setAttribute('data-mmx-note-save', '');
          container.appendChild(button);
        }

        let status = container.querySelector('[data-mmx-note-status]');
        if (!status) {
          status = document.createElement('span');
          status.className = 'visually-hidden';
          status.setAttribute('data-mmx-note-status', '');
          status.setAttribute('aria-live', 'polite');
          status.setAttribute('aria-atomic', 'true');
          container.appendChild(status);
        }

        syncNoteSaveState(container);
      });
  }

  function wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }

  function applyFooterUtilityState(options) {
    hydrateNoteSaveControls(document);

    const opts = options || {};
    const openPanel = footerUtilityOpenPanel;

    document.querySelectorAll('.mmx-cart-drawer-shell').forEach((shell) => {
      shell.querySelectorAll('[data-mmx-footer-toggle]').forEach((toggle) => {
        const name = String(toggle.getAttribute('data-mmx-footer-toggle') || '');
        toggle.setAttribute('aria-expanded', name === openPanel ? 'true' : 'false');
      });

      shell.querySelectorAll('[data-mmx-footer-panel]').forEach((panel) => {
        const name = String(panel.getAttribute('data-mmx-footer-panel') || '');
        panel.classList.toggle('toggle-target--hidden', name !== openPanel);
      });
    });

    if (!opts.focus || !openPanel) return;

    window.requestAnimationFrame(() => {
      const selector = openPanel === 'discount'
        ? '.mmx-cart-drawer-shell [data-mmx-footer-panel="discount"] [name="discount"]'
        : '.mmx-cart-drawer-shell [data-mmx-footer-panel="note"] textarea';
      const control = document.querySelector(selector);
      if (control) control.focus({ preventScroll: true });
    });
  }

  function setFooterUtilityPanel(name, open, options) {
    footerUtilityOpenPanel = open ? String(name || '') : null;
    applyFooterUtilityState(options || {});
  }

  function applyDiscountFormState() {
    document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-discount-form]').forEach((form) => {
      const state = discountFormState || 'idle';
      const message = discountFormMessage || '';
      const input = form.querySelector('[name="discount"]');
      const button = form.querySelector('button[type="submit"]');
      const messageNode = form.querySelector('[data-mmx-discount-message]');
      const isLoading = state === 'loading';
      const isStatus = state === 'loading' || state === 'success' || state === 'error';

      form.classList.toggle('mmx-discount-form--loading', isLoading);
      form.classList.toggle('mmx-discount-form--success', state === 'success');
      form.classList.toggle('mmx-discount-form--error', state === 'error');

      if (button) button.disabled = isLoading;

      if (input) {
        input.classList.toggle('mmx-discount-form__input--loading', isLoading);
        input.classList.toggle('mmx-discount-form__input--success', state === 'success');
        input.classList.toggle('mmx-discount-form__input--error', state === 'error');
        input.readOnly = state === 'loading' || state === 'success';

        if (isStatus) {
          input.value = '';
          input.placeholder = message;
        } else {
          input.placeholder = 'Discount code';
          if (discountFormRestoreValue) {
            input.value = discountFormRestoreValue;
            discountFormRestoreValue = '';
          }
        }
      }

      if (messageNode) {
        messageNode.textContent = message;
        messageNode.hidden = !message;
        messageNode.setAttribute('aria-live', 'polite');
        messageNode.setAttribute('aria-atomic', 'true');
      }
    });
  }

  function setDiscountFormState(state, message, options) {
    const opts = options || {};
    window.clearTimeout(discountFormStatusTimer);
    discountFormStatusTimer = null;

    discountFormState = state || 'idle';
    discountFormMessage = message || '';

    if (discountFormState === 'loading') {
      setFooterUtilityPanel('discount', true, { focus: false });
    }

    if (Object.prototype.hasOwnProperty.call(opts, 'restoreValue')) {
      discountFormRestoreValue = String(opts.restoreValue || '');
    }

    applyDiscountFormState();

    const shouldReset = discountFormState === 'success' || discountFormState === 'error';
    if (shouldReset && opts.autoReset !== false) {
      const resetAfter = Math.max(500, Number(opts.resetAfter || 3000));
      discountFormStatusTimer = window.setTimeout(() => {
        const completedState = discountFormState;
        discountFormState = 'idle';
        discountFormMessage = '';
        applyDiscountFormState();

        if (completedState === 'success' && footerUtilityOpenPanel === 'discount') {
          setFooterUtilityPanel('discount', false, { focus: false });
        }
      }, resetAfter);
    }
  }

  function updateCartDiscounts(cart) {
    const holders = document.querySelectorAll('[data-mmx-cart-discounts]');
    if (!holders.length) return;
    const discountSummary = collectCartDiscounts(cart);
    const labels = discountSummary.labels || [];
    const total = Number(discountSummary.total || 0);
    const removableCodes = new Set(
      (discountSummary.removableCodes || []).map((code) => String(code || '').toLowerCase())
    );

    holders.forEach((holder) => {
      if (!labels.length || total <= 0) {
        holder.innerHTML = '';
        holder.hidden = true;
        return;
      }

      holder.hidden = false;
      const singleClass = labels.length === 1 ? ' mmx-cart-discounts__row--single' : '';
      const chips = labels.map((title) => {
        const cleanTitle = String(title || 'Discount');
        const removable = removableCodes.has(cleanTitle.toLowerCase());
        const removeButton = removable
          ? `<button type="button" class="mmx-cart-discounts__remove" data-mmx-discount-remove="${escapeHtml(cleanTitle)}" aria-label="Remove discount ${escapeHtml(cleanTitle)}">&times;</button>`
          : '';
        return `<li class="mmx-cart-discounts__chip${removable ? ' mmx-cart-discounts__chip--removable' : ''}" title="${escapeHtml(cleanTitle)}"><span class="mmx-cart-discounts__title">${escapeHtml(cleanTitle)}</span>${removeButton}</li>`;
      }).join('');
      holder.innerHTML = `<div class="mmx-cart-discounts__row${singleClass}"><div class="mmx-cart-discounts__label">Discounts</div><ul class="mmx-cart-discounts__list">${chips}</ul><div class="mmx-cart-discounts__total"><span class="mmx-cart-discounts__total-label">Total discounts</span> -${formatMoney(total)}</div></div>`;
    });
  }

  function setDrawerState(cart, options) {
    const opts = options || {};
    const loading = Boolean(opts.loading);
    const hasItems = Boolean(cart && Number(cart.item_count || 0) > 0);
    const hideFooter = Boolean(opts.hideFooter);
    const showContent = hasItems || loading;
    document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer__content').forEach((node) => {
      node.classList.toggle('cart-drawer__content--hidden', !showContent);
    });
    document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer__footer').forEach((node) => {
      node.classList.toggle('cart-drawer__footer--hidden', !hasItems || hideFooter);
    });
    document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer__empty-content').forEach((node) => {
      node.classList.toggle('cart-drawer__empty-content--hidden', showContent);
    });
    document.querySelectorAll('.mmx-cart-drawer-shell .checkout-buttons').forEach((node) => {
      node.classList.toggle('hidden', !hasItems || hideFooter);
    });
    document.querySelectorAll('.mmx-cart-drawer-shell .cart-drawer').forEach((drawer) => {
      drawer.classList.toggle('mmx-cart-is-empty', !hasItems && !loading);
      drawer.classList.toggle('mmx-cart-is-loading', loading);
      drawer.classList.toggle('mmx-cart-is-optimistic', Boolean(opts.optimistic));
    });
  }


  function recommendationCustomisationConfig() {
    const cfg = parseConfig();
    const settings = cfg && cfg.recommendationCustomisation ? cfg.recommendationCustomisation : {};

    return {
      productTag: String(settings.productTag || 'requires_customization').trim().toLowerCase(),
      variantValue: String(settings.variantValue || 'Engraving').trim().toLowerCase(),
      buttonText: String(settings.buttonText || 'CUSTOMISE').trim() || 'CUSTOMISE'
    };
  }

  function recommendationProductTags(product) {
    const tags = product && product.tags;
    if (Array.isArray(tags)) return tags.map((tag) => String(tag || '').trim().toLowerCase()).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim().toLowerCase()).filter(Boolean);
    return [];
  }

  function productRequiresRecommendationCustomisation(product) {
    const settings = recommendationCustomisationConfig();
    return Boolean(settings.productTag && recommendationProductTags(product).includes(settings.productTag));
  }

  function variantRequiresRecommendationCustomisation(variant) {
    if (!variant) return false;
    const settings = recommendationCustomisationConfig();
    if (!settings.variantValue) return false;

    const values = Array.isArray(variant.options)
      ? variant.options.map((value) => String(value || '').trim().toLowerCase())
      : [];

    if (values.includes(settings.variantValue)) return true;

    // Fallback for product JSON shapes that expose only a combined title.
    return String(variant.title || '')
      .split('/')
      .map((value) => value.trim().toLowerCase())
      .includes(settings.variantValue);
  }

  function recommendationFormCustomisationState(form) {
    if (!form) return { enabled: false, required: false, variantId: 0, url: '', buttonText: 'CUSTOMISE' };

    const enabledMarker = form.querySelector('[data-mmx-customisation-product]');
    const enabled = form.dataset.mmxCustomisationProduct === 'true'
      || Boolean(enabledMarker && String(enabledMarker.value || '1') === '1');

    const variantControl = form.querySelector('[name="id"]');
    const option = variantControl && variantControl.tagName === 'SELECT'
      ? variantControl.options[variantControl.selectedIndex]
      : variantControl;

    const required = enabled && Boolean(
      option
      && String(option.getAttribute('data-requires-customisation') || '').toLowerCase() === 'true'
    );

    const urlMarker = form.querySelector('[data-mmx-product-url]');
    const labelMarker = form.querySelector('[data-mmx-customise-label]');

    return {
      enabled,
      required,
      variantId: Number(variantControl && variantControl.value || 0),
      url: String(form.dataset.mmxProductUrl || (urlMarker && urlMarker.value) || ''),
      buttonText: String(form.dataset.mmxCustomiseLabel || (labelMarker && labelMarker.value) || recommendationCustomisationConfig().buttonText || 'CUSTOMISE')
    };
  }

  function recommendationCustomisationUrl(form) {
    const state = recommendationFormCustomisationState(form);
    if (!state.required || !state.url || !state.variantId) return '';

    try {
      const url = new URL(state.url, window.location.origin);
      url.searchParams.set('variant', String(state.variantId));
      url.searchParams.set('mmx_customise', '1');
      const recommendationSource = recommendationSourceForForm(form);
      if (recommendationSource) {
        url.searchParams.set('mmx_rec_source', recommendationSource);
      }

      return url.origin === window.location.origin
        ? `${url.pathname}${url.search}${url.hash}`
        : url.href;
    } catch (error) {
      const separator = state.url.indexOf('?') >= 0 ? '&' : '?';
      const recommendationSource = recommendationSourceForForm(form);
      const sourceQuery = recommendationSource
        ? `&mmx_rec_source=${encodeURIComponent(recommendationSource)}`
        : '';
      return `${state.url}${separator}variant=${encodeURIComponent(state.variantId)}&mmx_customise=1${sourceQuery}`;
    }
  }

  function updateRecommendationCustomisationButton(form) {
    if (!form) return;
    const button = form.querySelector('.mmx-cart-addon__button');
    if (!button) return;

    const state = recommendationFormCustomisationState(form);
    const label = state.required ? state.buttonText : 'Add';

    button.dataset.mmxOriginalText = label;
    button.textContent = label;
    button.setAttribute('aria-label', label);
    button.classList.toggle('mmx-cart-addon__button--customise', state.required);
  }

  function recommendationCustomiseLandingRequested() {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('mmx_customise') === '1'
        && Number(params.get('variant') || 0) > 0;
    } catch (error) {
      return false;
    }
  }

  function recommendationEngravingContainers() {
    return Array.from(document.querySelectorAll(
      '#engraving_text_div, #engraving_text_div_gold_bar'
    ));
  }

  function recommendationEngravingFieldIsReady() {
    return recommendationEngravingContainers().some((container) => {
      if (!container) return false;

      const field = container.querySelector(
        '.engraving-input, input[name*="Engraving"], textarea[name*="Engraving"]'
      );

      if (!field) return false;

      const style = window.getComputedStyle(container);
      return style.display !== 'none'
        && style.visibility !== 'hidden'
        && container.getClientRects().length > 0;
    });
  }

  function recommendationSelectedEngravingControl() {
    const groupNames = [
      'Engraving',
      'Po-Engraving',
      'Customise',
      'Customisation',
      'Add',
      'Variation'
    ];

    for (const groupName of groupNames) {
      const groups = groupName === 'Add'
        ? Array.from(document.querySelectorAll('[id="Add"]'))
        : [document.getElementById(groupName)].filter(Boolean);

      for (const group of groups) {
        const checked = group && group.querySelector(
          'input[type="radio"]:checked, input[type="checkbox"]:checked, select'
        );

        if (checked) return checked;
      }
    }

    return document.querySelector(
      'input[type="radio"]:checked[value="Engraving"],'
      + ' input[type="radio"]:checked[value*="Engraving"],'
      + ' select[name*="option"]'
    );
  }

  function cleanRecommendationCustomiseMarker() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('mmx_customise')) return;

      url.searchParams.delete('mmx_customise');
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    } catch (error) {
      // The marker is harmless if the browser does not allow URL replacement.
    }
  }

  function initialiseRecommendationCustomiseLanding() {
    if (!recommendationCustomiseLandingRequested()) return;

    let attempt = 0;
    const delays = [0, 80, 180, 350, 650, 1000, 1600, 2400, 3600];

    const runAttempt = () => {
      attempt += 1;

      if (recommendationEngravingFieldIsReady()) {
        cleanRecommendationCustomiseMarker();
        return;
      }

      const selectedControl = recommendationSelectedEngravingControl();

      if (selectedControl) {
        selectedControl.dispatchEvent(
          new Event('input', { bubbles: true })
        );
        selectedControl.dispatchEvent(
          new Event('change', { bubbles: true })
        );
      }

      // Reuse the developer template's own field-generation functions when
      // available. Repeated calls are safe because that code already checks
      // how many engraving fields currently exist.
      try {
        if (typeof window.onChoiceSelected === 'function') {
          window.onChoiceSelected();
        }
      } catch (error) {
        console.warn(
          '[MMX Cart] Product engraving initialisation retry failed.',
          error
        );
      }

      try {
        if (
          typeof window.onChoiceSelectedGoldbar === 'function'
          && document.getElementById('engraving_text_div_gold_bar')
        ) {
          window.onChoiceSelectedGoldbar();
        }
      } catch (error) {
        console.warn(
          '[MMX Cart] Gold-bar engraving initialisation retry failed.',
          error
        );
      }

      window.requestAnimationFrame(() => {
        if (recommendationEngravingFieldIsReady()) {
          cleanRecommendationCustomiseMarker();
        }
      });

      if (attempt < delays.length) {
        window.setTimeout(runAttempt, delays[attempt]);
      }
    };

    runAttempt();
  }

  function variantOptionsHtml(product) {
    const variants = availableProductVariants(product);
    const customisationProduct = productRequiresRecommendationCustomisation(product);

    if (variants.length <= 1) {
      const variant = variants[0];
      const requiresCustomisation = customisationProduct
        && variantRequiresRecommendationCustomisation(variant);
      const title = meaningfulVariantTitle(variant);

      if (!variant) return '';

      return `<input
        type="hidden"
        name="id"
        value="${Number(variant.id || 0)}"
        data-requires-customisation="${requiresCustomisation ? 'true' : 'false'}">
        ${title ? `<div
          class="mmx-cart-addon__variant-static"
          aria-label="Selected variant"><span
            class="mmx-single-variant__value">${escapeHtml(title)}</span></div>` : ''}`;
    }

    const selectedVariant = variants[0] || {};
    const selectedTitle = normaliseVariantDisplayCasing(
      selectedVariant.title && selectedVariant.title !== 'Default Title'
        ? selectedVariant.title
        : 'Default'
    );

    return `<label class="visually-hidden">Choose option</label><span class="mmx-variant-select-shell mmx-cart-addon__variant-shell"><select class="mmx-cart-addon__variant" name="id" aria-label="Choose option">${variants.map((variant) => {
      const title = normaliseVariantDisplayCasing(
        variant.title && variant.title !== 'Default Title'
          ? variant.title
          : 'Default'
      );
      const priceCents = Number(variant.price || 0);
      const compareCents = Number(variant.compare_at_price || 0);
      const price = typeof variant.price === 'number' ? formatMoney(priceCents) : '';
      const requiresCustomisation = customisationProduct && variantRequiresRecommendationCustomisation(variant);
      const optionLabel = priceCents > 0
        ? `${title} — ${formatMoney(priceCents)}`
        : title;

      return `<option value="${Number(variant.id || 0)}" data-variant-title="${escapeHtml(title)}" data-variant-price="${escapeHtml(price)}" data-variant-price-cents="${priceCents}" data-variant-compare-at-cents="${compareCents}" data-requires-customisation="${requiresCustomisation ? 'true' : 'false'}">${escapeHtml(optionLabel)}</option>`;
    }).join('')}</select><span class="mmx-variant-select-display" data-mmx-variant-select-display aria-hidden="true">${escapeHtml(selectedTitle)}</span></span>`;
  }

  function autoAddonCardHtml(product) {
    const variants = availableProductVariants(product);
    if (!variants.length) return '';

    const firstVariant = variants[0] || {};
    const hasVisibleVariant = variants.length > 1
      || Boolean(meaningfulVariantTitle(firstVariant));
    const image = product && Array.isArray(product.images) && product.images.length ? product.images[0] : '';
    const image140 = image ? String(image).replace(/(\.jpg|\.jpeg|\.png|\.webp)(\?v=|\?|$)/i, '_140x$1$2') : '';
    const price = typeof firstVariant.price === 'number' ? addonPriceHtml(firstVariant.price, firstVariant.compare_at_price || 0) : '';
    const title = product && product.title ? product.title : 'Recommended product';
    const handle = String(product && product.handle || '').trim();
    const url = product && product.url
      ? product.url
      : (handle ? `${root}products/${encodeURIComponent(handle)}` : '#');
    const customisationProduct = productRequiresRecommendationCustomisation(product);
    const requiresCustomisation = customisationProduct && variantRequiresRecommendationCustomisation(firstVariant);
    const customisationSettings = recommendationCustomisationConfig();
    const buttonText = requiresCustomisation ? customisationSettings.buttonText : 'Add';

    return `<article class="mmx-cart-addon-card" data-product-title="${escapeHtml(title)}">
      <a class="mmx-cart-addon__image" href="${escapeHtml(url)}" aria-label="${escapeHtml(title)}">
        ${image140 ? `<img src="${escapeHtml(image140)}" alt="${escapeHtml(title)}" width="70" height="70" loading="lazy">` : '<div class="mmx-cart-item__image-placeholder" aria-hidden="true"></div>'}
      </a>
      <div class="mmx-cart-addon__info ${hasVisibleVariant ? 'mmx-cart-addon__info--has-variant' : 'mmx-cart-addon__info--single'}">
        <a class="mmx-cart-addon__title inh-col" href="${escapeHtml(url)}">${escapeHtml(title)}</a>
        <div class="mmx-cart-addon__price" data-mmx-cart-addon-price>${price}</div>
        <form method="post" action="${root}cart/add" class="mmx-cart-addon__form ${hasVisibleVariant ? 'mmx-cart-addon__form--has-variant' : 'mmx-cart-addon__form--single'}"${customisationProduct ? ` data-mmx-customisation-product="true" data-mmx-product-url="${escapeHtml(url)}" data-mmx-customise-label="${escapeHtml(customisationSettings.buttonText)}"` : ''}>
          ${customisationProduct ? `<input type="hidden" data-mmx-customisation-product value="1"><input type="hidden" data-mmx-product-url value="${escapeHtml(url)}"><input type="hidden" data-mmx-customise-label value="${escapeHtml(customisationSettings.buttonText)}">` : ''}
          ${image140 ? `<input type="hidden" data-product-image value="${escapeHtml(image140)}">` : ''}
          ${variantOptionsHtml(product)}
          <input type="hidden" name="quantity" value="1">
          <button type="submit" class="mmx-cart-addon__button${requiresCustomisation ? ' mmx-cart-addon__button--customise' : ''}" data-product-title="${escapeHtml(title)}">${escapeHtml(buttonText)}</button>
        </form>
      </div>
    </article>`;
  }

  function addonVisibleCount(container) {
    const raw = Number(container && container.getAttribute('data-mmx-addon-visible-count'));
    if (!Number.isFinite(raw)) return 3;
    return Math.max(1, Math.min(5, Math.round(raw)));
  }

  function addonPagesHtml(products, visibleCount) {
    const pages = [];
    for (let i = 0; i < products.length; i += visibleCount) {
      pages.push(`<div class="mmx-cart-addons__page" data-mmx-addons-page>${products.slice(i, i + visibleCount).map(autoAddonCardHtml).join('')}</div>`);
    }
    return pages.join('');
  }

  function addonShellHtml(heading, pagesHtml, visibleCount, options) {
    const opts = options || {};
    const emptyClass = opts.state === 'empty' ? ' mmx-cart-addons--empty' : '';
    const kind = String(opts.kind || '');
    const collapsible = Boolean(opts.collapsible);
    const collapsed = collapsible && opts.collapsed !== false;
    const collapseClass = collapsible ? ` mmx-cart-addons--collapsible${collapsed ? ' is-collapsed' : ''}` : '';

    return `<div class="mmx-cart-addons cart-drawer__content-item${emptyClass}${collapseClass}" data-mmx-addons data-mmx-addon-visible-count="${visibleCount}"${kind ? ` data-mmx-recommendation-kind="${escapeHtml(kind)}"` : ''}>
      <div class="mmx-cart-addons__top">
        ${collapsible
          ? `<button type="button" class="mmx-cart-addons__collapse" data-mmx-addons-collapse aria-expanded="${collapsed ? 'false' : 'true'}">
              <span class="mmx-cart-addons__heading product-detail-label">${escapeHtml(heading || 'Recently viewed')}</span>
              <span class="mmx-cart-addons__collapse-icon" aria-hidden="true">${collapsed ? '+' : '−'}</span>
            </button>`
          : `<div class="mmx-cart-addons__heading product-detail-label">${escapeHtml(heading || 'You may like')}</div>`}
        <div class="mmx-cart-addons__arrows">
          <button type="button" class="mmx-cart-addons__arrow" data-mmx-addons-prev aria-label="Previous products">‹</button>
          <button type="button" class="mmx-cart-addons__arrow" data-mmx-addons-next aria-label="Next products">›</button>
        </div>
      </div>
      <div class="mmx-cart-addons__body" data-mmx-addons-body ${collapsed ? 'hidden' : ''}>
        <div class="mmx-cart-addons__viewport" data-mmx-addons-viewport>
          <div class="mmx-cart-addons__track" data-mmx-addons-track>${pagesHtml}</div>
        </div>
        <div
          class="mmx-cart-addons__dots"
          data-mmx-addons-dots
          aria-label="Recommendation pages"
        ></div>
      </div>
    </div>`;
  }

  function syncAddonProgressDots(addons, pageCount, activeIndex) {
    if (!addons) return;

    const dots = addons.querySelector('[data-mmx-addons-dots]');
    if (!dots) return;

    const cfg = parseConfig();
    const dotSettings = cfg
      && cfg.recommendations
      && cfg.recommendations.progressDots;
    const enabled = !dotSettings || dotSettings.enabled !== false;

    if (!enabled) {
      dots.innerHTML = '';
      dots.hidden = true;
      return;
    }

    const count = Math.max(0, Number(pageCount || 0));
    const safeActive = Math.max(
      0,
      Math.min(Math.max(0, count - 1), Number(activeIndex || 0))
    );

    if (count <= 1) {
      dots.innerHTML = '';
      dots.hidden = true;
      return;
    }

    dots.hidden = false;

    const existing = Array.from(
      dots.querySelectorAll('[data-mmx-addons-dot]')
    );

    if (existing.length !== count) {
      dots.innerHTML = Array.from({ length: count }, (_, index) => (
        `<button
          type="button"
          class="mmx-cart-addons__dot"
          data-mmx-addons-dot
          data-page-index="${index}"
          aria-label="Go to recommendation page ${index + 1}"
        ></button>`
      )).join('');
    }

    dots.querySelectorAll('[data-mmx-addons-dot]').forEach((dot, index) => {
      const active = index === safeActive;
      dot.classList.toggle('is-active', active);

      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  function setAddonPage(addons, pageIndex) {
    if (!addons) return;
    mmxClearRecommendationTouchGesture();
    const track = addons.querySelector('[data-mmx-addons-track]');
    const pages = Array.from(addons.querySelectorAll('[data-mmx-addons-page]'));
    const prev = addons.querySelector('[data-mmx-addons-prev]');
    const next = addons.querySelector('[data-mmx-addons-next]');
    const max = Math.max(0, pages.length - 1);
    const safe = Math.max(0, Math.min(max, Number(pageIndex || 0)));

    addons.setAttribute('data-mmx-addon-page', String(safe));
    if (track) track.style.transform = `translateX(-${safe * 100}%)`;
    if (prev) prev.disabled = safe <= 0;
    if (next) next.disabled = safe >= max;

    syncAddonProgressDots(addons, pages.length, safe);
    addons.classList.toggle('mmx-cart-addons--single-page', pages.length <= 1);
  }

  function normaliseAddonCards(scope) {
    const rootNode = scope || document;

    rootNode.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-addons-page]').forEach((page) => {
      // MMX renders recommendation pages exclusively with mmx-cart-addon-card.
      // Remove foreign/native product-card nodes if another theme script injects them.
      Array.from(page.children).forEach((child) => {
        if (!child.classList || !child.classList.contains('mmx-cart-addon-card')) {
          child.remove();
        }
      });
    });

    rootNode.querySelectorAll('.mmx-cart-drawer-shell .mmx-cart-addon-card').forEach((card) => {
      card.classList.add('mmx-cart-addon-card--normalised');

      // Recommendation cards intentionally do not use theme product badges.
      card.querySelectorAll(
        [
          '.product-label',
          '.product-badge',
          '.product-block__label',
          '.product-block__badge',
          '.product-card__label',
          '.product-card__badge',
          '.product-tag',
          '[data-product-label]',
          '[data-product-badge]',
          '[data-label]',
          '[class*="product-label"]',
          '[class*="product-badge"]',
          '[class*="badge"]',
          '[class*="sticker"]',
          '[class*="ribbon"]',
          '[class*="flag"]'
        ].join(',')
      ).forEach((badge) => {
        if (!badge.closest('.mmx-cart-addon__title, .mmx-cart-addon__price, .mmx-cart-addon__form')) {
          badge.remove();
        }
      });

      // MMX recommendation cards have a fixed, known structure. Remove any
      // foreign element inserted outside that structure, including empty
      // badge shells whose text has already been removed by another script.
      Array.from(card.children).forEach((child) => {
        if (!child.matches('.mmx-cart-addon__image, .mmx-cart-addon__info')) {
          child.remove();
        }
      });

      const image = card.querySelector(':scope > .mmx-cart-addon__image');
      if (image) {
        Array.from(image.children).forEach((child) => {
          if (!child.matches('img, .mmx-cart-item__image-placeholder')) {
            child.remove();
          }
        });
      }

      const info = card.querySelector(':scope > .mmx-cart-addon__info');
      if (info) {
        Array.from(info.children).forEach((child) => {
          if (!child.matches('.mmx-cart-addon__title, .mmx-cart-addon__price, .mmx-cart-addon__form')) {
            child.remove();
          }
        });
      }

      // Some theme badge implementations use unusual class names or inject
      // plain text nodes. Remove exact badge copy without touching titles.
      const badgeTextPattern = /^(best\s*seller|bestseller|best\s*selling)$/i;
      card.querySelectorAll('*').forEach((node) => {
        if (node.closest('.mmx-cart-addon__title, .mmx-cart-addon__price, .mmx-cart-addon__form')) return;

        const dataLabel = String(node.getAttribute && node.getAttribute('data-label') || '').trim();
        if (badgeTextPattern.test(dataLabel)) {
          node.removeAttribute('data-label');
        }

        const text = String(node.textContent || '').replace(/\s+/g, ' ').trim();
        if (badgeTextPattern.test(text) && node.children.length === 0) {
          node.remove();
        }
      });
    });
  }

  function normaliseCartItemBadges(scope) {
    const rootNode = scope || document;

    rootNode.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-cart-item]').forEach((item) => {
      item.querySelectorAll(
        [
          '.product-label',
          '.product-badge',
          '.product-block__label',
          '.product-block__badge',
          '.product-card__label',
          '.product-card__badge',
          '.product-tag',
          '[data-product-label]',
          '[data-product-badge]',
          '[class*="product-label"]',
          '[class*="product-badge"]'
        ].join(',')
      ).forEach((badge) => badge.remove());
    });
  }

  function queueAddonNormalise(scope) {
    if (addonNormaliseQueued) return;
    addonNormaliseQueued = true;

    window.requestAnimationFrame(() => {
      addonNormaliseQueued = false;
      normaliseAddonCards(scope || document);
      normaliseCartItemBadges(scope || document);
    });
  }

  function observeAddonMutations() {
    const shell = document.querySelector('.mmx-cart-drawer-shell');
    if (!shell || shell === observedAddonShell) return;

    if (addonMutationObserver) addonMutationObserver.disconnect();

    observedAddonShell = shell;
    addonMutationObserver = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target && mutation.target.nodeType === 1
          ? mutation.target
          : mutation.target && mutation.target.parentElement;

        if (target && target.closest && target.closest('[data-mmx-addons], .mmx-cart-addon-card, [data-mmx-cart-item]')) {
          return true;
        }

        return Array.from(mutation.addedNodes || []).some((node) => (
          node.nodeType === 1
          && (
            (node.matches && node.matches('[data-mmx-addons], .mmx-cart-addon-card, [data-mmx-cart-item], [class*="product-label"], [class*="product-badge"]'))
            || (node.querySelector && node.querySelector('[data-mmx-addons], .mmx-cart-addon-card, [data-mmx-cart-item], [class*="product-label"], [class*="product-badge"]'))
          )
        ));
      });

      if (relevant) queueAddonNormalise(shell);
    });

    addonMutationObserver.observe(shell, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'data-label', 'data-product-label', 'data-product-badge']
    });
  }

  function syncAddonControls(scope) {
    normaliseAddonCards(scope || document);
    normaliseCartItemBadges(scope || document);
    observeAddonMutations();

    (scope || document).querySelectorAll('.mmx-cart-drawer-shell .mmx-cart-addon__form').forEach((form) => {
      updateRecommendationCustomisationButton(form);
    });

    syncDesktopVariantSelects(scope || document);

    (scope || document).querySelectorAll('.mmx-cart-drawer-shell [data-mmx-addons]').forEach((addons) => {
      const current = Number(addons.getAttribute('data-mmx-addon-page') || 0);
      setAddonPage(addons, current);
    });
  }

  let addonMutationObserver = null;
  let observedAddonShell = null;
  let addonNormaliseQueued = false;

  function recommendationSettings() {
    const cfg = parseConfig();
    const settings = cfg && cfg.recommendations ? cfg.recommendations : {};
    const recentlyViewed = settings.recentlyViewed || {};
    const youMayLike = settings.youMayLike || {};

    const visible = (value, fallback) => Math.max(1, Math.min(5, Math.round(Number(value || fallback || 3))));
    const maxRecent = (value, fallback, cap) => Math.max(3, Math.min(cap, Math.round(Number(value || fallback))));
    const maxRecommended = (value, fallback) => Math.max(3, Math.min(10, Math.round(Number(value || fallback))));

    return {
      recentlyViewed: {
        emptyEnabled: recentlyViewed.emptyEnabled !== false,
        filledEnabled: recentlyViewed.filledEnabled !== false,
        heading: String(recentlyViewed.heading || 'Recently viewed').trim().toLowerCase() === 'recently viewed'
          ? 'Recently viewed'
          : String(recentlyViewed.heading || 'Recently viewed').trim(),
        emptyVisibleCount: visible(recentlyViewed.emptyVisibleCount, 3),
        emptyMaxProducts: maxRecent(recentlyViewed.emptyMaxProducts, 6, 24),
        filledVisibleCount: visible(recentlyViewed.filledVisibleCount, 2),
        filledMaxProducts: maxRecent(recentlyViewed.filledMaxProducts, 4, 12),
        filledCollapsed: recentlyViewed.filledCollapsed !== false,
        retentionDays: Math.max(1, Number(recentlyViewed.retentionDays || 60))
      },
      youMayLike: {
        emptyEnabled: youMayLike.emptyEnabled !== false,
        filledEnabled: youMayLike.filledEnabled !== false,
        emptyHeading: String(youMayLike.emptyHeading || 'You may like'),
        filledHeading: String(youMayLike.filledHeading || 'You may like'),
        emptyVisibleCount: visible(youMayLike.emptyVisibleCount, 3),
        emptyMaxProducts: maxRecommended(youMayLike.emptyMaxProducts, 9),
        filledVisibleCount: visible(youMayLike.filledVisibleCount, 2),
        filledMaxProducts: maxRecommended(youMayLike.filledMaxProducts, 4),
        automatic: youMayLike.automatic !== false,
        filledSeedLimit: Math.max(1, Math.min(2, Math.round(Number(youMayLike.filledSeedLimit || 2)))),
        filledRewardAware: youMayLike.filledRewardAware !== false,
        filledRewardMaxGapCents: Math.max(0, Math.round(Number(youMayLike.filledRewardMaxGapCents || 15000))),
        filledRelatedMaxPricePercent: Math.max(0, Math.min(200, Number(youMayLike.filledRelatedMaxPricePercent || 70))),
        filledPreferCrossCategoryRelated: youMayLike.filledPreferCrossCategoryRelated !== false,
        filledSameCategoryOverflowMin: Math.max(1, Math.round(Number(youMayLike.filledSameCategoryOverflowMin || 4))),
        filledPopularityBoost: Math.max(0, Math.min(50, Number(youMayLike.filledPopularityBoost || 0))),
        filledPopularityProductIds: Array.isArray(youMayLike.filledPopularityProductIds) ? youMayLike.filledPopularityProductIds.map((id) => Number(id || 0)).filter((id) => id > 0).slice(0, 50) : [],
        firstVisitEnabled: youMayLike.firstVisitEnabled !== false,
        firstVisitBestsellers: Array.isArray(youMayLike.firstVisitBestsellers) ? youMayLike.firstVisitBestsellers.slice(0, 12) : [],
        firstVisitNewArrivals: Array.isArray(youMayLike.firstVisitNewArrivals) ? youMayLike.firstVisitNewArrivals.slice(0, 12) : [],
        emptyFallback: Array.isArray(youMayLike.emptyFallback) ? youMayLike.emptyFallback : [],
        filledFallback: Array.isArray(youMayLike.filledFallback) ? youMayLike.filledFallback : []
      }
    };
  }

  function safeRecentlyViewedRead() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(recentlyViewedStorageKey) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function safeRecentlyViewedWrite(entries) {
    try {
      window.localStorage.setItem(recentlyViewedStorageKey, JSON.stringify(entries));
    } catch (error) {
      // Browser storage might be unavailable or full. Recommendations still work.
    }
  }

  function cleanRecentlyViewedEntries(entries) {
    const settings = recommendationSettings().recentlyViewed;
    const expiry = Date.now() - (settings.retentionDays * 24 * 60 * 60 * 1000);
    const seen = new Set();

    return (Array.isArray(entries) ? entries : [])
      .filter((entry) => {
        const id = Number(entry && entry.id || 0);
        const handle = String(entry && entry.handle || '').trim();
        const viewedAt = Number(entry && entry.viewedAt || 0);

        if ((!id && !handle) || viewedAt < expiry) return false;

        const key = id ? `id:${id}` : `handle:${handle.toLowerCase()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 24);
  }

  function recordCurrentProductView() {
    const cfg = parseConfig();
    const current = cfg && cfg.currentProduct;
    const id = Number(current && current.id || 0);
    const handle = String(current && current.handle || '').trim();

    if (!id || !handle) return;

    const nextEntry = {
      id,
      handle,
      url: String(current.url || `${root}products/${encodeURIComponent(handle)}`),
      viewedAt: Date.now()
    };

    const existing = cleanRecentlyViewedEntries(safeRecentlyViewedRead())
      .filter((entry) => Number(entry.id || 0) !== id && String(entry.handle || '').toLowerCase() !== handle.toLowerCase());

    safeRecentlyViewedWrite([nextEntry, ...existing].slice(0, 24));
  }

  function recentViewEntries() {
    const clean = cleanRecentlyViewedEntries(safeRecentlyViewedRead());
    safeRecentlyViewedWrite(clean);
    return clean;
  }

  function cartRecommendationProductIds(cart) {
    return new Set(
      (cart && Array.isArray(cart.items) ? cart.items : [])
        .map((item) => Number(item && item.product_id || 0))
        .filter(Boolean)
    );
  }

  function uniqueAvailableProducts(products, excludedIds, limit) {
    const seen = new Set();
    const excluded = excludedIds instanceof Set ? excludedIds : new Set(excludedIds || []);
    const output = [];

    for (const product of (Array.isArray(products) ? products : [])) {
      const id = Number(product && product.id || 0);
      const handle = String(product && product.handle || '').trim();
      const key = id ? `id:${id}` : `handle:${handle.toLowerCase()}`;

      if ((!id && !handle) || seen.has(key) || (id && excluded.has(id))) continue;
      if (!availableProductVariants(product).length) continue;

      if (!product.url && handle) {
        product.url = `${root}products/${encodeURIComponent(handle)}`;
      }

      seen.add(key);
      output.push(product);

      if (output.length >= limit) break;
    }

    return output;
  }

  async function fetchRecommendationDescriptorProduct(descriptor) {
    const handle = String(descriptor && descriptor.handle || '').trim();
    const url = String(descriptor && descriptor.url || '');
    const product = await fetchProductData(handle, url);

    if (product && !product.url) {
      product.url = url || (handle ? `${root}products/${encodeURIComponent(handle)}` : '');
    }

    return product;
  }

  async function fetchDescriptorProducts(descriptors, maxProducts) {
    const limited = (Array.isArray(descriptors) ? descriptors : []).slice(0, Math.max(maxProducts * 2, maxProducts));
    const products = await Promise.all(limited.map(fetchRecommendationDescriptorProduct));
    return products.filter(Boolean);
  }

  async function fetchRecentlyViewedProducts(maxProducts, excludedIds) {
    const entries = recentViewEntries();
    const candidates = [];

    for (const entry of entries) {
      if (candidates.length >= maxProducts) break;
      if (excludedIds && excludedIds.has(Number(entry.id || 0))) continue;

      const product = await fetchRecommendationDescriptorProduct(entry);
      if (!product) continue;

      const available = uniqueAvailableProducts([product], excludedIds, 1);
      if (available.length) candidates.push(available[0]);
    }

    return uniqueAvailableProducts(candidates, excludedIds, maxProducts);
  }

  async function fetchShopifyRecommendations(productId, maxProducts, intent) {
    if (!productId || maxProducts <= 0) return [];

    const safeIntent = intent === 'complementary' ? 'complementary' : 'related';
    const requestLimit = Math.max(1, Math.min(10, maxProducts));
    const cacheKey = `${Number(productId)}|${safeIntent}|${requestLimit}`;

    if (shopifyRecommendationCache.has(cacheKey)) {
      return shopifyRecommendationCache.get(cacheKey);
    }

    const request = (async () => {
      const response = await fetch(
        `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${requestLimit}&intent=${safeIntent}`,
        {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }
      );

      if (!response.ok) {
        throw new Error(`Recommendations failed: ${response.status}`);
      }

      const data = await response.json();
      return Array.isArray(data && data.products) ? data.products : [];
    })();

    shopifyRecommendationCache.set(cacheKey, request);

    try {
      return await request;
    } catch (error) {
      // Do not permanently cache a failed network request. A later cart render
      // can retry without requiring a page reload.
      shopifyRecommendationCache.delete(cacheKey);
      throw error;
    }
  }

  function cartRecommendationSeedProductIds(cart, limit) {
    const maxSeeds = Math.max(1, Math.min(2, Number(limit || 2)));
    const byProduct = new Map();

    (cart && Array.isArray(cart.items) ? cart.items : []).forEach((item, index) => {
      if (!item || isGiftItem(item) || isFullyDiscountedGiftItem(item)) return;

      const productId = Number(item.product_id || 0);
      if (!productId) return;

      const lineValue = Math.max(0, Number(
        item.final_line_price != null ? item.final_line_price : item.original_line_price || 0
      ));
      const current = byProduct.get(productId);

      if (current) {
        current.value += lineValue;
        return;
      }

      byProduct.set(productId, {
        id: productId,
        value: lineValue,
        firstIndex: index
      });
    });

    return Array.from(byProduct.values())
      .sort((a, b) => (b.value - a.value) || (a.firstIndex - b.firstIndex))
      .slice(0, maxSeeds)
      .map((entry) => entry.id);
  }

  function recommendationProductPriceCents(product) {
    const variant = availableProductVariants(product)[0];
    return Math.max(0, Number(variant && variant.price || product && product.price || 0));
  }

  function recommendationRewardContext(cart, settings) {
    if (!settings || settings.filledRewardAware === false) return null;

    const cfg = parseConfig();
    const tiers = cfg && Array.isArray(cfg.tiers)
      ? cfg.tiers
        .filter((tier) => Number(tier && tier.amountCents || 0) > 0)
        .slice()
        .sort((a, b) => Number(a.amountCents || 0) - Number(b.amountCents || 0))
      : [];

    if (!tiers.length) return null;

    const spend = eligibleSpend(cart, cfg);
    const nextTier = tiers.find((tier) => spend < Number(tier.amountCents || 0));
    if (!nextTier) return null;

    const gapCents = Math.max(0, Number(nextTier.amountCents || 0) - spend);
    const maxGapCents = Math.max(0, Number(settings.filledRewardMaxGapCents || 0));

    if (!gapCents || !maxGapCents || gapCents > maxGapCents) return null;

    return { spend, gapCents };
  }

  function recommendationRewardFitScore(product, rewardContext) {
    if (!rewardContext) return 0;

    const priceCents = recommendationProductPriceCents(product);
    const gapCents = Math.max(1, Number(rewardContext.gapCents || 0));
    if (!priceCents) return 0;

    // The strongest boost goes to an add-on that clears the next reward with
    // little overspend. Products that almost close the gap receive a small
    // boost, but relevance remains the primary ranking signal.
    if (priceCents >= gapCents) {
      const overspend = priceCents - gapCents;
      const tolerance = Math.max(5000, gapCents * 1.5);
      return Math.max(0, 18 * (1 - (overspend / tolerance)));
    }

    const coverage = priceCents / gapCents;
    return coverage >= 0.65 ? 4 * coverage : 0;
  }

  function recommendationAddOnPriceScore(product, cart) {
    const priceCents = recommendationProductPriceCents(product);
    if (!priceCents) return 0;

    const cfg = parseConfig();
    const spend = Math.max(1, eligibleSpend(cart, cfg));
    const ratio = priceCents / spend;

    if (ratio <= 0.45) return 8;
    if (ratio <= 0.75) return 4;
    if (ratio <= 1.25) return 0;
    return -6;
  }

  function recommendationCategoryKey(value) {
    const text = String(value || '').trim().toLowerCase();
    if (!text) return '';

    const categories = [
      ['bracelet', /\bbracelet(s)?\b/],
      ['bangle', /\bbangle(s)?\b/],
      ['pendant', /\bpendant(s)?\b/],
      ['ring', /\bring(s)?\b/],
      ['earring', /\bearring(s)?\b/],
      ['necklace', /\bnecklace(s)?\b/],
      ['chain', /\bchain(s)?\b/],
      ['anklet', /\banklet(s)?\b/],
      ['charm', /\bcharm(s)?\b/]
    ];

    const match = categories.find((entry) => entry[1].test(text));
    return match ? match[0] : text.replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function recommendationProductCategoryKey(product) {
    return recommendationCategoryKey(
      product
        ? [product.product_type, product.type, product.title].filter(Boolean).join(' ')
        : ''
    );
  }

  function cartPaidProductCategoryKeys(cart) {
    const keys = new Set();

    (cart && Array.isArray(cart.items) ? cart.items : []).forEach((item) => {
      if (!item || isGiftItem(item) || isFullyDiscountedGiftItem(item)) return;

      const key = recommendationCategoryKey(
        [item.product_type, item.product_title, item.title].filter(Boolean).join(' ')
      );
      if (key) keys.add(key);
    });

    return keys;
  }

  function recommendationRelatedCategoryScore(product, cart, settings) {
    if (!settings || settings.filledPreferCrossCategoryRelated === false) return 0;

    const productCategory = recommendationProductCategoryKey(product);
    if (!productCategory) return 0;

    const cartCategories = cartPaidProductCategoryKeys(cart);
    return cartCategories.has(productCategory) ? -24 : 0;
  }

  function recommendationPassesRelatedPriceCeiling(product, cart, settings) {
    const maxPercent = Math.max(0, Number(settings && settings.filledRelatedMaxPricePercent || 0));
    if (!maxPercent) return true;

    const priceCents = recommendationProductPriceCents(product);
    if (!priceCents) return true;

    const cfg = parseConfig();
    const paidSpendCents = Math.max(0, eligibleSpend(cart, cfg));
    if (!paidSpendCents) return true;

    return priceCents <= (paidSpendCents * (maxPercent / 100));
  }

  function selectDiversifiedRelatedRecommendations(products, cart, settings, excludedIds, existingProducts, maxProducts) {
    const existing = uniqueAvailableProducts(existingProducts || [], excludedIds, maxProducts);
    if (existing.length >= maxProducts) return existing;

    const candidates = Array.isArray(products) ? products : [];
    if (!settings || settings.filledPreferCrossCategoryRelated === false) {
      return uniqueAvailableProducts([...existing, ...candidates], excludedIds, maxProducts);
    }

    const cartCategories = cartPaidProductCategoryKeys(cart);
    const crossCategory = [];
    const sameCategory = [];

    candidates.forEach((product) => {
      const category = recommendationProductCategoryKey(product);
      if (category && cartCategories.has(category)) {
        sameCategory.push(product);
      } else {
        crossCategory.push(product);
      }
    });

    // V11.2: automatic related fallbacks diversify away from categories already
    // present in the paid cart. Intentional Shopify complementary choices remain
    // untouched because this selector is only applied to the related fallback.
    let selected = uniqueAvailableProducts(
      [...existing, ...crossCategory],
      excludedIds,
      maxProducts
    );
    if (selected.length >= maxProducts) return selected;

    // Allow at most one related recommendation from each category already in
    // the cart before considering additional same-category fillers.
    const firstPerCartCategory = [];
    const overflowSameCategory = [];
    const usedCartCategories = new Set();

    sameCategory.forEach((product) => {
      const category = recommendationProductCategoryKey(product);
      if (category && !usedCartCategories.has(category)) {
        usedCartCategories.add(category);
        firstPerCartCategory.push(product);
      } else {
        overflowSameCategory.push(product);
      }
    });

    selected = uniqueAvailableProducts(
      [...selected, ...firstPerCartCategory],
      excludedIds,
      maxProducts
    );
    if (selected.length >= maxProducts) return selected;

    // V11.3 quality floor: do not keep adding repetitive same-category items
    // merely to reach the configured carousel maximum. Once we have the
    // configured minimum number of strong automatic recommendations, stop.
    // If we are below that minimum, allow overflow same-category products only
    // until the minimum is reached. Manual fallback products can still fill
    // remaining slots later because they are intentional merchandising choices.
    const overflowTarget = Math.min(
      maxProducts,
      Math.max(1, Number(settings && settings.filledSameCategoryOverflowMin || 4))
    );

    if (selected.length >= overflowTarget) return selected;

    return uniqueAvailableProducts(
      [...selected, ...overflowSameCategory],
      excludedIds,
      overflowTarget
    );
  }

  function recommendationPopularityScore(product, settings) {
    const boost = Math.max(0, Math.min(50, Number(settings && settings.filledPopularityBoost || 0)));
    if (!boost) return 0;

    const popularIds = settings && Array.isArray(settings.filledPopularityProductIds)
      ? settings.filledPopularityProductIds
      : [];
    if (!popularIds.length) return 0;

    const productId = Number(product && product.id || 0);
    if (!productId) return 0;

    const index = popularIds.indexOf(productId);
    if (index < 0) return 0;

    // V11.4: position #1 receives the full configured boost.
    // The influence decreases linearly through the first 50 products.
    const rankWeight = Math.max(
      0,
      1 - (index / Math.max(1, popularIds.length))
    );

    return boost * rankWeight;
  }

  function rankRecommendationGroups(groups, cart, settings, intent) {
    const candidates = new Map();
    let sequence = 0;

    (Array.isArray(groups) ? groups : []).forEach((group) => {
      const seedIndex = Math.max(0, Number(group && group.seedIndex || 0));
      const products = Array.isArray(group && group.products) ? group.products : [];

      products.forEach((product, position) => {
        const id = Number(product && product.id || 0);
        const handle = String(product && product.handle || '').trim().toLowerCase();
        const key = id ? `id:${id}` : `handle:${handle}`;
        if ((!id && !handle) || !key) return;

        let candidate = candidates.get(key);
        if (!candidate) {
          candidate = {
            product,
            seedHits: 0,
            bestSeedIndex: seedIndex,
            bestPosition: position,
            sequence: sequence++
          };
          candidates.set(key, candidate);
        }

        candidate.seedHits += 1;
        candidate.bestSeedIndex = Math.min(candidate.bestSeedIndex, seedIndex);
        candidate.bestPosition = Math.min(candidate.bestPosition, position);
      });
    });

    const rewardContext = recommendationRewardContext(cart, settings);

    return Array.from(candidates.values())
      .map((candidate) => {
        const consensusScore = Math.max(0, candidate.seedHits - 1) * 20;
        const seedScore = candidate.bestSeedIndex === 0 ? 8 : 4;
        const shopifyRankScore = Math.max(0, 10 - (candidate.bestPosition * 2));
        const rewardScore = recommendationRewardFitScore(candidate.product, rewardContext);
        const priceScore = recommendationAddOnPriceScore(candidate.product, cart);
        const categoryScore = intent === 'related'
          ? recommendationRelatedCategoryScore(candidate.product, cart, settings)
          : 0;
        const popularityScore = recommendationPopularityScore(candidate.product, settings);

        return {
          ...candidate,
          score: consensusScore + seedScore + shopifyRankScore + rewardScore + priceScore + categoryScore + popularityScore
        };
      })
      .sort((a, b) => (b.score - a.score) || (a.sequence - b.sequence))
      .map((candidate) => candidate.product);
  }

  async function fetchRecommendationGroups(seedIds, maxProducts, intent) {
    const results = await Promise.allSettled(
      seedIds.map((productId) => fetchShopifyRecommendations(productId, maxProducts, intent))
    );

    return results.map((result, seedIndex) => ({
      seedIndex,
      products: result.status === 'fulfilled' ? result.value : []
    }));
  }

  async function fetchSmartFilledRecommendations(cart, settings, excludedIds, maxProducts) {
    const seedIds = cartRecommendationSeedProductIds(cart, settings.filledSeedLimit);
    if (!seedIds.length) return [];

    const complementaryGroups = await fetchRecommendationGroups(
      seedIds,
      maxProducts,
      'complementary'
    );
    const rankedComplementary = rankRecommendationGroups(
      complementaryGroups,
      cart,
      settings,
      'complementary'
    );

    let recommendedProducts = uniqueAvailableProducts(
      rankedComplementary,
      excludedIds,
      maxProducts
    );

    if (recommendedProducts.length >= maxProducts) return recommendedProducts;

    const relatedFetchLimit = Math.min(10, Math.max(maxProducts * 2, maxProducts));
    const relatedGroups = await fetchRecommendationGroups(
      seedIds,
      relatedFetchLimit,
      'related'
    );
    const rankedRelated = rankRecommendationGroups(
      relatedGroups,
      cart,
      settings,
      'related'
    );
    const guardedRelated = rankedRelated.filter((product) =>
      recommendationPassesRelatedPriceCeiling(product, cart, settings)
    );

    recommendedProducts = selectDiversifiedRelatedRecommendations(
      guardedRelated,
      cart,
      settings,
      excludedIds,
      recommendedProducts,
      maxProducts
    );

    return recommendedProducts;
  }

  function latestViewedProductId() {
    const entry = recentViewEntries()[0];
    return Number(entry && entry.id || 0);
  }

  function recommendationSlot(kind, state) {
    return document.querySelector(
      `.mmx-cart-drawer-shell [data-mmx-recommendation-slot][data-mmx-recommendation-kind="${kind}"][data-mmx-recommendation-state="${state}"]`
    );
  }

  function hideRecommendationSlot(slot) {
    if (!slot) return;
    slot.hidden = true;
    slot.innerHTML = '';
    slot.removeAttribute('data-mmx-loaded');
  }

  function showRecommendationLoading(slot, heading, visibleCount, state, kind, options) {
    if (!slot) return;

    slot.hidden = false;
    slot.innerHTML = addonShellHtml(
      heading,
      '<div class="mmx-cart-addons__page" data-mmx-addons-page><div class="mmx-cart-addons__loading">Loading products…</div></div>',
      visibleCount,
      { state, kind, ...(options || {}) }
    );
    syncAddonControls(slot);
  }

  function showRecommendationProducts(slot, heading, products, visibleCount, state, kind, options) {
    if (!slot || !products.length) {
      hideRecommendationSlot(slot);
      return;
    }

    slot.hidden = false;
    slot.innerHTML = addonShellHtml(
      heading,
      addonPagesHtml(products, visibleCount),
      visibleCount,
      { state, kind, ...(options || {}) }
    );
    slot.setAttribute('data-mmx-loaded', 'true');
    syncAddonControls(slot);
  }

  async function renderFirstVisitEmptyCartDiscovery(requestId, settings, cartIds) {
    const youMayLike = settings && settings.youMayLike ? settings.youMayLike : {};
    const bestsellersSlot = recommendationSlot('first-visit-bestsellers', 'empty');
    const newArrivalsSlot = recommendationSlot('first-visit-new-arrivals', 'empty');

    hideRecommendationSlot(bestsellersSlot);
    hideRecommendationSlot(newArrivalsSlot);

    if (youMayLike.firstVisitEnabled === false) return false;

    const bestsellerDescriptors = Array.isArray(youMayLike.firstVisitBestsellers)
      ? youMayLike.firstVisitBestsellers
      : [];
    const newArrivalDescriptors = Array.isArray(youMayLike.firstVisitNewArrivals)
      ? youMayLike.firstVisitNewArrivals
      : [];

    if (!bestsellerDescriptors.length && !newArrivalDescriptors.length) return false;

    const visibleCount = 3;
    const maxProducts = 6;

    if (bestsellerDescriptors.length) {
      showRecommendationLoading(
        bestsellersSlot,
        'Bestsellers',
        visibleCount,
        'empty',
        'you-may-like'
      );
    }

    if (newArrivalDescriptors.length) {
      showRecommendationLoading(
        newArrivalsSlot,
        'New Arrivals',
        visibleCount,
        'empty',
        'you-may-like'
      );
    }

    let bestsellerProducts = [];
    let newArrivalProducts = [];

    if (bestsellerDescriptors.length) {
      try {
        const fetchedBestsellers = await fetchDescriptorProducts(
          bestsellerDescriptors,
          maxProducts
        );

        bestsellerProducts = uniqueAvailableProducts(
          fetchedBestsellers,
          cartIds,
          maxProducts
        );
      } catch (error) {
        console.warn('[MMX Cart] First-visit Bestsellers failed.', error);
        bestsellerProducts = [];
      }
    }

    if (requestId !== recommendationRenderRequest) return false;

    const bestsellerIds = new Set(
      bestsellerProducts
        .map((product) => Number(product && product.id || 0))
        .filter(Boolean)
    );

    const newArrivalExcludedIds = new Set([
      ...(cartIds instanceof Set ? cartIds : []),
      ...bestsellerIds
    ]);

    if (newArrivalDescriptors.length) {
      try {
        const fetchedNewArrivals = await fetchDescriptorProducts(
          newArrivalDescriptors,
          maxProducts
        );

        newArrivalProducts = uniqueAvailableProducts(
          fetchedNewArrivals,
          newArrivalExcludedIds,
          maxProducts
        );
      } catch (error) {
        console.warn('[MMX Cart] First-visit New Arrivals failed.', error);
        newArrivalProducts = [];
      }
    }

    if (requestId !== recommendationRenderRequest) return false;

    showRecommendationProducts(
      bestsellersSlot,
      'Bestsellers',
      bestsellerProducts,
      visibleCount,
      'empty',
      'you-may-like'
    );

    showRecommendationProducts(
      newArrivalsSlot,
      'New Arrivals',
      newArrivalProducts,
      visibleCount,
      'empty',
      'you-may-like'
    );

    return Boolean(bestsellerProducts.length || newArrivalProducts.length);
  }

  async function updateRecommendationSections(cart) {
    const requestId = ++recommendationRenderRequest;
    const hasItems = Boolean(cart && Number(cart.item_count || 0) > 0);
    const state = hasItems ? 'filled' : 'empty';
    const otherState = hasItems ? 'empty' : 'filled';
    const settings = recommendationSettings();
    const cartIds = cartRecommendationProductIds(cart);

    ['recently-viewed', 'you-may-like', 'first-visit-bestsellers', 'first-visit-new-arrivals'].forEach((kind) => {
      hideRecommendationSlot(recommendationSlot(kind, otherState));
    });

    const recentSlot = recommendationSlot('recently-viewed', state);
    const youMayLikeSlot = recommendationSlot('you-may-like', state);
    const firstVisitBestsellersSlot = recommendationSlot('first-visit-bestsellers', 'empty');
    const firstVisitNewArrivalsSlot = recommendationSlot('first-visit-new-arrivals', 'empty');

    const firstVisitEmptyCart = state === 'empty' && recentViewEntries().length === 0;

    if (firstVisitEmptyCart) {
      const firstVisitRendered = await renderFirstVisitEmptyCartDiscovery(
        requestId,
        settings,
        cartIds
      );

      if (requestId !== recommendationRenderRequest) return;

      if (firstVisitRendered) {
        hideRecommendationSlot(recentSlot);
        hideRecommendationSlot(youMayLikeSlot);
        return;
      }
    } else {
      hideRecommendationSlot(firstVisitBestsellersSlot);
      hideRecommendationSlot(firstVisitNewArrivalsSlot);
    }
    const recentEnabled = state === 'empty'
      ? settings.recentlyViewed.emptyEnabled
      : settings.recentlyViewed.filledEnabled;
    const recommendedEnabled = state === 'empty'
      ? settings.youMayLike.emptyEnabled
      : settings.youMayLike.filledEnabled;

    const recentVisibleCount = state === 'empty'
      ? settings.recentlyViewed.emptyVisibleCount
      : settings.recentlyViewed.filledVisibleCount;
    const recentMaxProducts = state === 'empty'
      ? settings.recentlyViewed.emptyMaxProducts
      : settings.recentlyViewed.filledMaxProducts;
    const recommendedVisibleCount = state === 'empty'
      ? settings.youMayLike.emptyVisibleCount
      : settings.youMayLike.filledVisibleCount;
    const recommendedMaxProducts = state === 'empty'
      ? settings.youMayLike.emptyMaxProducts
      : settings.youMayLike.filledMaxProducts;
    const recentShellOptions = state === 'filled' && settings.recentlyViewed.filledCollapsed
      ? { collapsible: true, collapsed: true }
      : {};

    let recentProducts = [];

    if (!recentEnabled || !recentViewEntries().length) {
      hideRecommendationSlot(recentSlot);
    } else {
      showRecommendationLoading(
        recentSlot,
        settings.recentlyViewed.heading,
        recentVisibleCount,
        state,
        'recently-viewed',
        recentShellOptions
      );

      try {
        recentProducts = await fetchRecentlyViewedProducts(
          recentMaxProducts,
          cartIds
        );
      } catch (error) {
        console.warn('[MMX Cart] Recently viewed products failed.', error);
        recentProducts = [];
      }

      if (requestId !== recommendationRenderRequest) return;

      showRecommendationProducts(
        recentSlot,
        settings.recentlyViewed.heading,
        recentProducts,
        recentVisibleCount,
        state,
        'recently-viewed',
        recentShellOptions
      );
    }

    if (!recommendedEnabled) {
      hideRecommendationSlot(youMayLikeSlot);
      return;
    }

    const recentIds = new Set(recentProducts.map((product) => Number(product && product.id || 0)).filter(Boolean));
    const excludedIds = new Set([...cartIds, ...recentIds]);
    const heading = state === 'empty'
      ? settings.youMayLike.emptyHeading
      : settings.youMayLike.filledHeading;
    const fallback = state === 'empty'
      ? settings.youMayLike.emptyFallback
      : settings.youMayLike.filledFallback;
    const seedProductId = state === 'empty'
      ? latestViewedProductId()
      : 0;
    const filledSeedProductIds = state === 'filled'
      ? cartRecommendationSeedProductIds(cart, settings.youMayLike.filledSeedLimit)
      : [];

    if (state === 'empty' && !seedProductId && !fallback.length) {
      hideRecommendationSlot(youMayLikeSlot);
      return;
    }

    if (state === 'filled' && !filledSeedProductIds.length && !fallback.length) {
      hideRecommendationSlot(youMayLikeSlot);
      return;
    }

    showRecommendationLoading(
      youMayLikeSlot,
      heading,
      recommendedVisibleCount,
      state,
      'you-may-like'
    );

    let recommendedProducts = [];

    if (settings.youMayLike.automatic) {
      try {
        if (state === 'filled' && filledSeedProductIds.length) {
          recommendedProducts = await fetchSmartFilledRecommendations(
            cart,
            settings.youMayLike,
            excludedIds,
            recommendedMaxProducts
          );
        } else if (state === 'empty' && seedProductId) {
          // Preserve the proven empty-cart behavior: the most recently viewed
          // product seeds Shopify related recommendations.
          const automaticProducts = await fetchShopifyRecommendations(
            seedProductId,
            recommendedMaxProducts,
            'related'
          );

          recommendedProducts = uniqueAvailableProducts(
            automaticProducts,
            excludedIds,
            recommendedMaxProducts
          );
        }
      } catch (error) {
        console.warn('[MMX Cart] Shopify smart recommendations failed.', error);
      }
    }

    if (recommendedProducts.length < recommendedMaxProducts && fallback.length) {
      try {
        const fallbackProducts = await fetchDescriptorProducts(
          fallback,
          recommendedMaxProducts
        );
        const combined = [...recommendedProducts, ...fallbackProducts];

        recommendedProducts = uniqueAvailableProducts(
          combined,
          excludedIds,
          recommendedMaxProducts
        );
      } catch (error) {
        console.warn('[MMX Cart] Recommendation fallback failed.', error);
      }
    }

    if (requestId !== recommendationRenderRequest) return;

    showRecommendationProducts(
      youMayLikeSlot,
      heading,
      recommendedProducts,
      recommendedVisibleCount,
      state,
      'you-may-like'
    );
  }




  function renderFastCart(cart, options) {
    const opts = options || {};
    if (!cart) return;
    closeDesktopVariantMenu({ focus: false });
    cartState = cart;
    if (typeof window.__MMXCartExpirySyncCartState === 'function') {
      window.__MMXCartExpirySyncCartState(cart);
    }
    const list = document.querySelector('.mmx-cart-drawer-shell .cart-item-list__body');
    if (list) {
      if (opts.loading && (!cart.items || !cart.items.length)) {
        list.innerHTML = `<div class="mmx-cart-loading-row"><span class="mmx-spinner" aria-hidden="true"></span><span>${escapeHtml(opts.message || 'Updating cart…')}</span></div>`;
      } else {
        list.innerHTML = displayCartEntries(cart).map((entry) => renderCartItem(entry.item, entry.lineIndex)).join('');
      }
    }
    setDrawerState(cart, opts);
    updateCartCount(cart);
    updateCartDiscounts(cart);
    applyDiscountFormState();
    applyFooterUtilityState({ focus: false });
    renderAll(cart);
    updateRecommendationSections(cart);
    syncAddonControls(document);
    hydrateCartVariantEditors(cart);
    enhanceCompareAtPrices(cart);
    enhanceGiftWorthPrices();
    renderInventoryNotices(cart);
    syncPreorderCheckoutGate(cart);
    scheduleInventoryRefresh(cart, opts.loading ? 350 : 160);
    clearCartItemWorking();


    if (Number(cart.item_count || 0) > 0) {
      reserveAcceleratedCheckoutSpace(7000);
      if (!opts.loading) scheduleAcceleratedCheckoutRefresh(260);
    } else {
      releaseAcceleratedCheckoutSpace(0);
    }

    if (!opts.loading) stopFastCartFeedback();
  }

  function projectedCartAfterChange(cart, body) {
    if (!cart || !Array.isArray(cart.items) || !body || !body.id) return cart;
    const targetId = String(body.id);
    const nextQuantity = sanitizeCartQuantity(body.quantity, 0, { allowZero: true });
    const projected = Object.assign({}, cart);
    projected.items = cart.items.map((item) => Object.assign({}, item));

    projected.items = projected.items.reduce((items, item) => {
      if (String(item.key) !== targetId) {
        items.push(item);
        return items;
      }

      if (nextQuantity <= 0) return items;

      const currentQuantity = Math.max(1, Number(item.quantity || 1));
      const finalUnit = Number(item.final_line_price || 0) / currentQuantity;
      const originalUnit = Number(item.original_line_price || item.final_line_price || 0) / currentQuantity;
      item.quantity = nextQuantity;
      item.final_line_price = Math.round(finalUnit * nextQuantity);
      item.original_line_price = Math.round(originalUnit * nextQuantity);
      items.push(item);
      return items;
    }, []);

    projected.item_count = projected.items.reduce((count, item) => count + Number(item.quantity || 0), 0);
    projected.total_price = projected.items.reduce((total, item) => total + Number(item.final_line_price || 0), 0);
    return projected;
  }

  function desiredGiftTierIdsForCart(cart, cfg) {
    const desired = new Set();
    if (!cart || !cfg || !Array.isArray(cfg.tiers)) return desired;

    const giftTiers = cfg.tiers.filter((tier) => tier && tier.autoAddGift && Number(tier.giftVariantId) > 0);
    if (!giftTiers.length) return desired;

    const spend = eligibleSpend(cart, cfg);
    const qualified = giftTiers.filter((tier) => spend >= Number(tier.amountCents || 0));

    if (cfg.giftRewardBehavior === 'highest') {
      const highest = qualified.reduce((selected, tier) => (
        !selected || Number(tier.amountCents || 0) > Number(selected.amountCents || 0) ? tier : selected
      ), null);
      if (highest) desired.add(String(highest.id));
      return desired;
    }

    qualified.forEach((tier) => desired.add(String(tier.id)));
    return desired;
  }

  function managedGiftLinesToRemoveAfterChange(cart, cfg, body) {
    if (!cart || !cfg || !Array.isArray(cart.items) || !Array.isArray(cfg.tiers)) return [];

    const projected = projectedCartAfterChange(cart, body);
    const desiredIds = desiredGiftTierIdsForCart(projected, cfg);
    const giftTiersByVariant = new Map();
    cfg.tiers.forEach((tier) => {
      if (tier && tier.autoAddGift && Number(tier.giftVariantId) > 0) {
        giftTiersByVariant.set(Number(tier.giftVariantId), String(tier.id));
      }
    });

    return cart.items.filter((item) => {
      if (!isGiftItem(item)) return false;
      const currentTierId = giftTierId(item) || giftTiersByVariant.get(Number(item.variant_id)) || '';
      return currentTierId && !desiredIds.has(String(currentTierId));
    });
  }

  function preemptivelySettleGiftRemoval(body, cfg) {
    const giftLines = managedGiftLinesToRemoveAfterChange(cartState, cfg, body);
    if (!giftLines.length) return;
    giftLines.forEach((line) => optimisticRemoveLine(line.key, 'Removing free gift…'));
    markGiftLinesWorking(giftLines, 'Removing free gift…');
  }


  function optimisticRemoveLine(lineKey, message) {
    const wrapper = Array.from(document.querySelectorAll('[data-mmx-cart-line-wrapper]')).find((node) => node.getAttribute('data-mmx-line-key') === String(lineKey));
    if (wrapper) {
      wrapper.classList.add('mmx-cart-line-removing');
      const item = wrapper.querySelector('.cart-item');
      if (item) markCartItemWorking(item, message || 'Removing…');
      window.setTimeout(() => {
        if (wrapper && wrapper.parentNode && wrapper.classList.contains('mmx-cart-line-removing')) {
          wrapper.style.display = 'none';
        }
      }, 120);
    }
  }

  const activeCartLineMutations = new Set();
  let cartMutationQueue = Promise.resolve();

  function isInvalidCartLineError(error) {
    const message = String(error && error.message || error || '').trim();
    return /no valid id or line parameter|invalid (?:id|line)|line (?:item )?(?:does not exist|not found)|unable to find.*(?:id|line)/i.test(message);
  }

  function cartLineMutationIsLocked(lineKey, wrapper) {
    const key = String(lineKey || '');
    if (!key) return false;
    return activeCartLineMutations.has(key)
      || Boolean(wrapper && wrapper.getAttribute('data-mmx-line-mutation-locked') === 'true');
  }

  function lockCartLineMutation(lineKey, wrapper) {
    const key = String(lineKey || '');
    if (!key || cartLineMutationIsLocked(key, wrapper)) return false;

    activeCartLineMutations.add(key);
    if (wrapper) wrapper.setAttribute('data-mmx-line-mutation-locked', 'true');
    return true;
  }

  function unlockCartLineMutation(lineKey) {
    const key = String(lineKey || '');
    if (!key) return;

    activeCartLineMutations.delete(key);
    document.querySelectorAll('.mmx-cart-drawer-shell [data-mmx-cart-line-wrapper][data-mmx-line-key]').forEach((wrapper) => {
      if (wrapper.getAttribute('data-mmx-line-key') === key) {
        wrapper.removeAttribute('data-mmx-line-mutation-locked');
      }
    });
  }

  function enqueueCartLineMutation(lineKey, wrapper, task) {
    if (!lockCartLineMutation(lineKey, wrapper)) return false;

    enqueueCartMutation(async () => {
      try {
        return await task();
      } finally {
        unlockCartLineMutation(lineKey);
      }
    });

    return true;
  }

  function enqueueCartMutation(task) {
    cartMutationQueue = cartMutationQueue
      .then(task)
      .catch((error) => {
        if (isInvalidCartLineError(error)) {
          console.warn('[MMX Cart] Ignored a stale cart-line action and reconciled the drawer.', error);
          return fetchCart(true).then((cart) => {
            if (cart) renderFastCart(cart, { source: 'stale-line-recovery' });
            return cart;
          });
        }

        console.error('[MMX Cart] Cart mutation failed.', error);
        if (!(error && error.mmxSuppressError)) {
          showCartError(error.message || 'Cart update failed. Please try again.');
        }

        if (error && error.mmxCartRecovered) return cartState;

        return fetchCart(true).then((cart) => {
          if (cart) renderFastCart(cart, { source: 'mutation-error' });
          return cart;
        });
      })
      .finally(() => {
        // Queue-level cleanup is intentionally redundant with each mutation's
        // own finally block. It is the last defence against a disabled checkout.
        setSyncingState(false);
        clearCartItemWorking();
        stopFastCartFeedback();
      });
    return cartMutationQueue;
  }

  function showCartError(message) {
    const drawer = document.querySelector('.mmx-cart-drawer-shell .cart-drawer');
    if (!drawer) return;
    let error = drawer.querySelector('.mmx-cart-error');
    if (!error) {
      error = document.createElement('div');
      error.className = 'mmx-cart-error cart-drawer__content-item';
      const header = drawer.querySelector('.cart-drawer__header');
      if (header && header.parentNode) header.parentNode.insertBefore(error, header.nextSibling);
    }
    error.textContent = message || 'Cart update failed. Please try again.';
    error.hidden = false;
    window.setTimeout(() => { error.hidden = true; }, 4500);
  }

  async function applyCartChange(body, options) {
    const opts = options || {};
    const cfg = parseConfig();
    const safeBody = Object.assign({}, body, {
      quantity: sanitizeCartQuantity(body && body.quantity, 0, { allowZero: true })
    });

    if (opts.lineKey && !cartItemByKey(opts.lineKey, cartState)) {
      const currentCart = await fetchCart(true);
      if (currentCart) {
        cartState = currentCart;
        renderFastCart(currentCart, { source: 'cart-change-preflight' });
      }

      // The item has already disappeared or its Shopify line key has changed.
      // Treat the requested action as settled instead of sending a stale key.
      if (!cartItemByKey(opts.lineKey, currentCart || cartState)) {
        return currentCart || cartState;
      }
    }

    if (cfg) preemptivelySettleGiftRemoval(safeBody, cfg);
    if (opts.lineKey && safeBody.quantity === 0) optimisticRemoveLine(opts.lineKey);

    setSyncingState(true);

    try {
      const cart = await postJson(routes.change, safeBody);
      cartState = cart;
      let finalCart = cart;

      if (cfg) {
        // For remove/quantity changes, settle managed gifts before rendering the final state.
        // This avoids the awkward intermediate state where a free gift appears alone for a few seconds.
        showPendingRewardRows(cart, cfg, 'Updating rewards…');
        finalCart = await syncGifts(cart, cfg, { renderDuring: false }) || cart;
      }

      cartState = finalCart || cart;
      renderFastCart(cartState, { source: 'cart-change-settled' });
      return cartState;
    } catch (error) {
      // Shopify remains authoritative. Restore the last accepted cart immediately
      // so an inventory rejection never leaves an optimistic quantity on screen.
      const recoveredCart = await fetchCart(true);
      if (recoveredCart) {
        cartState = recoveredCart;
        renderFastCart(recoveredCart, { source: 'cart-change-error-recovery' });
      } else if (cartState) {
        renderFastCart(cartState, { source: 'cart-change-error-fallback' });
      }

      // A stale key usually means the line was already removed or replaced by
      // another successful cart refresh. The authoritative cart above is the
      // correct outcome, so do not expose Shopify's raw API message to shoppers.
      if (isInvalidCartLineError(error)) {
        return recoveredCart || cartState;
      }

      const maxQuantity = Math.max(0, Math.floor(Number(opts.maxQuantity || 0)));
      const requestedQuantity = Math.max(0, Math.floor(Number(safeBody.quantity || 0)));
      const looksLikeInventoryError = /available|inventory|stock|quantity|add more|sold out/i.test(String(error && error.message || ''));
      const hasKnownInventoryLimit = Boolean(opts.inventoryLimitEnforced);
      const friendlyError = new Error(
        hasKnownInventoryLimit && maxQuantity > 0 && (requestedQuantity > maxQuantity || looksLikeInventoryError)
          ? inventoryLimitMessage(maxQuantity, false)
          : (error && error.message) || 'Cart update failed. Please try again.'
      );
      friendlyError.mmxCartRecovered = true;
      throw friendlyError;
    } finally {
      setSyncingState(false);
      clearCartItemWorking();
      stopFastCartFeedback();
    }
  }

  function cartQuantityForVariant(cart, variantId) {
    const id = Number(variantId || 0);
    const items = Array.isArray(cart && cart.items) ? cart.items : [];

    return items.reduce((total, item) => {
      if (!item || Number(item.variant_id || 0) !== id) return total;
      return total + Math.max(0, Math.floor(Number(item.quantity || 0)));
    }, 0);
  }

  function inventoryLineForVariant(variantId, snapshot) {
    const id = Number(variantId || 0);
    const activeSnapshot = snapshot || activeInventorySnapshot();
    const lines = activeSnapshot && Array.isArray(activeSnapshot.lines)
      ? activeSnapshot.lines
      : [];

    return lines.find((line) => (
      Number(line && line.variantId || 0) === id
    )) || null;
  }

  function addInventoryLimitMessage(available, inCart, remaining) {
    const stock = Math.max(0, Math.floor(Number(available || 0)));
    const existing = Math.max(0, Math.floor(Number(inCart || 0)));
    const left = Math.max(0, Math.floor(Number(remaining || 0)));

    if (stock <= 0) {
      return 'This variant is sold out and was not added to your cart.';
    }

    if (left <= 0) {
      const stockNoun = stock === 1 ? 'item is' : 'items are';
      const cartNoun = existing === 1 ? 'it is' : 'they are';
      return `Only ${stock} ${stockNoun} available, and ${cartNoun} already in your cart.`;
    }

    const leftNoun = left === 1 ? 'item' : 'items';
    const cartNoun = existing === 1 ? 'item is' : 'items are';
    return `Only ${left} more ${leftNoun} can be added because ${existing} ${cartNoun} already in your cart.`;
  }

  async function preflightAddInventory(formData) {
    const variantId = Number(formData && formData.get('id') || 0);
    const requestedQuantity = sanitizeCartQuantity(
      formData && formData.get('quantity'),
      1,
      { allowZero: false }
    );

    if (!variantId || requestedQuantity <= 0) {
      return { blocked: false, cart: cartState };
    }

    const cart = await fetchCart(true);
    const snapshot = await refreshInventorySnapshot(cart);
    const line = inventoryLineForVariant(variantId, snapshot);

    if (
      !line
      || !line.tracked
      || String(line.policy || 'deny').toLowerCase() !== 'deny'
    ) {
      return { blocked: false, cart };
    }

    const available = Math.max(
      0,
      Math.floor(Number(line.quantity || 0))
    );
    const inCart = cartQuantityForVariant(cart, variantId);
    const remaining = Math.max(0, available - inCart);

    if (requestedQuantity <= remaining) {
      return { blocked: false, cart };
    }

    return {
      blocked: true,
      cart,
      available,
      inCart,
      remaining,
      message: addInventoryLimitMessage(
        available,
        inCart,
        remaining
      )
    };
  }

  async function handleAddToCartForm(form, submitter) {
    if (!form) return;

    const returnRecommendationAddToTop = Boolean(
      isDrawerRecommendationForm(form)
      && mmxCartDrawerIsOpen()
    );

    const formData = new FormData(form);
    const cfg = parseConfig();
    stampRecommendationAttributionProperties(form, formData);
    stampBnplPropertiesForForm(form, formData, cfg);
    if (submitter && submitter.name && !formData.has(submitter.name)) {
      formData.append(submitter.name, submitter.value || '');
    }

    setAddonButtonState(submitter, 'adding');

    const inventoryCheck = await preflightAddInventory(formData);

    if (inventoryCheck.blocked) {
      if (inventoryCheck.cart) {
        cartState = inventoryCheck.cart;
        renderFastCart(inventoryCheck.cart, {
          source: 'add-inventory-preflight'
        });
      }

      document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', {
        bubbles: true,
        cancelable: false,
        detail: { mmxFastOpen: true }
      }));

      window.setTimeout(() => {
        showCartError(inventoryCheck.message);
      }, 80);

      setAddonButtonState(submitter, 'reset');
      return null;
    }

    setSyncingState(true);

    const addResponse = await fetch(routes.add, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        [MMX_INTERNAL_CART_REQUEST_HEADER]: '1'
      },
      credentials: 'same-origin',
      body: formData
    }).then(async (response) => {
      if (!response.ok) {
        let message = `Add to cart failed with status ${response.status}`;
        try {
          const error = await response.json();
          message = error.description || error.message || message;
        } catch (parseError) {}
        throw new Error(message);
      }
      return response.json();
    });

    // Option B: only open on a successful Shopify add; never show a phantom item first.
    renderOptimisticAdd(form, formData);
    openDrawerImmediately('Updating your cart…');

    // Fetch the authoritative cart immediately after Shopify accepts the add.
    const cart = await fetchCart(true);
    renderFastCart(cart, { source: 'add-main' });

    if (cfg) {
      showPendingRewardRows(cart, cfg, 'Adding your free gift…');
      // Keep the paid item visible first, then settle the free gift state. This avoids a long blank/loading drawer.
      const synced = await syncGifts(cart, cfg, { renderDuring: false });
      if (synced) renderFastCart(synced, { source: 'add-sync' });
    }

    if (returnRecommendationAddToTop) {
      returnDrawerToRewardsAfterRecommendationAdd();
    }

    document.documentElement.classList.remove('mmx-cart-optimistic-open');
    setAddonButtonState(submitter, 'added');
    window.setTimeout(() => setAddonButtonState(submitter, 'reset'), 1100);
    setSyncingState(false);
    return addResponse;
  }

function addBusinessDays(startDate, businessDays) {
    const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    let remaining = Math.max(0, Number(businessDays || 0));
    while (remaining > 0) {
      date.setDate(date.getDate() + 1);
      const day = date.getDay();
      if (day !== 0 && day !== 6) remaining -= 1;
    }
    return date;
  }

  function formatDateRange(minDate, maxDate) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const sameMonth = minDate.getMonth() === maxDate.getMonth() && minDate.getFullYear() === maxDate.getFullYear();
    if (sameMonth) return `${minDate.getDate()} - ${maxDate.getDate()} ${months[maxDate.getMonth()]}`;
    return `${minDate.getDate()} ${months[minDate.getMonth()]} - ${maxDate.getDate()} ${months[maxDate.getMonth()]}`;
  }

  function updateDelivery(cfg) {
    if (!cfg || !cfg.delivery || !cfg.delivery.enabled) return;
    const today = new Date();
    const delivery = cfg.delivery;
    const standardRange = formatDateRange(
      addBusinessDays(today, delivery.standardMinDays),
      addBusinessDays(today, delivery.standardMaxDays)
    );
    const priorityRange = formatDateRange(
      addBusinessDays(today, delivery.priorityMinDays),
      addBusinessDays(today, delivery.priorityMaxDays)
    );

    document.querySelectorAll('[data-mmx-standard-delivery]').forEach((node) => {
      node.textContent = `Shipping/Collection: Receive by ${standardRange}`;
    });
    document.querySelectorAll('[data-mmx-priority-delivery]').forEach((node) => {
      node.textContent = `${delivery.priorityLabel}: Receive by ${priorityRange}`;
    });
  }

  function updateBnpl(cart, cfg) {
    if (!cart || !cfg || !cfg.bnpl || !cfg.bnpl.enabled) {
      setBnplVisibility(true);
      return;
    }

    const count = Math.max(1, Number(cfg.bnpl.installments || 4));
    const amount = Math.ceil(checkoutDisplayTotal(cart) / count);
    document.querySelectorAll('[data-mmx-bnpl-amount]').forEach((node) => {
      node.textContent = `${formatMoney(amount)}/mth`;
    });

    const excludedTags = normaliseTagList(cfg.bnpl.excludedTags || 'investmentbar');
    if (!excludedTags.length) {
      setBnplVisibility(false);
      return;
    }

    if (cartHasBnplExcludedProperty(cart) || cartContainsDomBnplExclusion(cart) || cartContainsBnplExcludedProduct(cart, cfg) || cartContainsBnplExcludedHandleOrSku(cart, cfg)) {
      setBnplVisibility(true);
      return;
    }

    const token = ++bnplCheckToken;

    // Keep the current BNPL layout stable while async checks run.
    // Confirmed excluded carts are already hidden by the immediate checks above.
    renderedSectionSaysBnplHidden().then((sectionHidden) => {
      if (token !== bnplCheckToken) return;

      if (sectionHidden !== null) {
        setBnplVisibility(Boolean(sectionHidden));
        return;
      }

      cartContainsBnplExcludedTag(cart, cfg).then((shouldHide) => {
        if (token !== bnplCheckToken) return;
        setBnplVisibility(Boolean(shouldHide));
      }).catch((error) => {
        console.warn('[MMX Cart] Unable to check BNPL product tag exclusions.', error);
        if (token === bnplCheckToken) setBnplVisibility(false);
      });
    }).catch((error) => {
      console.warn('[MMX Cart] Unable to render cart section for BNPL tag check. Falling back to product JSON.', error);

      cartContainsBnplExcludedTag(cart, cfg).then((shouldHide) => {
        if (token !== bnplCheckToken) return;
        setBnplVisibility(Boolean(shouldHide));
      }).catch((fallbackError) => {
        console.warn('[MMX Cart] Unable to check BNPL product tag exclusions.', fallbackError);
        if (token === bnplCheckToken) setBnplVisibility(false);
      });
    });
  }

  function updateCheckoutTotal(cart) {
    if (!cart) return;
    const displayTotal = checkoutDisplayTotal(cart);
    document.querySelectorAll('[data-mmx-checkout-total]').forEach((node) => {
      node.textContent = formatMoney(displayTotal);
    });
  }

  function renderAll(cart) {
    const cfg = parseConfig();
    if (!cart || !cfg) return;

    const safeRender = (label, callback) => {
      try {
        callback();
      } catch (error) {
        console.error(`[MMX Cart] ${label} update failed.`, error);
      }
    };

    safeRender('Spend tiers', () => {
      document.querySelectorAll('mmx-spend-tiers').forEach((element) => {
        if (typeof element.render === 'function') {
          element.render(cart, cfg);
          mmxUpdatePdpSpendTierPreview(element, cart, cfg);
        }
      });
    });
    safeRender('Delivery estimates', () => updateDelivery(cfg));
    safeRender('BNPL', () => updateBnpl(cart, cfg));
    safeRender('Checkout total', () => updateCheckoutTotal(cart));
  }

  
async function refresh(force) {
    const cfg = parseConfig();
    try {
      const cart = await fetchCart(Boolean(force));
      if (!cart) return;
      if (Date.now() < addPendingUntil && Number(cart.item_count || 0) === 0) {
        renderFastCart(cart, { loading: true, message: 'Adding to cart…', source: 'add-pending' });
        scheduleRefresh(true, 650);
        return;
      }
      addPendingUntil = 0;
      renderFastCart(cart, { source: 'refresh' });
      if (cfg) await syncGifts(cart, cfg, { renderDuring: true });
    } finally {
      if (!syncingGift) setSyncingState(false);
      clearCartItemWorking();
      stopFastCartFeedback();
    }
  }

  function scheduleRefresh(force, delay) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(() => refresh(force), Number(delay || 0));
  }

  mmxInstallExternalCartMutationObserver();
  setVariantDrawerInteractionState(false);

  function isAddToCartForm(form) {
    if (!form || !form.matches) return false;
    const action = String(form.getAttribute('action') || '');
    const hasAddAction = action.indexOf('/cart/add') >= 0 || action.indexOf('cart/add') >= 0;
    return hasAddAction || form.classList.contains('product-purchase-form') || Boolean(form.querySelector('[name="id"]'));
  }

  document.addEventListener('click', (event) => {
    const closeControl = event.target.closest
      && event.target.closest(
        '[data-mmx-mobile-variant-close], [data-mmx-mobile-variant-backdrop]'
      );

    if (closeControl) {
      event.preventDefault();
      event.stopPropagation();
      closeDesktopVariantMenu({ focus: false });
      return;
    }

    const optionButton = event.target.closest
      && event.target.closest(
        '[data-mmx-desktop-variant-menu] [data-mmx-desktop-variant-option]'
      );

    if (optionButton && activeDesktopVariantSelect) {
      event.preventDefault();
      event.stopPropagation();

      const optionIndex = Number(
        optionButton.getAttribute('data-option-index') || -1
      );

      if (
        optionIndex >= 0
        && activeDesktopVariantSelect.options
        && activeDesktopVariantSelect.options[optionIndex]
      ) {
        const select = activeDesktopVariantSelect;
        closeDesktopVariantMenu({ focus: false });
        select.selectedIndex = optionIndex;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        window.setTimeout(() => {
          if (select.isConnected) syncVariantSelectDisplay(select);
        }, 0);
      }

      return;
    }

    if (
      desktopVariantMenu
      && !desktopVariantMenu.hidden
      && !desktopVariantMenu.contains(event.target)
      && !(activeDesktopVariantTrigger
        && activeDesktopVariantTrigger.contains(event.target))
    ) {
      closeDesktopVariantMenu({ focus: false });
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    const trigger = event.target.closest
      && event.target.closest(
        '.mmx-cart-drawer-shell [data-mmx-desktop-variant-trigger]'
      );

    if (trigger) {
      const shell = trigger.closest('.mmx-variant-select-shell');
      const select = shell && shell.querySelector('select');

      if (!select || select.disabled) return;

      if (
        event.key === 'Enter'
        || event.key === ' '
        || event.key === 'ArrowDown'
        || event.key === 'ArrowUp'
      ) {
        event.preventDefault();

        const preference = event.key === 'ArrowUp'
          ? 'last'
          : (event.key === 'ArrowDown' ? 'first' : 'selected');

        openDesktopVariantMenu(select, preference);
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        closeDesktopVariantMenu({ focus: true });
      }

      return;
    }

    const option = event.target.closest
      && event.target.closest(
        '[data-mmx-desktop-variant-menu] [data-mmx-desktop-variant-option]'
      );

    if (!option) return;

    const options = desktopVariantMenu
      ? Array.from(
          desktopVariantMenu.querySelectorAll(
            '[data-mmx-desktop-variant-option]'
          )
        )
      : [];

    const currentIndex = options.indexOf(option);

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = Math.max(
        0,
        Math.min(options.length - 1, currentIndex + direction)
      );

      if (options[nextIndex]) {
        options[nextIndex].focus({ preventScroll: true });
      }

      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const target = event.key === 'Home'
        ? options[0]
        : options[options.length - 1];

      if (target) target.focus({ preventScroll: true });
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      option.click();
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDesktopVariantMenu({ focus: true });
      return;
    }

    if (event.key === 'Tab') {
      closeDesktopVariantMenu({ focus: false });
    }
  }, true);

  document.addEventListener('scroll', (event) => {
    if (!desktopVariantMenu || desktopVariantMenu.hidden) return;

    const scrollTarget = event.target;

    // Scrolling the dropdown itself must never close it. This also prevents
    // the menu from closing immediately when focus moves to the currently
    // selected option and the browser scrolls that option into view.
    if (
      scrollTarget === desktopVariantMenu
      || (
        scrollTarget
        && scrollTarget.nodeType === 1
        && desktopVariantMenu.contains(scrollTarget)
      )
    ) {
      return;
    }

    // The mobile version is a fixed bottom sheet and remains aligned while
    // the drawer underneath scrolls. Desktop remains anchored to its field.
    if (mobileVariantSheetEnabled()) return;

    closeDesktopVariantMenu({ focus: false });
  }, true);

  window.addEventListener('pageshow', () => {
    if (!mmxMobileDrawerOpenIntent) {
      mmxUnlockMobilePageScroll({
        force: true,
        restorePosition: false
      });
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (
      document.visibilityState === 'visible'
      && !mmxMobileDrawerOpenIntent
    ) {
      mmxUnlockMobilePageScroll({
        force: true,
        restorePosition: false
      });
    }
  });

  window.addEventListener('resize', () => {
    closeDesktopVariantMenu({ focus: false });
    syncDesktopVariantSelects(document);

    if (!mmxMobileDrawerViewport() && mmxMobileScrollLockActive) {
      mmxUnlockMobilePageScroll();
    } else if (
      mmxMobileDrawerViewport()
      && mmxMobileDrawerOpenIntent
    ) {
      mmxLockMobilePageScroll();
    }
  });

  if (desktopVariantMedia && typeof desktopVariantMedia.addEventListener === 'function') {
    desktopVariantMedia.addEventListener('change', () => {
      closeDesktopVariantMenu({ focus: false });
      syncDesktopVariantSelects(document);
    });
  }

  document.addEventListener('theme:close-cart-drawer', () => {
    mmxMobileDrawerOpenIntent = false;
    mmxScheduleMobilePageScrollUnlock(360);
    setVariantDrawerInteractionState(false);
    closePreorderModal({ restoreFocus: false });
  });

  document.addEventListener('pointerdown', (event) => {
    const closeControl = event.target
      && event.target.closest
      && event.target.closest(
        '.mmx-cart-drawer-shell .cc-popup-close,'
        + ' .mmx-cart-drawer-shell .cc-popup-background'
      );

    if (closeControl) {
      mmxMobileDrawerOpenIntent = false;
      mmxScheduleMobilePageScrollUnlock(360);
      setVariantDrawerInteractionState(false);
    }
  }, true);

  document.addEventListener('keydown', (event) => {
    if (
      event.key === 'Escape'
      && document.documentElement.classList.contains(
        'mmx-variant-drawer-interactive'
      )
    ) {
      mmxMobileDrawerOpenIntent = false;
      mmxScheduleMobilePageScrollUnlock(360);
      setVariantDrawerInteractionState(false);
    }
  }, true);

  document.addEventListener('click', (event) => {
    const modalClose = event.target.closest
      && event.target.closest('[data-mmx-preorder-close]');
    if (modalClose) {
      event.preventDefault();
      closePreorderModal({ restoreFocus: true });
      return;
    }

    const confirm = event.target.closest
      && event.target.closest('[data-mmx-preorder-confirm]');
    if (confirm) {
      event.preventDefault();
      confirmPreorderModal();
      return;
    }

    const walletGate = event.target.closest
      && event.target.closest('[data-mmx-preorder-wallet-gate]');
    if (walletGate) {
      event.preventDefault();
      event.stopImmediatePropagation();
      openPreorderModal({ type: 'wallet', trigger: walletGate });
      return;
    }

    const checkout = event.target.closest
      && event.target.closest(
        '.mmx-cart-drawer-shell .checkout-buttons a[href*="/checkout"],'
        + ' .mmx-cart-drawer-shell .checkout-buttons button[name="checkout"],'
        + ' .mmx-cart-drawer-shell .checkout-buttons [formaction*="/checkout"]'
      );

    if (!checkout) return;
    if (
      checkout.getAttribute('aria-disabled') === 'true'
      || checkout.disabled
    ) {
      event.preventDefault();
      return;
    }

    const terms = checkout.closest('.cart-drawer')
      && checkout.closest('.cart-drawer').querySelector('terms-agreement input[type="checkbox"]');
    if (terms && !terms.checked) return;

    if (!preorderRequiresAcknowledgement(cartState)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    openPreorderModal({
      type: 'checkout',
      href: checkout.href || checkout.getAttribute('formaction') || `${root}checkout`,
      trigger: checkout
    });
  }, true);

  document.addEventListener('change', (event) => {
    const checkbox = event.target.closest
      && event.target.closest('[data-mmx-preorder-checkbox]');
    if (!checkbox) return;

    const modal = checkbox.closest('[data-mmx-preorder-modal]');
    const submit = modal && modal.querySelector('[data-mmx-preorder-confirm]');
    if (submit) submit.disabled = !checkbox.checked;
  }, true);

  document.addEventListener('keydown', (event) => {
    if (!document.documentElement.classList.contains('mmx-preorder-modal-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closePreorderModal({ restoreFocus: true });
      return;
    }

    preorderFocusTrap(event);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target && event.target.closest && event.target.closest('form');
    if (!form || !document.querySelector('.mmx-cart-drawer-shell')) return;
    if (!isAddToCartForm(form)) return;

    const customisationUrl = form.classList.contains('mmx-cart-addon__form')
      ? recommendationCustomisationUrl(form)
      : '';

    if (customisationUrl) {
      event.preventDefault();
      event.stopImmediatePropagation();
      window.location.assign(customisationUrl);
      return;
    }

    if (window.theme && window.theme.settings && window.theme.settings.after_add_to_cart && window.theme.settings.after_add_to_cart !== 'drawer') return;
    const submitter = event.submitter || document.activeElement;
    if (submitter && submitter.matches && (submitter.matches('[name="checkout"], [formaction*="/checkout"]') || submitter.classList.contains('atc-bar-pricing-buy'))) return;

    // V1.7.3: MMX owns the add-to-cart request and renders an optimistic cart row immediately.
    // V1.7 allowed both MMX and Symmetry to submit, which could duplicate quantity.
    // V1.7.1 let Symmetry submit and only polled /cart.js, which made the drawer feel slow.
    event.preventDefault();
    event.stopImmediatePropagation();

    if (form.dataset.mmxSubmitting === 'true') return;
    form.dataset.mmxSubmitting = 'true';
    handleAddToCartForm(form, submitter)
      .catch((error) => {
        document.documentElement.classList.remove('mmx-cart-optimistic-open');
        setAddonButtonState(submitter, 'reset');
        console.error('[MMX Cart] Add to cart failed.', error);
        showCartError(error.message || 'Unable to add this item to cart. Please try again.');
        return fetchCart(true).then((cart) => {
          if (cart) renderFastCart(cart, { source: 'add-error' });
        });
      })
      .finally(() => {
        delete form.dataset.mmxSubmitting;
        setSyncingState(false);
      });
  }, true);

  document.addEventListener('click', (event) => {
    const actionControl = event.target.closest && event.target.closest('.mmx-cart-drawer-shell [data-mmx-cart-action]');
    if (!actionControl) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const lineKey = actionControl.getAttribute('data-mmx-line-key');
    const item = actionControl.closest('.cart-item');
    const wrapper = actionControl.closest('[data-mmx-cart-line-wrapper]');
    const cartItem = cartItemByKey(lineKey, cartState);
    const quantityInput = item && item.querySelector('.cart-item__quantity-input:not(.mmx-gift-quantity-input)');
    const currentQty = quantityInput ? sanitizeCartQuantity(quantityInput.value, 0, { allowZero: true }) : 0;
    const limit = inventoryLimitForItem(cartItem);
    const maxQuantity = limit.enforced ? limit.max : MMX_MAX_CART_LINE_QUANTITY;
    let nextQty = currentQty;
    const action = actionControl.getAttribute('data-mmx-cart-action');

    if (!lineKey) return;

    if (
      actionControl.getAttribute('aria-disabled') === 'true'
      || cartLineMutationIsLocked(lineKey, wrapper)
    ) {
      if (wrapper && cartItem) syncQuantityControlState(wrapper, cartItem);
      return;
    }

    if (action === 'increase' && (
      actionControl.classList.contains('unusable')
      || currentQty >= maxQuantity
    )) {
      if (wrapper && cartItem) syncQuantityControlState(wrapper, cartItem);
      return;
    }

    if (action === 'remove') nextQty = 0;
    if (action === 'decrease') nextQty = sanitizeCartQuantity(currentQty - 1, 0, { allowZero: true });
    if (action === 'increase') nextQty = sanitizeCartQuantity(currentQty + 1, currentQty, { allowZero: false, max: maxQuantity });

    const accepted = enqueueCartLineMutation(lineKey, wrapper, () => applyCartChange(
      { id: lineKey, quantity: nextQty },
      { lineKey, quantity: nextQty, previousQuantity: currentQty, maxQuantity, inventoryLimitEnforced: limit.enforced }
    ));

    if (!accepted) {
      if (wrapper && cartItem) syncQuantityControlState(wrapper, cartItem);
      return;
    }

    if (quantityInput) quantityInput.value = nextQty;
    markCartItemWorking(item, nextQty === 0 ? 'Removing…' : 'Updating…');
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target.closest && event.target.closest('.mmx-cart-drawer-shell .cart-item__quantity-input:not(.mmx-gift-quantity-input)');
    if (!input) return;

    const lineKey = input.getAttribute('data-mmx-line-key');
    const item = cartItemByKey(lineKey, cartState);
    const limit = inventoryLimitForItem(item);
    const maxQuantity = limit.enforced ? limit.max : MMX_MAX_CART_LINE_QUANTITY;
    const digits = String(input.value || '').replace(/[^0-9]/g, '').slice(0, String(Math.max(1, maxQuantity)).length);

    if (!digits) {
      input.value = '';
      delete input.dataset.mmxQuantityClamped;
      return;
    }

    const requestedQuantity = Number(digits);
    if (requestedQuantity > maxQuantity) {
      input.value = String(maxQuantity);
      input.dataset.mmxQuantityClamped = 'true';
    } else {
      input.value = digits;
      delete input.dataset.mmxQuantityClamped;
    }
  }, true);

  document.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('.mmx-cart-drawer-shell .cart-item__quantity-input:not(.mmx-gift-quantity-input)');
    if (!input) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const lineKey = input.getAttribute('data-mmx-line-key');
    if (!lineKey) return;

    const item = cartItemByKey(lineKey, cartState);
    const limit = inventoryLimitForItem(item);
    const maxQuantity = limit.enforced ? limit.max : MMX_MAX_CART_LINE_QUANTITY;
    const initialQty = sanitizeCartQuantity(input.getAttribute('data-initial-value'), 1, { allowZero: false, max: maxQuantity });
    const wasClamped = input.dataset.mmxQuantityClamped === 'true';
    const nextQty = sanitizeCartQuantity(input.value, initialQty, { allowZero: true, max: maxQuantity });

    delete input.dataset.mmxQuantityClamped;
    input.value = nextQty;

    if (wasClamped) {
      showCartError(inventoryLimitMessage(maxQuantity, true));
    }

    if (nextQty === initialQty) {
      const wrapper = input.closest('[data-mmx-cart-line-wrapper]');
      if (wrapper && item) syncQuantityControlState(wrapper, item);
      return;
    }

    const wrapper = input.closest('[data-mmx-cart-line-wrapper]');
    const accepted = enqueueCartLineMutation(lineKey, wrapper, () => applyCartChange(
      { id: lineKey, quantity: nextQty },
      { lineKey, quantity: nextQty, previousQuantity: initialQty, maxQuantity, inventoryLimitEnforced: limit.enforced }
    ));

    if (!accepted) {
      input.value = initialQty;
      if (wrapper && item) syncQuantityControlState(wrapper, item);
      return;
    }

    markCartItemWorking(input.closest('.cart-item'), nextQty === 0 ? 'Removing…' : 'Updating…');
  }, true);


  document.addEventListener('change', (event) => {
    const select = event.target.closest && event.target.closest('.mmx-cart-drawer-shell .mmx-cart-line-variant__select');
    if (!select) return;

    syncVariantSelectDisplay(select);
    event.preventDefault();
    event.stopImmediatePropagation();

    const editor = select.closest('[data-mmx-cart-variant-editor]');
    const lineKey = editor && editor.getAttribute('data-line-key');
    const lineIndex = Number(editor && editor.getAttribute('data-line-index') || 0);
    const currentVariantId = Number(editor && editor.getAttribute('data-current-variant-id') || 0);
    const nextVariantId = Number(select.value || 0);
    const itemElement = select.closest('.cart-item');

    if (!lineKey || !nextVariantId) return;

    if (nextVariantId === currentVariantId) {
      removePendingCartLineEngravingEditor(itemElement);
      return;
    }

    const selectedOption = select.options && select.selectedIndex >= 0
      ? select.options[select.selectedIndex]
      : null;
    const variantTitle = String(
      selectedOption
      && (
        selectedOption.getAttribute('data-variant-title')
        || selectedOption.textContent
      )
      || ''
    ).trim();

    const selectedPriceCents = Number(
      selectedOption && selectedOption.getAttribute('data-variant-price-cents') || 0
    );
    const selectedCompareCents = Number(
      selectedOption && selectedOption.getAttribute('data-variant-compare-at-cents') || 0
    );

    const cartItem = cartItemByKey(lineKey, cartState);
    const customisationProduct = editor
      && editor.getAttribute('data-mmx-customisation-product') === 'true';
    const requiresEngraving = customisationProduct
      && targetVariantRequiresEngraving(null, variantTitle);
    const existingEngraving = cartItemEngravingProperty(cartItem);

    // Do not commit No Engraving -> Engraving until the required line-item
    // property has been entered. Engraving -> another Engraving variant keeps
    // the existing property and can continue through the normal flow.
    if (requiresEngraving && !existingEngraving) {
      showPendingCartLineEngravingEditor({
        itemElement,
        variantEditor: editor,
        cartItem,
        lineKey,
        lineIndex,
        variantId: nextVariantId,
        variantTitle,
        priceCents: selectedPriceCents,
        compareAtCents: selectedCompareCents
      });
      return;
    }

    removePendingCartLineEngravingEditor(itemElement);

    if (targetVariantIsNoEngraving(null, variantTitle) && itemElement) {
      itemElement.querySelectorAll('.line-item-property').forEach((propertyRow) => {
        const label = propertyRow.querySelector('.line-item-property__label');
        if (label && isEngravingPropertyName(label.textContent)) {
          propertyRow.remove();
        }
      });
    }

    const priceNode = itemElement && itemElement.querySelector('[data-mmx-cart-price]');

    if (priceNode && selectedPriceCents > 0) {
      priceNode.innerHTML = priceHtmlWithSavings(
        selectedPriceCents,
        selectedCompareCents
      );
      priceNode.setAttribute('data-current-price', String(selectedPriceCents));
      priceNode.setAttribute('data-compare-at-price', String(selectedCompareCents));
      priceNode.setAttribute('data-variant-id', String(nextVariantId));
    }

    const isNoEngravingTarget = targetVariantIsNoEngraving(null, variantTitle);
    markCartItemWorking(itemElement, 'Updating variant…', {
      silent: isNoEngravingTarget
    });
    select.disabled = true;

    enqueueCartMutation(() => applyCartVariantChange(
      {
        lineKey,
        lineIndex,
        variantId: nextVariantId,
        variantTitle
      },
      {
        lineKey,
        lineIndex,
        variantId: nextVariantId,
        variantTitle
      }
    ));
  }, true);

  document.addEventListener('input', (event) => {
    const input = event.target.closest
      && event.target.closest('.mmx-cart-drawer-shell [data-mmx-cart-line-engraving-input]');
    if (!input) return;

    applyEngravingInputLimits(input);

    updatePendingEngravingButton(
      input.closest('[data-mmx-cart-line-engraving]')
    );
  }, true);


  document.addEventListener('click', (event) => {
    const button = event.target.closest
      && event.target.closest('.mmx-cart-drawer-shell [data-mmx-cart-line-engraving-update]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const pendingEditor = button.closest('[data-mmx-cart-line-engraving]');
    const input = pendingEditor
      && pendingEditor.querySelector('[data-mmx-cart-line-engraving-input]');
    const itemElement = button.closest('.cart-item');
    const variantEditor = itemElement
      && itemElement.querySelector('[data-mmx-cart-variant-editor]');
    const select = variantEditor
      && variantEditor.querySelector('.mmx-cart-line-variant__select');

    if (!pendingEditor || !input || !variantEditor || !select) return;

    const engravingText = String(
      applyEngravingInputLimits(input)
    ).trim();
    if (!engravingText) {
      input.focus();
      updatePendingEngravingButton(pendingEditor);
      return;
    }

    const lineKey = String(pendingEditor.getAttribute('data-line-key') || '');
    const lineIndex = Number(pendingEditor.getAttribute('data-line-index') || 0);
    const variantId = Number(pendingEditor.getAttribute('data-variant-id') || 0);
    const variantTitle = String(pendingEditor.getAttribute('data-variant-title') || '');
    const engravingPropertyName = String(
      input.dataset.propertyName || 'Engraving Text'
    ).trim() || 'Engraving Text';

    if (!lineKey || !variantId) return;

    const priceCents = Number(
      pendingEditor.getAttribute('data-variant-price-cents') || 0
    );
    const compareAtCents = Number(
      pendingEditor.getAttribute('data-variant-compare-at-cents') || 0
    );
    const priceNode = itemElement && itemElement.querySelector('[data-mmx-cart-price]');

    if (priceNode && priceCents > 0) {
      priceNode.innerHTML = priceHtmlWithSavings(priceCents, compareAtCents);
      priceNode.setAttribute('data-current-price', String(priceCents));
      priceNode.setAttribute('data-compare-at-price', String(compareAtCents));
      priceNode.setAttribute('data-variant-id', String(variantId));
    }

    button.disabled = true;
    button.classList.add('is-loading');
    button.innerHTML = '<span class="mmx-spinner" aria-hidden="true"></span>';
    select.disabled = true;

    const status = pendingEditor.querySelector(
      '[data-mmx-cart-line-engraving-status]'
    );
    if (status) status.textContent = 'Updating engraving option.';

    markCartItemWorking(itemElement, 'Updating variant…');

    enqueueCartMutation(() => applyCartVariantChange(
      {
        lineKey,
        lineIndex,
        variantId,
        variantTitle,
        engravingText,
        engravingPropertyName
      },
      {
        lineKey,
        lineIndex,
        variantId,
        variantTitle
      }
    ));
  }, true);


  document.addEventListener('change', (event) => {
    const select = event.target.closest && event.target.closest('.mmx-empty-rec__variant, .mmx-cart-addon__variant');
    if (!select) return;
    syncVariantSelectDisplay(select);
    const card = select.closest('.mmx-empty-rec-card, .mmx-cart-addon-card');
    const priceNode = card && card.querySelector('[data-mmx-empty-rec-price], [data-mmx-cart-addon-price]');
    const option = select.options && select.selectedIndex >= 0 ? select.options[select.selectedIndex] : null;
    const price = option && option.getAttribute('data-variant-price');
    const priceCents = option && option.getAttribute('data-variant-price-cents');
    const compareCents = option && option.getAttribute('data-variant-compare-at-cents');
    if (priceNode && priceCents) {
      priceNode.innerHTML = addonPriceHtml(Number(priceCents), Number(compareCents || 0));
    } else if (priceNode && price) {
      priceNode.textContent = price;
    }

    const form = select.closest('.mmx-cart-addon__form');
    if (form) updateRecommendationCustomisationButton(form);
  }, true);


  document.addEventListener('click', (event) => {
    const toggle = event.target.closest && event.target.closest('.mmx-cart-drawer-shell [data-mmx-footer-toggle]');
    if (!toggle) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const name = String(toggle.getAttribute('data-mmx-footer-toggle') || '');
    const isOpen = toggle.getAttribute('aria-expanded') === 'true';
    setFooterUtilityPanel(name, !isOpen, { focus: !isOpen });
  }, true);


  document.addEventListener('input', (event) => {
    const input = event.target
      && event.target.closest
      && event.target.closest('.mmx-cart-drawer-shell [data-mmx-note-input]');

    if (!input) return;

    window.clearTimeout(noteSaveCollapseTimer);
    noteSaveCollapseTimer = null;

    const container = input.closest('.mmx-cart-note-form');
    syncNoteSaveState(container);
  }, true);


  document.addEventListener('click', (event) => {
    const button = event.target
      && event.target.closest
      && event.target.closest('.mmx-cart-drawer-shell [data-mmx-note-save]');

    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const container = button.closest('.mmx-cart-note-form');
    const input = container && container.querySelector('[data-mmx-note-input]');
    if (!container || !input) return;

    const submittedNote = String(input.value || '');

    window.clearTimeout(noteSaveCollapseTimer);
    noteSaveCollapseTimer = null;
    setNoteSaveState(container, 'saving');

    enqueueCartMutation(async () => {
      try {
        const results = await Promise.all([
          postJson(routes.update, { note: submittedNote }),
          wait(350)
        ]);
        const cart = results[0];

        cartState = cart;

        // Do not mark newer text as saved if the customer continued typing
        // while the request was running.
        if (String(input.value || '') !== submittedNote) {
          input.dataset.savedNote = submittedNote;
          input.dataset.hasSavedState = 'true';
          syncNoteSaveState(container);
          return cart;
        }

        input.dataset.savedNote = submittedNote;
        input.dataset.hasSavedState = 'true';
        setNoteSaveState(container, 'saved');

        noteSaveCollapseTimer = window.setTimeout(() => {
          const stillSaved = String(input.value || '') === String(input.dataset.savedNote || '');

          if (stillSaved && footerUtilityOpenPanel === 'note') {
            setFooterUtilityPanel('note', false, { focus: false });
          }
        }, 900);

        return cart;
      } catch (error) {
        console.error('[MMX Cart] Unable to save order note.', error);
        setNoteSaveState(container, 'idle');
        showCartError('Unable to save the order note. Please try again.');
        return cartState;
      }
    });
  }, true);


  document.addEventListener('submit', (event) => {
    const form = event.target && event.target.closest && event.target.closest('.mmx-cart-drawer-shell [data-mmx-discount-form]');
    if (!form) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    const input = form.querySelector('[name="discount"]');
    const code = input ? input.value.trim() : '';
    if (!code) {
      setDiscountFormState('error', 'Enter a discount code first.', {
        restoreValue: '',
        resetAfter: 3000
      });
      return;
    }

    enqueueCartMutation(async () => {
      setDiscountFormState('loading', 'Applying discount…', {
        restoreValue: code,
        autoReset: false
      });
      const before = cartState || await fetchCart(true);
      const beforeDiscounts = collectCartDiscounts(before);
      const beforeTotalDiscount = Number(before && before.total_discount || 0);
      const discountUrl = `${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}cart`)}`;

      await fetch(discountUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html,application/xhtml+xml' }
      });

      const cart = await fetchCart(true);
      const cfg = parseConfig();
      let finalCart = cart;
      if (cfg) finalCart = await syncGifts(cart, cfg, { renderDuring: false }) || cart;
      renderFastCart(finalCart, { source: 'discount-apply' });

      const discounts = collectCartDiscounts(finalCart);
      const labels = discounts.labels || [];
      const beforeLabels = beforeDiscounts.labels || [];
      const applied = labels.some((title) => String(title || '').toLowerCase() === code.toLowerCase())
        || Number(finalCart && finalCart.total_discount || 0) > beforeTotalDiscount
        || labels.length > beforeLabels.length;

      if (input) input.value = '';
      setDiscountFormState(
        applied ? 'success' : 'error',
        applied ? 'Discount applied.' : 'Code not applied.',
        {
          restoreValue: applied ? '' : code,
          resetAfter: 3000
        }
      );
      return finalCart;
    });
  }, true);


  document.addEventListener('click', (event) => {
    const button = event.target && event.target.closest && event.target.closest('.mmx-cart-drawer-shell [data-mmx-discount-remove]');
    if (!button) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const code = String(button.getAttribute('data-mmx-discount-remove') || '').trim();
    if (!code || button.disabled) return;

    enqueueCartMutation(async () => {
      button.disabled = true;
      button.classList.add('is-removing');
      button.setAttribute('aria-busy', 'true');
      setSyncingState(true, { silent: true });

      const before = cartState || await fetchCart(true);
      const remainingCodes = collectRemovableDiscountCodes(before)
        .filter((activeCode) => String(activeCode || '').toLowerCase() !== code.toLowerCase());

      let cart = await postJson(routes.update, {
        discount: remainingCodes.join(',')
      });

      const cfg = parseConfig();
      if (cfg) cart = await syncGifts(cart, cfg, { renderDuring: false }) || cart;

      renderFastCart(cart, { source: 'discount-remove' });
      return cart;
    });
  }, true);


  document.addEventListener('click', (event) => {
    const toggle = event.target.closest && event.target.closest('[data-mmx-addons-collapse]');
    if (!toggle) return;

    event.preventDefault();
    const addons = toggle.closest('[data-mmx-addons]');
    const body = addons && addons.querySelector('[data-mmx-addons-body]');
    const icon = toggle.querySelector('.mmx-cart-addons__collapse-icon');
    if (!addons || !body) return;

    const opening = addons.classList.contains('is-collapsed');
    addons.classList.toggle('is-collapsed', !opening);
    body.hidden = !opening;
    toggle.setAttribute('aria-expanded', opening ? 'true' : 'false');
    if (icon) icon.textContent = opening ? '−' : '+';

    if (opening) {
      setAddonPage(addons, Number(addons.getAttribute('data-mmx-addon-page') || 0));
    }
  }, true);


  document.addEventListener('click', (event) => {
    const prev = event.target.closest && event.target.closest('[data-mmx-addons-prev]');
    const next = event.target.closest && event.target.closest('[data-mmx-addons-next]');
    if (!prev && !next) return;

    const addons = (prev || next).closest('[data-mmx-addons]');
    if (!addons) return;

    event.preventDefault();
    const current = Number(addons.getAttribute('data-mmx-addon-page') || 0);
    setAddonPage(addons, current + (next ? 1 : -1));
  }, true);

  document.addEventListener('click', (event) => {
    const dot = event.target.closest
      && event.target.closest('[data-mmx-addons-dot]');

    if (!dot) return;

    const addons = dot.closest('[data-mmx-addons]');
    if (!addons) return;

    event.preventDefault();
    const pageIndex = Number(dot.getAttribute('data-page-index') || 0);
    setAddonPage(addons, pageIndex);
  }, true);


  function mmxRecommendationSwipeSettings() {
    const cfg = parseConfig();
    const settings = cfg
      && cfg.recommendations
      && cfg.recommendations.swipeNavigation;

    return {
      enabled: !settings || settings.enabled !== false,
      minimumDistance: Math.max(
        30,
        Math.min(90, Number(settings && settings.minimumDistance || 50))
      )
    };
  }

  function mmxRecommendationSwipeViewport() {
    return Boolean(
      window.matchMedia
      && window.matchMedia('(max-width: 749px)').matches
    );
  }

  function mmxRecommendationSwipeBlockedTarget(target) {
    return Boolean(
      target
      && target.closest
      && target.closest(
        'button, input, select, textarea, option, label,'
        + ' [contenteditable="true"],'
        + ' [data-mmx-desktop-variant-trigger]'
      )
    );
  }

  function mmxFindTouch(touchList, identifier) {
    if (!touchList) return null;

    for (let index = 0; index < touchList.length; index += 1) {
      if (touchList[index].identifier === identifier) {
        return touchList[index];
      }
    }

    return null;
  }

  function mmxClearRecommendationTouchGesture() {
    mmxRecommendationTouchGesture = null;
  }

  document.addEventListener('touchstart', (event) => {
    const settings = mmxRecommendationSwipeSettings();

    if (
      !settings.enabled
      || !mmxRecommendationSwipeViewport()
      || !event.touches
      || event.touches.length !== 1
      || mmxRecommendationSwipeBlockedTarget(event.target)
    ) {
      mmxClearRecommendationTouchGesture();
      return;
    }

    const viewport = event.target
      && event.target.closest
      && event.target.closest(
        '.mmx-cart-drawer-shell [data-mmx-addons-viewport]'
      );

    if (!viewport) {
      mmxClearRecommendationTouchGesture();
      return;
    }

    const addons = viewport.closest('[data-mmx-addons]');
    const pages = addons
      ? Array.from(addons.querySelectorAll('[data-mmx-addons-page]'))
      : [];

    if (!addons || pages.length <= 1) {
      mmxClearRecommendationTouchGesture();
      return;
    }

    const touch = event.touches[0];

    mmxRecommendationTouchGesture = {
      addons,
      viewport,
      identifier: touch.identifier,
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      startTime: Number(event.timeStamp || Date.now()),
      minimumDistance: settings.minimumDistance,
      pageCount: pages.length,
      currentPage: Math.max(
        0,
        Math.min(
          pages.length - 1,
          Number(addons.getAttribute('data-mmx-addon-page') || 0)
        )
      ),
      axis: null,
      cancelled: false
    };
  }, { capture: true, passive: true });

  document.addEventListener('touchmove', (event) => {
    const gesture = mmxRecommendationTouchGesture;
    if (!gesture) return;

    if (!event.touches || event.touches.length !== 1) {
      gesture.cancelled = true;
      return;
    }

    const touch = mmxFindTouch(event.touches, gesture.identifier);
    if (!touch) {
      gesture.cancelled = true;
      return;
    }

    gesture.lastX = touch.clientX;
    gesture.lastY = touch.clientY;

    const deltaX = gesture.lastX - gesture.startX;
    const deltaY = gesture.lastY - gesture.startY;
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);

    if (!gesture.axis && Math.max(absoluteX, absoluteY) >= 10) {
      /*
        Require a clear directional lead. Ambiguous diagonal movement is left
        undecided until the shopper's intent becomes clearer.
      */
      if (absoluteY >= absoluteX * 1.25) {
        gesture.axis = 'vertical';
      } else if (absoluteX >= absoluteY * 1.25) {
        gesture.axis = 'horizontal';
        gesture.viewport.classList.add('is-mmx-swipe-active');
      }
    }
  }, { capture: true, passive: true });

  document.addEventListener('touchend', (event) => {
    const gesture = mmxRecommendationTouchGesture;
    if (!gesture) return;

    const touch = mmxFindTouch(event.changedTouches, gesture.identifier);
    mmxClearRecommendationTouchGesture();

    if (gesture.viewport) {
      gesture.viewport.classList.remove('is-mmx-swipe-active');
    }

    if (
      !touch
      || gesture.cancelled
      || gesture.axis !== 'horizontal'
    ) {
      return;
    }

    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;
    const absoluteX = Math.abs(deltaX);
    const absoluteY = Math.abs(deltaY);
    const elapsed = Math.max(
      1,
      Number(event.timeStamp || Date.now()) - gesture.startTime
    );
    const horizontalVelocity = absoluteX / elapsed;

    /*
      A normal swipe needs the configured distance. A shorter, deliberate flick
      is also accepted, but only when it is fast and clearly horizontal.
    */
    const passedDistance = absoluteX >= gesture.minimumDistance;
    const passedFlick = absoluteX >= 32 && horizontalVelocity >= 0.38;
    const clearlyHorizontal = absoluteX >= absoluteY * 1.35;

    if ((!passedDistance && !passedFlick) || !clearlyHorizontal) {
      return;
    }

    let nextPage = gesture.currentPage + (deltaX < 0 ? 1 : -1);
    nextPage = Math.max(
      0,
      Math.min(gesture.pageCount - 1, nextPage)
    );

    /*
      Suppress the synthetic link click that mobile browsers can emit after a
      completed swipe across a product image or title.
    */
    gesture.addons.setAttribute(
      'data-mmx-swipe-suppress-click-until',
      String(Date.now() + 550)
    );

    setAddonPage(gesture.addons, nextPage);
  }, { capture: true, passive: true });

  document.addEventListener('touchcancel', () => {
    const gesture = mmxRecommendationTouchGesture;

    if (gesture && gesture.viewport) {
      gesture.viewport.classList.remove('is-mmx-swipe-active');
    }

    mmxClearRecommendationTouchGesture();
  }, { capture: true, passive: true });

  document.addEventListener('click', (event) => {
    const addons = event.target
      && event.target.closest
      && event.target.closest(
        '.mmx-cart-drawer-shell [data-mmx-addons]'
      );

    if (!addons) return;

    const suppressUntil = Number(
      addons.getAttribute('data-mmx-swipe-suppress-click-until') || 0
    );

    if (Date.now() >= suppressUntil) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    addons.removeAttribute('data-mmx-swipe-suppress-click-until');
  }, true);


  document.addEventListener('theme:cartchanged', (event) => {
    if (event.detail && event.detail.mmxFastRenderer) return;
    addPendingUntil = 0;
    scheduleRefresh(true, 50);
  }, true);

  document.addEventListener('theme:open-cart-drawer', (event) => {
    mmxMobileDrawerOpenIntent = true;
    mmxLockMobilePageScroll();
    setVariantDrawerInteractionState(true);
    mmxScheduleDrawerWidgetVisibilitySync([40, 140, 400, 900]);

    // Prepare all existing desktop variant controls before the theme starts
    // translating the drawer, so the selector and cart move as one layer.
    syncDesktopVariantSelects(document);

    if (event && event.detail && event.detail.mmxFastOpen) return;

    if (Date.now() < externalCartMutationPendingUntil) {
      mmxShowExternalCartPendingState();
      mmxScheduleAuthoritativeCartReconciliation('drawer-open');
      return;
    }

    // Keep an authoritative safety refresh, but wait until the drawer's slide
    // transition has settled so the cart DOM is not replaced mid-animation.
    scheduleRefresh(true, 420);

    if (cartState && Number(cartState.item_count || 0) > 0) {
      scheduleAcceleratedCheckoutRefresh(460);
    }
  });

  document.addEventListener('shopify:section:load', () => {
    config = null;
    configText = '';
    observedAddonShell = null;
    footerUtilityOpenPanel = null;
    inventorySnapshot = null;
    inventoryRefreshRequest += 1;
    window.clearTimeout(inventoryRefreshTimer);
    inventoryRefreshTimer = null;
    recommendationRenderRequest += 1;
    recordCurrentProductView();
    observeAddonMutations();
    mmxObserveDrawerAndFloatingWidgets();
    applyFooterUtilityState({ focus: false });
    initialiseRecommendationCustomiseLanding();
    scheduleRefresh(true, 0);
    scheduleAcceleratedCheckoutRefresh(300);
  });
  document.addEventListener('shopify:section:reorder', () => scheduleRefresh(false, 0));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      recordCurrentProductView();
      observeAddonMutations();
      mmxObserveDrawerAndFloatingWidgets();
      applyFooterUtilityState({ focus: false });
      initialiseRecommendationCustomiseLanding();
      scheduleRefresh(true, 0);
    }, { once: true });
  } else {
    recordCurrentProductView();
    observeAddonMutations();
    mmxObserveDrawerAndFloatingWidgets();
    applyFooterUtilityState({ focus: false });
    initialiseRecommendationCustomiseLanding();
    scheduleRefresh(true, 0);
  }

  window.MMXCart = Object.freeze({
    refresh: () => refresh(true),
    eligibleSpend: () => eligibleSpend(cartState, parseConfig()),
    checkoutDisplayTotal: () => checkoutDisplayTotal(cartState),
    formatMoney,
    render: () => renderFastCart(cartState, { source: 'manual' })
  });

  window.mmxCartDebugWallet = function() {
    const node = acceleratedCheckoutNode(document);
    return {
      sectionId: currentCartDrawerSectionId(),
      exists: Boolean(node),
      walletState: node && node.getAttribute('data-mmx-wallet-state'),
      refreshing: Boolean(node && node.classList.contains('is-refreshing')),
      reserved: document.documentElement.classList.contains('mmx-wallet-space-reserved'),
      hasButtons: acceleratedCheckoutHasButtons(node),
      painted: acceleratedCheckoutIsPainted(node),
      containerRect: node ? {
        width: Math.round(node.getBoundingClientRect().width),
        height: Math.round(node.getBoundingClientRect().height)
      } : null,
      html: node ? node.innerHTML.slice(0, 1500) : '',
      hostname: window.location.hostname,
      userAgent: navigator.userAgent
    };
  };

  window.mmxCartDebugBnpl = function() {
    const cfg = parseConfig();
    return fetchCart(true).then((cart) => ({
      cart,
      configBnpl: cfg && cfg.bnpl,
      shellCurrentProductExcluded: shellCurrentProductBnplExcluded(),
      domProductMarkers: domExcludedProductMarkers(),
      domVariantMarkers: domExcludedVariantMarkers(),
      domHandleTokens: domExcludedHandleTokens(),
      hasExcludedProperty: cartHasBnplExcludedProperty(cart),
      hasDomExclusion: cartContainsDomBnplExclusion(cart),
      hasConfigProductExclusion: cartContainsBnplExcludedProduct(cart, cfg),
      hasConfigHandleSkuExclusion: cartContainsBnplExcludedHandleOrSku(cart, cfg),
      bnplNodes: Array.from(document.querySelectorAll('[data-mmx-bnpl-block], .mmx-cart-drawer__bnpl')).map((node) => ({
        hidden: node.hidden,
        className: node.className,
        inlineDisplay: node.style.display,
        computedDisplay: window.getComputedStyle(node).display,
        text: (node.textContent || '').trim()
      }))
    }));
  };


  /* ==========================================================
     MMX CART DRAWER V5 — suppress iOS image-link long-press preview
     CSS -webkit-touch-callout is the primary fix. These event guards are a
     narrow fallback for WebKit versions that still try to open a context/drag
     preview. Normal click/tap navigation is intentionally left untouched.
     ========================================================== */
  function mmxCartImageLinkTarget(target) {
    if (!target || !target.closest) return null;
    return target.closest(
      '.mmx-cart-drawer-shell .cart-item__image > a,'
      + ' .mmx-cart-drawer-shell .mmx-cart-addon__image,'
      + ' .mmx-cart-drawer-shell .mmx-empty-rec__image'
    );
  }

  document.addEventListener('contextmenu', (event) => {
    if (!mmxCartImageLinkTarget(event.target)) return;
    event.preventDefault();
  }, true);

  document.addEventListener('dragstart', (event) => {
    if (!mmxCartImageLinkTarget(event.target)) return;
    event.preventDefault();
  }, true);


  /* ==========================================================
     MMX V11.7 — cart expiry bridge
     ========================================================== */

  window.__MMXCartExpiryBridge = {
    getCart: () => fetchCart(true),

    hasPaidItems: (cart) => Boolean(
      cart
      && Array.isArray(cart.items)
      && cart.items.some((item) => !isGiftItem(item))
    ),

    clearCart: () => enqueueCartMutation(async () => {
      const current = await fetchCart(true);
      const updates = {};

      (
        current && Array.isArray(current.items)
          ? current.items
          : []
      ).forEach((item) => {
        const key = String(item && item.key || '');
        if (key) updates[key] = 0;
      });

      if (!Object.keys(updates).length) {
        if (current) {
          cartState = current;
          renderFastCart(current, {
            source: 'cart-expiry-already-empty'
          });
        }

        return current;
      }

      startFastCartFeedback('Expiring cart…');

      try {
        const cleared = await postJson(
          routes.update,
          { updates }
        );

        const authoritative =
          cleared && Array.isArray(cleared.items)
            ? cleared
            : await fetchCart(true);

        if (authoritative) {
          cartState = authoritative;

          renderFastCart(authoritative, {
            source: 'cart-expiry-clear'
          });
        }

        return authoritative;
      } finally {
        stopFastCartFeedback();
      }
    }),

    showExpiredMessage: (message) => {
      const text = String(message || '').trim();
      if (!text) return;

      document.querySelectorAll(
        '.mmx-cart-drawer-shell .cart-drawer__empty-content'
      ).forEach((container) => {
        const emptyMessage = container.querySelector(
          '.mmx-empty-cart-redesign__message'
        );

        let notice = container.querySelector(
          '[data-mmx-cart-expiry-notice]'
        );

        if (!notice) {
          notice = document.createElement('div');
          notice.className = 'mmx-cart-expiry-notice';
          notice.setAttribute(
            'data-mmx-cart-expiry-notice',
            ''
          );
        }

        /*
          V11.7.2:
          Keep the normal empty-cart header and reward tracker first,
          then place the expiry notice directly above the standard
          "Your cart is empty" message.
        */
        if (
          emptyMessage
          && emptyMessage.parentNode
        ) {
          emptyMessage.parentNode.insertBefore(
            notice,
            emptyMessage
          );
        } else {
          container.appendChild(notice);
        }

        notice.textContent = text;
        notice.hidden = false;
      });
    }
  };
}());

/* ==========================================================
   MMX CART DRAWER V11.7.2 — expired message placement polish
   Fixed-date mode uses Singapore time: UTC+08:00
   ========================================================== */

;(() => {
  const SELECTOR =
    '.mmx-cart-drawer-shell [data-mmx-cart-announcement][data-mmx-countdown-enabled="true"]';

  const STORAGE_KEY = 'mmx_cart_expiry_v1';

  const activeTimers = new WeakMap();

  let memorySession = null;
  let cartClearPromise = null;

  function modeFor(element) {
    const mode = String(
      element.getAttribute('data-mmx-countdown-mode')
      || 'fixed'
    ).trim();

    return mode === 'cart' ? 'cart' : 'fixed';
  }

  function parseFixedTarget(element) {
    const date = String(
      element.getAttribute('data-mmx-countdown-date')
      || ''
    ).trim();

    const time = String(
      element.getAttribute('data-mmx-countdown-time')
      || ''
    ).trim();

    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return NaN;
    }

    if (!/^\d{2}:\d{2}$/.test(time)) {
      return NaN;
    }

    return Date.parse(
      `${date}T${time}:00+08:00`
    );
  }

  function cartExpiryMinutes(element) {
    const raw = Number(
      element.getAttribute(
        'data-mmx-cart-expiry-minutes'
      ) || 10
    );

    if (!Number.isFinite(raw)) return 10;

    return Math.max(
      1,
      Math.min(60, Math.round(raw))
    );
  }

  function readCartExpirySession() {
    let stored = null;

    try {
      stored = window.localStorage.getItem(
        STORAGE_KEY
      );
    } catch (error) {
      stored = null;
    }

    if (stored) {
      try {
        const parsed = JSON.parse(stored);

        if (
          parsed
          && Number.isFinite(
            Number(parsed.expiresAt)
          )
        ) {
          memorySession = parsed;
          return parsed;
        }
      } catch (error) {
        // Invalid/stale storage is ignored.
      }
    }

    return memorySession;
  }

  function writeCartExpirySession(session) {
    memorySession = session;

    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(session)
      );
    } catch (error) {
      // Session-memory fallback remains active.
    }
  }

  function clearCartExpirySession() {
    memorySession = null;

    try {
      window.localStorage.removeItem(
        STORAGE_KEY
      );
    } catch (error) {
      // Nothing else required.
    }
  }

  async function resolveCartExpiryTarget(element, knownCart) {
    const bridge = window.__MMXCartExpiryBridge;

    if (
      !bridge
      || typeof bridge.getCart !== 'function'
    ) {
      return NaN;
    }

    const cart =
      knownCart
      && Array.isArray(knownCart.items)
        ? knownCart
        : await bridge.getCart();

    if (!bridge.hasPaidItems(cart)) {
      clearCartExpirySession();
      return NaN;
    }

    const minutes = cartExpiryMinutes(element);
    const saved = readCartExpirySession();

    if (
      saved
      && Number(saved.durationMinutes) === minutes
      && Number.isFinite(Number(saved.expiresAt))
    ) {
      return Number(saved.expiresAt);
    }

    const expiresAt =
      Date.now() + (minutes * 60 * 1000);

    writeCartExpirySession({
      expiresAt,
      durationMinutes: minutes
    });

    return expiresAt;
  }

  function fixedCountdownText(milliseconds) {
    const totalSeconds = Math.max(
      0,
      Math.floor(milliseconds / 1000)
    );

    const days = Math.floor(
      totalSeconds / 86400
    );

    const hours = Math.floor(
      (totalSeconds % 86400) / 3600
    );

    const minutes = Math.floor(
      (totalSeconds % 3600) / 60
    );

    const seconds =
      totalSeconds % 60;

    const hh = String(hours).padStart(2, '0');
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');

    return days > 0
      ? `${days}d ${hh}:${mm}:${ss}`
      : `${hh}:${mm}:${ss}`;
  }

  function cartCountdownText(milliseconds) {
    const totalSeconds = Math.max(
      0,
      Math.ceil(milliseconds / 1000)
    );

    const minutes = Math.floor(
      totalSeconds / 60
    );

    const seconds =
      totalSeconds % 60;

    return (
      `${String(minutes).padStart(2, '0')}:`
      + `${String(seconds).padStart(2, '0')}`
    );
  }

  function showExpiredAnnouncement(element) {
    const copy = element.querySelector(
      '[data-mmx-cart-announcement-copy]'
    );

    const message = String(
      element.getAttribute(
        'data-mmx-countdown-expired-text'
      )
      || 'Offer ended'
    );

    if (!copy) return;

    copy.innerHTML = '';

    const expired =
      document.createElement('div');

    expired.className =
      'mmx-cart-announcement__expired';

    expired.textContent = message;

    copy.appendChild(expired);
  }

  async function expireAnnouncement(element) {
    const countdownMode =
      modeFor(element);

    const expiryDisplay = String(
      element.getAttribute(
        'data-mmx-countdown-expiry'
      ) || 'hide'
    );

    const cartBehaviour = String(
      element.getAttribute(
        'data-mmx-cart-expiry-behavior'
      ) || 'display_only'
    );

    if (countdownMode === 'cart') {
      element.dataset.mmxCartExpiryExpired = 'true';

      element.removeAttribute(
        'data-mmx-cart-expiry-waiting'
      );
    }

    if (
      countdownMode === 'cart'
      && cartBehaviour === 'clear_cart'
    ) {
      const bridge =
        window.__MMXCartExpiryBridge;

      if (
        !bridge
        || typeof bridge.clearCart !== 'function'
      ) {
        return;
      }

      if (!cartClearPromise) {
        cartClearPromise =
          Promise.resolve(
            bridge.clearCart()
          )
            .then((cart) => {
              if (
                cart
                && Number(
                  cart.item_count || 0
                ) === 0
              ) {
                clearCartExpirySession();

                if (
                  expiryDisplay === 'message'
                  && typeof bridge.showExpiredMessage
                    === 'function'
                ) {
                  bridge.showExpiredMessage(
                    element.getAttribute(
                      'data-mmx-countdown-expired-text'
                    )
                    || 'Your cart has expired'
                  );
                }
              }

              return cart;
            })
            .finally(() => {
              cartClearPromise = null;
            });
      }

      await cartClearPromise;
      return;
    }

    if (expiryDisplay === 'message') {
      showExpiredAnnouncement(element);
      return;
    }

    element.hidden = true;
  }

  function rememberOriginalAnnouncementCopy(element) {
    if (
      !element
      || element.__mmxOriginalAnnouncementCopyHtml !== undefined
    ) {
      return;
    }

    const copy = element.querySelector(
      '[data-mmx-cart-announcement-copy]'
    );

    if (copy) {
      element.__mmxOriginalAnnouncementCopyHtml =
        copy.innerHTML;
    }
  }

  function restoreOriginalAnnouncementCopy(element) {
    if (!element) return;

    const copy = element.querySelector(
      '[data-mmx-cart-announcement-copy]'
    );

    if (
      copy
      && typeof element.__mmxOriginalAnnouncementCopyHtml
        === 'string'
    ) {
      copy.innerHTML =
        element.__mmxOriginalAnnouncementCopyHtml;
    }
  }

  function stopElementTimer(element) {
    if (!element) return;

    const timerId = activeTimers.get(element);

    if (timerId) {
      window.clearInterval(timerId);
    }

    activeTimers.delete(element);
  }

  function hideCartExpiryNotices() {
    document.querySelectorAll(
      '.mmx-cart-drawer-shell [data-mmx-cart-expiry-notice]'
    ).forEach((notice) => {
      notice.hidden = true;
    });
  }

  function setCartCountdownWaiting(element) {
    if (!element) return;

    stopElementTimer(element);
    restoreOriginalAnnouncementCopy(element);
    hideCartExpiryNotices();

    element.dataset.mmxCartExpiryWaiting = 'true';

    element.removeAttribute(
      'data-mmx-cart-expiry-expired'
    );

    element.dataset.mmxCountdownInitialised = 'true';
    element.hidden = true;
  }

  async function startCountdown(element, knownCart) {
    if (
      !element
      || element.dataset
        .mmxCountdownInitialised === 'true'
    ) {
      return;
    }

    rememberOriginalAnnouncementCopy(element);

    element.dataset.mmxCountdownInitialised =
      'true';

    const countdownMode =
      modeFor(element);

    const countdown = element.querySelector(
      '[data-mmx-cart-countdown]'
    );

    const timeOutput =
      element.querySelector(
        '[data-mmx-cart-countdown-time]'
      );

    if (!countdown || !timeOutput) {
      return;
    }

    const target =
      countdownMode === 'cart'
        ? await resolveCartExpiryTarget(
            element,
            knownCart
          )
        : parseFixedTarget(element);

    if (!Number.isFinite(target)) {
      if (countdownMode === 'cart') {
        setCartCountdownWaiting(element);
      } else {
        countdown.hidden = true;
      }

      return;
    }

    if (countdownMode === 'cart') {
      element.removeAttribute(
        'data-mmx-cart-expiry-waiting'
      );

      element.removeAttribute(
        'data-mmx-cart-expiry-expired'
      );

      element.hidden = false;
    }

    let intervalId = null;
    let expired = false;

    const tick = () => {
      if (!element.isConnected) {
        if (intervalId) {
          window.clearInterval(intervalId);
        }

        return;
      }

      const remaining =
        target - Date.now();

      if (remaining <= 0) {
        if (expired) return;

        expired = true;

        if (intervalId) {
          window.clearInterval(intervalId);
        }

        activeTimers.delete(element);

        timeOutput.textContent =
          countdownMode === 'cart'
            ? '00:00'
            : '00:00:00';

        Promise.resolve(
          expireAnnouncement(element)
        );

        return;
      }

      timeOutput.textContent =
        countdownMode === 'cart'
          ? cartCountdownText(remaining)
          : fixedCountdownText(remaining);
    };

    tick();

    if (target > Date.now()) {
      intervalId =
        window.setInterval(tick, 1000);

      activeTimers.set(
        element,
        intervalId
      );
    }
  }

  function initialiseCountdowns(scope) {
    const root =
      scope && scope.querySelectorAll
        ? scope
        : document;

    if (
      root.matches
      && root.matches(SELECTOR)
    ) {
      startCountdown(root);
    }

    root
      .querySelectorAll(SELECTOR)
      .forEach(startCountdown);
  }

  window.__MMXCartExpirySyncCartState =
    (cart) => {
      const bridge =
        window.__MMXCartExpiryBridge;

      if (!bridge) return;

      const cartTimers = Array.from(
        document.querySelectorAll(
          `${SELECTOR}[data-mmx-countdown-mode="cart"]`
        )
      );

      const hasPaidItems =
        bridge.hasPaidItems(cart);

      if (!hasPaidItems) {
        clearCartExpirySession();

        cartTimers.forEach((element) => {
          setCartCountdownWaiting(element);
        });

        return;
      }

      cartTimers.forEach((element) => {
        if (
          element.getAttribute(
            'data-mmx-cart-expiry-waiting'
          ) !== 'true'
        ) {
          return;
        }

        restoreOriginalAnnouncementCopy(element);

        element.hidden = false;

        element.removeAttribute(
          'data-mmx-cart-expiry-waiting'
        );

        element.removeAttribute(
          'data-mmx-cart-expiry-expired'
        );

        element.removeAttribute(
          'data-mmx-countdown-initialised'
        );

        startCountdown(element, cart);
      });
    };

  function bootAnnouncementCountdowns() {
    initialiseCountdowns(document);

    const observer =
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.addedNodes.forEach(
            (node) => {
              if (!(node instanceof Element)) {
                return;
              }

              initialiseCountdowns(node);
            }
          );
        }
      });

    observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      bootAnnouncementCountdowns,
      { once: true }
    );
  } else {
    bootAnnouncementCountdowns();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialiseCountdowns(
        event.target || document
      );
    }
  );
})();
